import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase, supabaseAdmin } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { motion, AnimatePresence } from 'framer-motion'
import {
    MapPin, Bed, Bath, Maximize2, Search, Phone, Mail,
    Calculator, ArrowLeft, MessageCircle, ChevronRight, ChevronLeft,
    Loader2, Linkedin, Instagram, Facebook, Lock, CheckCircle2,
    Home, Key, TrendingUp, FileText, ShieldCheck, Eye, Send, X, UserPlus,
    Filter, RotateCcw, ExternalLink, SlidersHorizontal, Menu, Clock, Printer, Download,
    ArrowUp, Compass, CalendarDays, Flame, Snowflake, Waves, Trees, Car, Layout, ChefHat, Armchair,
    Star, Globe, Users, Grid, List, TrendingDown, Building, User, Calendar,
    MoreHorizontal, Link2, ArrowRight, Check, Plus, Minus, Share2, Twitter, Youtube,
    Play, Pause, Volume2, Heart, Shield, Zap, Sparkles, Wand2, Monitor
} from 'lucide-react'
import html2pdf from 'html2pdf.js'
import type { Tables } from '../../lib/database.types'

type Property = Tables<'properties'> & {
    cover_url?: string;
    images?: { url: string; is_cover: boolean }[];
    agent?: { full_name: string; photo_url: string; phone: string };
}

interface AgencyConfig {
    agency_id: string;
    agency_name: string;
    logo_url: string | null;
    primary_color: string;
    accent_color: string;
    contact_phone: string;
    contact_whatsapp: string | null;
    contact_email: string;
    contact_address: string;
    banners_list: any[];
    portals_list: any[];
    kit_digital_logo_url: string | null;
    mortgage_default_interest: number;
    mortgage_default_years: number;
    opening_hours_es: string | null;
    opening_hours_ca: string | null;
    legal_privacy_es: string | null;
    legal_privacy_ca: string | null;
    legal_cookies_es: string | null;
    legal_cookies_ca: string | null;
    legal_terms_es: string | null;
    legal_terms_ca: string | null;
    custom_pages?: any[];
    show_blog?: boolean;
    show_kit_digital?: boolean;
    secondary_language?: string;
}

const getWebT = (lang: string, secondary: string = 'ca') => {
    const isSec = lang !== 'es';
    const s = secondary || 'ca';
    
    if (s === 'en') {
        return {
            inicio: isSec ? 'Home' : 'Inicio',
            propiedades: isSec ? 'Properties' : 'Propiedades',
            contacto: isSec ? 'Contact' : 'Contacto',
            blog: isSec ? 'Blog' : 'Blog',
            calculadora: isSec ? 'Calculator' : 'Calculadora',
            valoracion: isSec ? 'Valuation' : 'Valoración',
            disponibles: isSec ? 'available properties' : 'propiedades disponibles',
            ver_mas: isSec ? 'View details' : 'Ver detalles',
            llamar: isSec ? 'Call' : 'Llamar',
            cerrar: isSec ? 'Close' : 'Cerrar',
            menu: isSec ? 'Menu' : 'Menú',
            aviso_legal: isSec ? 'Legal Notice' : 'Aviso Legal',
            privacidad: isSec ? 'Privacy Policy' : 'Política de Privacidad',
            entendido: isSec ? 'Got it' : 'Entendido'
        }
    }
    if (s === 'fr') {
        return {
            inicio: isSec ? 'Accueil' : 'Inicio',
            propiedades: isSec ? 'Propriétés' : 'Propiedades',
            contacto: isSec ? 'Contact' : 'Contacto',
            blog: isSec ? 'Blog' : 'Blog',
            calculadora: isSec ? 'Calculateur' : 'Calculadora',
            valoracion: isSec ? 'Évaluation' : 'Valoración',
            disponibles: isSec ? 'propriétés disponibles' : 'propiedades disponibles',
            ver_mas: isSec ? 'Voir plus' : 'Ver detalles',
            llamar: isSec ? 'Appeler' : 'Llamar',
            cerrar: isSec ? 'Fermer' : 'Cerrar',
            menu: isSec ? 'Menu' : 'Menú',
            aviso_legal: isSec ? 'Mentions Légales' : 'Aviso Legal',
            privacidad: isSec ? 'Confidentialité' : 'Política de Privacidad',
            entendido: isSec ? 'D\'accord' : 'Entendido'
        }
    }
    if (s === 'de') {
        return {
            inicio: isSec ? 'Startseite' : 'Inicio',
            propiedades: isSec ? 'Immobilien' : 'Propiedades',
            contacto: isSec ? 'Kontakt' : 'Contacto',
            blog: isSec ? 'Blog' : 'Blog',
            calculadora: isSec ? 'Rechner' : 'Calculadora',
            valoracion: isSec ? 'Bewertung' : 'Valoración',
            disponibles: isSec ? 'verfügbare Immobilien' : 'propiedades disponibles',
            ver_mas: isSec ? 'Details ansehen' : 'Ver detalles',
            llamar: isSec ? 'Anrufen' : 'Llamar',
            cerrar: isSec ? 'Schließen' : 'Cerrar',
            menu: isSec ? 'Menü' : 'Menú',
            aviso_legal: isSec ? 'Impressum' : 'Aviso Legal',
            privacidad: isSec ? 'Datenschutz' : 'Política de Privacidad',
            entendido: isSec ? 'Verstanden' : 'Entendido'
        }
    }
    // Default to Catalan if CA or anything else
    return {
        inicio: isSec ? 'Inici' : 'Inicio',
        propiedades: isSec ? 'Propietats' : 'Propiedades',
        contacto: isSec ? 'Contacte' : 'Contacto',
        blog: isSec ? 'Blog' : 'Blog',
        calculadora: isSec ? 'Calculadora' : 'Calculadora',
        valoracion: isSec ? 'Valoració' : 'Valoración',
        disponibles: isSec ? 'propietats disponibles' : 'propiedades disponibles',
        ver_mas: isSec ? 'Veure detalls' : 'Ver detalles',
        llamar: isSec ? 'Trucar' : 'Llamar',
        cerrar: isSec ? 'Tancar' : 'Cerrar',
        menu: isSec ? 'Menú' : 'Menú',
        aviso_legal: isSec ? 'Avís Legal' : 'Aviso Legal',
        privacidad: isSec ? 'Privadesa' : 'Política de Privacidad',
        entendido: isSec ? 'Entès' : 'Entendido'
    }
}

// Logo oficial de WhatsApp (auricular de teléfono dentro del globo).
// Usamos un SVG inline para no depender de Lucide (cuyo MessageCircle no es el
// logo de marca real). Se renderiza en blanco para botones verdes.
function WhatsAppIcon({ size = 20, color = '#fff' }: { size?: number; color?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
            <path fill={color} d="M16.001 3C8.821 3 3 8.82 3 16c0 2.293.6 4.523 1.74 6.495L3 29l6.694-1.722A12.96 12.96 0 0 0 16 29c7.18 0 13-5.82 13-13S23.181 3 16.001 3Zm0 23.6a10.6 10.6 0 0 1-5.401-1.475l-.387-.23-3.972 1.022 1.06-3.872-.252-.4A10.6 10.6 0 1 1 26.6 16c0 5.854-4.745 10.6-10.599 10.6Zm5.815-7.945c-.32-.16-1.89-.93-2.182-1.037-.292-.107-.504-.16-.717.16-.213.32-.823 1.037-1.01 1.25-.186.213-.371.24-.69.08-.32-.16-1.35-.498-2.572-1.585-.95-.847-1.59-1.892-1.776-2.212-.186-.32-.02-.493.14-.652.143-.143.32-.371.479-.557.16-.186.213-.32.319-.534.107-.213.054-.4-.026-.56-.08-.16-.717-1.73-.983-2.37-.259-.624-.522-.54-.717-.55l-.61-.011a1.17 1.17 0 0 0-.85.4c-.292.32-1.116 1.09-1.116 2.66s1.142 3.087 1.302 3.3c.16.213 2.245 3.43 5.439 4.81.76.328 1.353.524 1.816.67.762.243 1.456.208 2.005.126.612-.091 1.89-.772 2.156-1.518.266-.747.266-1.387.186-1.518-.08-.133-.293-.213-.612-.373Z"/>
        </svg>
    )
}

const OP_LABEL: Record<string, string> = { venda: 'VENTA', lloguer: 'ALQUILER', 'traspàs': 'TRASPASO', venta: 'VENTA', alquiler: 'ALQUILER' }
const OP_LABEL_CA: Record<string, string> = { venda: 'VENDA', lloguer: 'LLOGUER', 'traspàs': 'TRASPÀS', venta: 'VENDA', alquiler: 'LLOGUER' }
const OP_COLOR: Record<string, string> = { venda: '#1d4ed8', lloguer: '#7c3aed', 'traspàs': '#a16207', venta: '#1d4ed8', alquiler: '#7c3aed' }

const STATUS_BADGE: Record<string, { bg: string; color: string; label_es: string; label_ca: string }> = {
    available: { bg: '#ecfdf5', color: '#047857', label_es: 'Disponible', label_ca: 'Disponible' }, // Oscurecido green
    reserved: { bg: '#fffbeb', color: '#9a3412', label_es: 'Reservada', label_ca: 'Reservada' }, // Oscurecido orange
    sold: { bg: '#f8fafc', color: '#1e293b', label_es: 'Vendida', label_ca: 'Venuda' }, // Oscurecido slate
    rented: { bg: '#eff6ff', color: '#1e40af', label_es: 'Alquilada', label_ca: 'Llogada' }, // Blue passed
    archived: { bg: '#fef2f2', color: '#b91c1c', label_es: 'Archivada', label_ca: 'Arxivada' }, // Oscurecido red
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCESSIBILITY & PERFORMANCE UTILS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Optimizes images by using Supabase's image transformation service
 * or adding standard resizing parameters.
 */
const optimizeImg = (url: string, width = 800, quality = 80) => {
    if (!url || typeof url !== 'string') return url;
    if (url.includes('supabase.co') && url.includes('/storage/v1/object/public/')) {
        return url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/') + `?width=${width}&quality=${quality}&format=webp&resize=contain`;
    }
    return url;
};

/**
 * Ensures the tenant query parameter is preserved across all navigation
 */
const addTenant = (url: string) => {
    const params = new URLSearchParams(window.location.search);
    const tenant = params.get('tenant');
    if (!tenant) return url;
    
    const [path, search] = url.split('?');
    const urlParams = new URLSearchParams(search || '');
    urlParams.set('tenant', tenant);
    return `${path}?${urlParams.toString()}`;
};



function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
}

function getLuminance(r: number, g: number, b: number) {
    const a = [r, g, b].map(v => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrast(rgb1: any, rgb2: any) {
    const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
    const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
}

function darkenColor(hex: string, amount: number) {
    const rgb = hexToRgb(hex);
    const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
    const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
    const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/** 
 * Asegura un ratio de contraste mínimo de 4.5:1 (WCAG AA) para un color sobre blanco.
 * Si no llega, o oscurece progresivamente.
 */
function ensureContrast(color: string, minRatio: number = 4.5): string {
    if (!color || !color.startsWith('#')) return color;
    let current = color;
    const white = { r: 255, g: 255, b: 255 };
    let safety = 0;
    while (getContrast(hexToRgb(current), white) < minRatio && safety < 10) {
        current = darkenColor(current, 0.1);
        safety++;
    }
    return current;
}

function autoFormatLegal(text: string) {
    if (!text) return '';
    // If it already looks like HTML, don't auto-format
    if (text.includes('<p>') || text.includes('<div>') || text.includes('<strong>')) return text;

    // Split by double newlines for paragraphs
    return text.split(/\n\s*\n/).map(para => {
        const lines = para.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length === 0) return '';

        // Detect headers or lists
        if (lines.length === 1 && (/^\d+\./.test(lines[0]) || (lines[0].length > 5 && lines[0] === lines[0].toUpperCase()))) {
            return `<h3>${lines[0]}</h3>`;
        }

        // Detect list items
        const isList = lines.every(l => l.startsWith('-') || l.startsWith('•') || /^\d+\)/.test(l));
        if (isList) {
            return `<ul>${lines.map(l => `<li>${l.replace(/^[-•]\s*|^\d+\)\s*/, '')}</li>`).join('')}</ul>`;
        }

        return `<p>${para.replace(/\n/g, '<br/>')}</p>`;
    }).join('');
}

function BannersCarouselBlock({ data, settings, lang }: { data: any, settings: any, lang: 'es' | 'ca' }) {
    const banners = data.banners || [];
    const height = settings.height || 500;
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (banners.length <= 1) return;
        const timer = setInterval(() => {
            setIdx(prev => (prev + 1) % banners.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [banners.length]);

    if (banners.length === 0) return null;

    return (
        <div style={{ position: 'relative', height: `${height}px`, width: '100%', overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: `url(${banners[idx].img || banners[idx].url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)' }} />
                    <div style={{ position: 'absolute', bottom: 60, left: 0, right: 0, textAlign: 'center', color: '#fff', padding: '0 40px' }}>
                        <motion.h2
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)', fontWeight: 900, marginBottom: 10, textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                        >
                            {lang === 'ca' ? (banners[idx].title_ca || banners[idx].title_es) : (banners[idx].title_es || banners[idx].title_ca)}
                        </motion.h2>
                        <motion.p
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                            style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', opacity: 0.9, fontWeight: 500 }}
                        >
                            {lang === 'ca' ? (banners[idx].subtitle_ca || banners[idx].subtitle_es) : (banners[idx].subtitle_es || banners[idx].subtitle_ca)}
                        </motion.p>
                    </div>
                </motion.div>
            </AnimatePresence>

            {banners.length > 1 && (
                <div 
                    role="tablist" 
                    aria-label={lang === 'ca' ? 'Navegació de banners' : 'Navegación de banners'}
                    style={{ position: 'absolute', bottom: 25, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 10, zIndex: 10 }}
                >
                    {banners.map((_: any, i: number) => (
                        <button 
                            key={i} 
                            role="tab"
                            aria-selected={i === idx}
                            aria-label={`${lang === 'ca' ? 'Anar al banner' : 'Ir al banner'} ${i + 1}`}
                            onClick={() => setIdx(i)} 
                            style={{
                                width: i === idx ? 30 : 12, height: 12, borderRadius: 6, border: 'none',
                                background: i === idx ? '#fff' : 'rgba(255,255,255,0.4)',
                                cursor: 'pointer', transition: 'all 0.3s'
                            }} 
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// Convierte un texto en un slug seguro para URL: minúsculas, sin acentos, guiones.
function makeSlug(text: string): string {
    if (!text) return ''
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
}

// URL canónica para un post de blog en cada idioma.
function blogPostUrl(lang: string, post: any): string {
    const segment = lang === 'ca' ? 'noticies' : (lang === 'en' ? 'news' : 'blog')
    const title = lang === 'ca' ? (post.title_ca || post.title_es) : (post.title_es || post.title_ca)
    const slug = makeSlug(title || 'post') || 'post'
    const shortId = String(post.id || '').slice(-6)
    return addTenant(`/${lang}/${segment}/${slug}-${shortId}`)
}

// URL canónica para una propiedad en cada idioma.
function propertyUrl(lang: string, prop: any): string {
    const segment = lang === 'ca' ? 'propietats' : (lang === 'en' ? 'properties' : 'propiedades')
    const title = lang === 'ca' ? (prop.title_ca || prop.title_es) : (prop.title_es || prop.title_ca)
    const base = makeSlug(title || prop.type || 'propiedad') || 'propiedad'
    const ref = (prop.ref_code || '').toString()
    return addTenant(`/${lang}/${segment}/${base}${ref ? '-' + ref.toLowerCase() : ''}`)
}

export default function PublicHome() {
    const { profile } = useAuth()
    const isSuperAdmin = profile?.role === 'superadmin'
    const { lang: routeLang, view: routeView, slug: routeSlug } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [properties, setProperties] = useState<Property[]>([])
    const [pageBlocks, setPageBlocks] = useState<any[]>([])
    const [config, setConfig] = useState<AgencyConfig | null>(null)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [legalModal, setLegalModal] = useState<{ title: string, content: string } | null>(null)
    const [showCookieConsent, setShowCookieConsent] = useState(false)
    const [posts, setPosts] = useState<any[]>([])
    const [selectedPost, setSelectedPost] = useState<any | null>(null)
    const [view, setView] = useState(() => {
        // Inicializa la vista correctamente desde la URL para evitar un flash de HomeView
        // antes de que el useEffect ajuste la vista.
        if (!routeView) return 'home'
        if (routeView === 'blog' || routeView === 'noticies' || routeView === 'news') return 'blog'
        if (routeView === 'propiedades' || routeView === 'propietats' || routeView === 'properties') return 'properties'
        if (routeView === 'valoracion' || routeView === 'valoracio' || routeView === 'valuation') return 'valuation'
        if (routeView === 'contacto' || routeView === 'contacte' || routeView === 'contact') return 'contact'
        if (routeView === 'calculadora' || routeView === 'calculator') return 'calculator'
        return 'home'
    })
    const [lang, setLang] = useState<string>((routeLang as any) || 'es')
    const T_WEB = getWebT(lang, config?.secondary_language)
    const [detailsProp, setDetailsProp] = useState<Property | null>(null)
    const [mapProp, setMapProp] = useState<Property | null>(null)
    const [openPdfOnLoad, setOpenPdfOnLoad] = useState(false)
    const [customPageId, setCustomPageId] = useState<string | null>(null)
    const [pdfProp, setPdfProp] = useState<Property | null>(null)
    const [featTab, setFeatTab] = useState<'todos' | 'venda' | 'lloguer'>('todos')
    const [lightbox, setLightbox] = useState<{ open: boolean, images: string[], index: number }>({ open: false, images: [], index: 0 })
    const [openMobileSubmenus, setOpenMobileSubmenus] = useState<string[]>([])

    useEffect(() => {
        if (routeLang) {
            setLang(routeLang)
        }
        if (routeView) {
            const v = (routeView === 'blog' || routeView === 'noticies') ? 'blog' : (routeView === 'home' ? 'home' : routeView);
            if (v !== view) setView(v);
        }
    }, [routeLang, routeView])

    useEffect(() => {
        if (routeView) {
            const v = (routeView === 'blog' || routeView === 'noticies') ? 'blog' : 'home';
            if (v !== view) setView(v);
        }
    }, [routeView])

    useEffect(() => {
        const consent = localStorage.getItem('cookie_consent_status')
        if (!consent) {
            const timer = setTimeout(() => setShowCookieConsent(true), 1500)
            return () => clearTimeout(timer)
        }
    }, [])

    const handleCookieAction = (status: 'accepted' | 'declined') => {
        localStorage.setItem('cookie_consent_status', status)
        setShowCookieConsent(false)
    }

    const changeView = (newView: any) => {
        setMobileMenuOpen(false);
        setDetailsProp(null);
        setCustomPageId(null);
        setSelectedPost(null);
        setView(newView);

        let url = `/${lang}`;
        const sec = config?.secondary_language || 'ca';
        
        if (newView === 'properties') {
            url += lang === sec ? '/propietats' : (lang === 'en' ? '/properties' : '/propiedades');
        } else if (newView === 'valuation') {
            url += lang === sec ? '/valoracio' : (lang === 'en' ? '/valuation' : '/valoracion');
        } else if (newView === 'contact') {
            url += lang === sec ? '/contacte' : (lang === 'en' ? '/contact' : '/contacto');
        } else if (newView === 'calculator') {
            url += lang === 'en' ? '/calculator' : '/calculadora';
        } else if (newView === 'blog') {
            url += lang === sec ? '/noticies' : (lang === 'en' ? '/news' : '/blog');
        } else if (['aviso-legal', 'avis-legal', 'privacidad', 'privadesa', 'privasitat', 'cookies', 'terminos', 'termes'].includes(newView)) {
            url += `/${newView}`;
        } else if (newView === 'home') {
            url = `/${lang}`;
        }

        const targetUrl = addTenant(url);
        const currentFull = window.location.pathname + window.location.search;
        if (currentFull !== targetUrl) {
            navigate(targetUrl);
        }
        if (window.scrollY > 0) window.scrollTo(0, 0);
    }

    useEffect(() => {
        if (!routeView) {
            const cleanPath = window.location.pathname.replace(/\/$/, "");
            if (cleanPath === "/" || cleanPath === "/es" || cleanPath === "/ca" || cleanPath === "") {
                setView('home');
            } else if (window.location.pathname.startsWith('/p/')) {
                const slug = window.location.pathname.split('/p/')[1];
                if (config?.custom_pages) {
                    const found = config.custom_pages.find((p: any) => p.slug === slug);
                    if (found) {
                        setCustomPageId(found.id);
                        setView('custom');
                    }
                }
            }
            return;
        }
        let matched = false;
        if (routeView === 'propiedades' || routeView === 'propietats') { setView('properties'); matched = true; }
        else if (routeView === 'valoracion' || routeView === 'valoracio') { setView('valuation'); matched = true; }
        else if (routeView === 'contacto' || routeView === 'contacte') { setView('contact'); matched = true; }
        else if (routeView === 'calculadora') { setView('calculator'); matched = true; }
        else if (routeView === 'blog' || routeView === 'noticies' || routeView === 'noticias') { setView('blog'); matched = true; }


        // Legal modals based on route
        if (routeView === 'avis-legal' || routeView === 'aviso-legal') {
            setLegalModal({
                title: lang === 'ca' ? 'Avís Legal' : 'Aviso Legal',
                content: (lang === 'ca' ? config?.legal_terms_ca : config?.legal_terms_es) || (config?.legal_terms_es || '')
            });
            matched = true;
        } else if (routeView === 'privasitat' || routeView === 'privacidad' || routeView === 'privadesa') {
            setLegalModal({
                title: lang === 'ca' ? 'Política de Privadesa' : 'Política de Privacidad',
                content: (lang === 'ca' ? config?.legal_privacy_ca : config?.legal_privacy_es) || (config?.legal_privacy_es || '')
            });
            matched = true;
        } else if (routeView === 'cookies') {
            setLegalModal({
                title: 'Cookies',
                content: (lang === 'ca' ? config?.legal_cookies_ca : config?.legal_cookies_es) || (config?.legal_cookies_es || '')
            });
            matched = true;
        } else if (routeView === 'termes' || routeView === 'terminos') {
            setLegalModal({
                title: lang === 'ca' ? 'Termes de Servei' : 'Términos de Servicio',
                content: (lang === 'ca' ? config?.legal_terms_ca : config?.legal_terms_es) || (config?.legal_terms_es || '')
            });
            matched = true;
        }

        if (!matched && config?.custom_pages) {
            const found = config.custom_pages.find((p: any) => p.slug === routeView);
            if (found) {
                setCustomPageId(found.id);
                setView('custom');
            } else {
                setView('home'); // fallback
            }
        }
    }, [routeView, window.location.pathname, config, lang])

    const handleLinkClick = (link: string) => {
        if (!link) return;
        setMobileMenuOpen(false);
        if (link.startsWith('http')) {
            window.open(link, '_blank');
            return;
        }
        if (link.startsWith('/p/')) {
            const slug = link.replace('/p/', '');
            const page = config?.custom_pages?.find(p => p.slug === slug);
            if (page) {
                setCustomPageId(page.id);
                setView('custom');
                window.scrollTo(0, 0);
                navigate(link);
            }
            return;
        }

        const views: any = {
            'home': 'home',
            '/': 'home',
            'properties': 'properties',
            'valuation': 'valuation',
            'valoracion': 'valuation',
            'valoracio': 'valuation',
            'blog': 'blog',
            'calculadora': 'calculator',
            'calculator': 'calculator',
            'contacte': 'contact',
            'contacto': 'contact',
            'contact': 'contact'
        };

        if (views[link]) {
            changeView(views[link]);
        } else {
            changeView(link);
        }
    };

    const changeLang = (newLang: string) => {
        const sec = config?.secondary_language || 'ca';
        let url = `/${newLang}`;
        if (view === 'properties') url = newLang === sec ? `/${newLang}/propietats` : `/${newLang}/propiedades`;
        if (view === 'valuation') url = newLang === sec ? `/${newLang}/valoracio` : `/${newLang}/valoracion`;
        if (view === 'contact') url = newLang === sec ? `/${newLang}/contacte` : `/${newLang}/contacto`;
        if (view === 'calculator') url = `/${newLang}/calculadora`;
        if (view === 'blog') url = newLang === sec ? `/${newLang}/noticies` : `/${newLang}/blog`;
        navigate(addTenant(url));
    }

    useEffect(() => {
        setDetailsProp(null)
    }, [routeView, routeLang])

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50)
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        async function load() {
            setLoading(true)
            try {
                const queryTenant = new URLSearchParams(window.location.search).get('tenant')
                if (queryTenant) localStorage.setItem('local_dev_tenant', queryTenant)

                const host = window.location.hostname
                const dataClient = isSuperAdmin ? supabaseAdmin : supabase
                
                let { data: agency } = await dataClient.from('agencies').select('*').eq('custom_domain', host).maybeSingle()

                if (!agency) {
                    const slug = queryTenant || localStorage.getItem('local_dev_tenant') || import.meta.env.VITE_AGENCY_SLUG || 'demo'
                    const { data: slugAgency } = await dataClient.from('agencies').select('*').eq('slug', slug).maybeSingle()
                    agency = slugAgency

                    // ULTIMATE FALLBACK: If we are on the main domain (or localhost) and no tenant is provided/found, default to 'demo'
                    if (!agency && !queryTenant && (host.includes('inmocrm.cat') || host === 'localhost' || host === '127.0.0.1')) {
                        console.log('🚀 PUBLIC_HOME: Domain match / No tenant. Using demo as fallback.')
                        const { data: fallback } = await dataClient.from('agencies').select('*').eq('slug', 'demo').maybeSingle()
                        agency = fallback
                    }
                }

                if (!agency) {
                    console.error('🚀 PUBLIC_HOME: No agency found after all fallbacks.')
                    setLoading(false)
                    return
                }

                let curConfig: any = null
                if (agency) {
                    const { data: dbCfg } = await dataClient.from('agency_configs').select('*').eq('agency_id', agency.id).maybeSingle()
                    if (dbCfg) {
                        curConfig = { ...dbCfg }
                        if (typeof curConfig.banners_list === 'string') curConfig.banners_list = JSON.parse(curConfig.banners_list)
                        if (typeof curConfig.custom_pages === 'string') curConfig.custom_pages = JSON.parse(curConfig.custom_pages)
                        if (typeof curConfig.default_blocks_visibility === 'string') curConfig.default_blocks_visibility = JSON.parse(curConfig.default_blocks_visibility)
                    }
                }

                // IDENTITY PATCHING: Ensure basic branding is ALWAYS present even if config is empty or partial
                if (agency) {
                    const fallbackName = agency.name || (agency.slug ? agency.slug.charAt(0).toUpperCase() + agency.slug.slice(1) : 'Tu Inmobiliaria')
                    
                    if (!curConfig) {
                        console.warn('🚀 PUBLIC_HOME: No agency_config found for agency:', agency.slug, '. Creating dynamic fallback.')
                        curConfig = {
                            agency_id: agency.id,
                            agency_name: fallbackName,
                            contact_email: agency.contact_email || '',
                            contact_phone: agency.contact_phone || '',
                            primary_color: '#0f172a',
                            accent_color: '#e11d48',
                            banners_list: [],
                            portals_list: [],
                            favicon_url: '/favicon.ico'
                        }
                    } else {
                        // Patch partial config
                        if (!curConfig.agency_name || curConfig.agency_name === 'Tu Inmobiliaria') curConfig.agency_name = fallbackName
                        if (!curConfig.contact_email) curConfig.contact_email = agency.contact_email || ''
                        if (!curConfig.contact_phone) curConfig.contact_phone = agency.contact_phone || ''
                    }
                    
                    console.log(`🚀 PUBLIC_HOME: Final resolved identity for [${agency.slug}]:`, curConfig.agency_name)
                }

                // Asegurar que las listas son arrays reales y no strings JSON sin debidamente parsear o campos rotos
                if (typeof curConfig.banners_list === 'string') try { curConfig.banners_list = JSON.parse(curConfig.banners_list) } catch(e) { curConfig.banners_list = [] }
                if (typeof curConfig.custom_pages === 'string') try { curConfig.custom_pages = JSON.parse(curConfig.custom_pages) } catch(e) { curConfig.custom_pages = [] }
                if (typeof curConfig.portals_list === 'string') try { curConfig.portals_list = JSON.parse(curConfig.portals_list) } catch(e) { curConfig.portals_list = [] }
                
                if (!Array.isArray(curConfig.banners_list)) curConfig.banners_list = []
                if (!Array.isArray(curConfig.custom_pages)) curConfig.custom_pages = []
                if (!Array.isArray(curConfig.portals_list)) curConfig.portals_list = []

                // Forzar que default_blocks_visibility sea un array siempre
                let dbv = curConfig.default_blocks_visibility
                if (typeof dbv === 'string') {
                    try { dbv = JSON.parse(dbv) } catch(e) { dbv = null }
                }
                curConfig.default_blocks_visibility = Array.isArray(dbv) ? dbv : null

                setConfig(curConfig)

                if (curConfig) {
                    document.title = `${curConfig.agency_name || 'Inmobiliaria'} | Gestión Inmobiliaria`
                    const metaDesc = document.querySelector('meta[name="description"]')
                    if (metaDesc) metaDesc.setAttribute('content', curConfig.seo_description_es || 'Tu inmobiliaria de confianza')
                }

                if (curConfig.agency_id) {
                    const propsQuery = dataClient
                        .from('properties')
                        .select('*, agency_users(full_name, photo_url, phone)')
                        .eq('agency_id', curConfig.agency_id)
                    
                    // Only filter by published if NOT superadmin
                    if (!isSuperAdmin) {
                        propsQuery.eq('is_published', true).neq('status', 'archived')
                    }

                    const { data: props } = await propsQuery.order('created_at', { ascending: false })

                    if (props) {
                        const { data: imgs } = await dataClient.from('property_images').select('*').in('property_id', props.map(p => p.id))
                        const enriched = props.map((p: any) => {
                            const pImgs = imgs?.filter(i => i.property_id === p.id) || []
                            return {
                                ...p,
                                cover_url: pImgs.find(i => i.is_cover)?.url || pImgs[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80',
                                images: pImgs,
                                agent: p.agency_users
                            }
                        })
                        enriched.sort((a, b) => {
                            if (a.is_featured && !b.is_featured) return -1;
                            if (!a.is_featured && b.is_featured) return 1;
                            return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
                        });
                        setProperties(enriched)
                    }

                    console.log('🚀 PUBLIC_HOME: Fetching blog posts for agency:', curConfig.agency_id)
                    const blogQuery = dataClient
                        .from('blog_posts')
                        .select('*')
                        .eq('agency_id', curConfig.agency_id)
                    
                    if (!isSuperAdmin) {
                        blogQuery.eq('is_published', true)
                    }

                    const { data: blogData, error: blogErr } = await blogQuery.order('published_at', { ascending: false })
                    
                    if (blogData) {
                        console.log('🚀 PUBLIC_HOME: Blog posts loaded:', blogData.length)
                        setPosts(blogData)
                    } else if (blogErr) {
                        console.error('🚀 PUBLIC_HOME: Error loading blog posts:', blogErr)
                    }

                    const { data: blocksData, error: blocksErr } = await dataClient
                        .from('page_blocks')
                        .select('*')
                        .eq('agency_id', curConfig.agency_id)
                        .eq('is_active', true)
                        .order('order_index', { ascending: true })
                    if (blocksData) setPageBlocks(blocksData)
                    else if (blocksErr && blocksErr.code !== '42P01') console.error(blocksErr)
                }
            } catch (err) {
                console.error('Error loading public home:', err)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [])

    // Sincroniza la URL con la propiedad seleccionada (modal abierto)
    useEffect(() => {
        const path = window.location.pathname
        const onPropertiesPage = path.includes('/propiedades') || path.includes('/propietats') || path.includes('/properties')
        if (!onPropertiesPage) return

        if (detailsProp) {
            const desired = propertyUrl(lang, detailsProp)
            if (window.location.pathname + window.location.search !== desired) {
                navigate(desired, { replace: true })
            }
        } else {
            // Si estoy en /propiedades/{slug} y se cierra el modal, volver al índice
            const segments = window.location.pathname.split('/').filter(Boolean)
            const onPropDetailUrl = segments.length === 3
            const hasOldParam = new URLSearchParams(window.location.search).has('prop')
            if (onPropDetailUrl || hasOldParam) {
                const segment = lang === 'ca' ? 'propietats' : (lang === 'en' ? 'properties' : 'propiedades')
                navigate(`/${lang}/${segment}`, { replace: true })
            }
        }
    }, [detailsProp, lang])

    // SEO: actualiza title, meta description y canonical según el contenido visible
    useEffect(() => {
        if (!config) return
        const agencyName = config.agency_name || 'Inmobiliaria'
        let title = `${agencyName} | Gestión Inmobiliaria`
        let desc = (lang === 'ca' ? (config as any).seo_description_ca : (config as any).seo_description_es) || (config as any).seo_description_es || 'Tu inmobiliaria de confianza'

        if (selectedPost) {
            const postTitle = lang === 'ca' ? (selectedPost.title_ca || selectedPost.title_es) : (selectedPost.title_es || selectedPost.title_ca)
            title = `${postTitle} — ${agencyName}`
            const excerpt = (selectedPost.excerpt_es || selectedPost.excerpt_ca || '').toString().slice(0, 155)
            if (excerpt) desc = excerpt
        } else if (detailsProp) {
            const propTitle = lang === 'ca' ? (detailsProp.title_ca || detailsProp.title_es) : (detailsProp.title_es || detailsProp.title_ca)
            const city = detailsProp.location_city || ''
            title = `${propTitle}${city ? ' en ' + city : ''} — ${agencyName}`
            desc = `${propTitle}${detailsProp.price ? ' por ' + detailsProp.price + ' €' : ''}${city ? ' en ' + city : ''}. ${agencyName}.`.slice(0, 155)
        }

        document.title = title
        const meta = document.querySelector('meta[name="description"]')
        if (meta) meta.setAttribute('content', desc)

        // Canonical link
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
        if (!canonical) {
            canonical = document.createElement('link')
            canonical.rel = 'canonical'
            document.head.appendChild(canonical)
        }
        canonical.href = window.location.origin + window.location.pathname

        // og:title / og:description / og:url
        const setOg = (prop: string, content: string) => {
            let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null
            if (!el) {
                el = document.createElement('meta')
                el.setAttribute('property', prop)
                document.head.appendChild(el)
            }
            el.setAttribute('content', content)
        }
        setOg('og:title', title)
        setOg('og:description', desc)
        setOg('og:url', canonical.href)
        setOg('og:type', (selectedPost || detailsProp) ? 'article' : 'website')
    }, [config, lang, selectedPost, detailsProp])

    useEffect(() => {
        const autoPrint = searchParams.get('print') === 'true'

        // 1) Soporte URL antigua: /es/propiedades?prop=UUID
        const propId = searchParams.get('prop')
        if (propId && properties.length > 0) {
            const found = properties.find(p => p.id === propId)
            if (found) {
                setDetailsProp(found)
                if (autoPrint) setTimeout(() => window.print(), 1200)
                return
            }
        }

        // 2) URL SEO-friendly: /es/propiedades/{slug-titulo}-{ref_code}
        if (routeSlug && properties.length > 0 &&
            (routeView === 'propiedades' || routeView === 'propietats' || routeView === 'properties')) {
            const slugLow = routeSlug.toLowerCase()

            // a) Buscar propiedad cuyo ref_code (en minúsculas, posiblemente con guiones)
            //    coincida con el final del slug. Esto soporta refs tipo "LO-9552",
            //    "REF-7695" o simples "7695".
            let found = properties.find(p => {
                const refLow = makeSlug((p.ref_code || '').toString())
                if (!refLow) return false
                // El slug acaba en "-{ref}" (con guion separador) o es exactamente el ref
                return slugLow === refLow || slugLow.endsWith('-' + refLow)
            })

            // b) Fallback: por slug del título — útil si el slug se compartió antes
            //    de incluir el ref_code o si el ref_code cambió.
            if (!found) {
                found = properties.find(p => {
                    const tSlug = makeSlug(p.title_es || p.title_ca || '')
                    if (!tSlug) return false
                    return slugLow === tSlug || slugLow.startsWith(tSlug + '-')
                })
            }

            if (found) {
                setDetailsProp(found)
                if (autoPrint) setTimeout(() => window.print(), 1200)
            }
        }
    }, [searchParams, properties, routeSlug, routeView])

    useEffect(() => {
        const path = window.location.pathname
        if (path.startsWith('/p/')) {
            const slug = path.split('/p/')[1]
            if (config?.custom_pages) {
                const found = config.custom_pages.find((p: any) => p.slug === slug)
                if (found) {
                    setCustomPageId(found.id)
                    setView('custom')
                }
            }
        }
    }, [config, window.location.pathname])

    useEffect(() => {
        // 1) Soporte URL antigua: /es/blog?post=UUID
        const postId = searchParams.get('post')
        if (postId && posts.length > 0) {
            const found = posts.find(p => p.id === postId)
            if (found && selectedPost?.id !== found.id) {
                setSelectedPost(found)
                if (view !== 'blog') setView('blog')
            }
            return
        }

        // 2) URL SEO-friendly: /es/blog/{slug-titulo}-{shortId}
        if (routeSlug && posts.length > 0 &&
            (routeView === 'blog' || routeView === 'noticies' || routeView === 'news')) {
            const lastDash = routeSlug.lastIndexOf('-')
            const possibleShortId = lastDash >= 0 ? routeSlug.slice(lastDash + 1) : ''
            let found: any = null
            if (possibleShortId.length >= 4) {
                found = posts.find((p: any) => String(p.id || '').endsWith(possibleShortId))
            }
            if (!found) {
                found = posts.find((p: any) =>
                    makeSlug(p.title_es || '') === routeSlug ||
                    makeSlug(p.title_ca || '') === routeSlug ||
                    routeSlug.startsWith(makeSlug(p.title_es || p.title_ca || ''))
                )
            }
            if (found && selectedPost?.id !== found.id) {
                setSelectedPost(found)
                if (view !== 'blog') setView('blog')
            }
        }
    }, [searchParams, posts, routeSlug, routeView])

    // Garantizamos accesibilidad (ratio 4.5:1 contra blanco)
    const accent = ensureContrast(config?.accent_color || '#2563eb')
    const primary = ensureContrast(config?.primary_color || '#0f172a')
    const phoneClear = config?.contact_phone?.replace(/\s/g, '')

    if (loading) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader2 size={40} className="animate-spin" color={accent} /></div>
    
    if (!config) return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
            <Building size={64} color="#e2e8f0" style={{ marginBottom: 20 }} />
            <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 10 }}>Inmobiliaria no encontrada</h1>
            <p style={{ color: '#475569', maxWidth: 400 }}>No hemos podido encontrar la inmobiliaria que buscas. Por favor, verifica el nombre del tenant o contacta con soporte.</p>
            <button
                onClick={() => { localStorage.removeItem('local_dev_tenant'); window.location.href = '/?tenant=demo' }}
                style={{ marginTop: 24, padding: '12px 24px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}
            >
                Ir a Demo
            </button>
        </div>
    )

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
            <header className="public-header-container no-print" style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100,
                background: '#fff', borderBottom: '1px solid #e2e8f0',
                height: 88, display: 'flex', alignItems: 'center', transition: 'all 0.3s',
                boxShadow: scrolled ? '0 10px 30px rgba(0,0,0,0.05)' : 'none'
            }}>
                <div className="public-header-inner" style={{ maxWidth: 1400, margin: '0 auto', width: '100%', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0, maxWidth: '60%' }}>
                        {config?.logo_url ? (
                            <img 
                                src={optimizeImg(config.logo_url, 1000, 95)} 
                                className="main-header-logo"
                                style={{ height: 64, width: 'auto', cursor: 'pointer', objectFit: 'contain', display: 'block', maxWidth: '100%' }} 
                                onClick={() => { 
                                    setDetailsProp(null);
                                    setCustomPageId(null);
                                    setView('home');
                                    setMobileMenuOpen(false);
                                    navigate(`/${lang}`);
                                    window.scrollTo(0, 0);
                                }} 
                                alt={`Logo de ${config.agency_name}`} 
                            />
                        ) : (
                            <div 
                                style={{ fontSize: 24, fontWeight: 900, color: primary, cursor: 'pointer' }} 
                                onClick={() => { 
                                    setDetailsProp(null);
                                    setCustomPageId(null);
                                    setView('home');
                                    setMobileMenuOpen(false);
                                    navigate(addTenant(`/${lang}`));
                                    window.scrollTo(0, 0);
                                }} 
                                aria-label="Volver al inicio"
                            >
                                {config?.agency_name || 'Tu Inmobiliaria'}
                            </div>
                        )}
                        <nav className="public-nav-links pc-only" style={{ display: 'flex', gap: 16, fontSize: 12, fontWeight: 800, color: primary, height: '100%', alignItems: 'center' }}>
                            <span onClick={() => changeView('home')} style={{ cursor: 'pointer', color: view === 'home' ? accent : primary, borderBottom: view === 'home' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>{T_WEB.inicio}</span>
                            <span onClick={() => changeView('properties')} style={{ cursor: 'pointer', color: view === 'properties' ? accent : primary, borderBottom: view === 'properties' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>{T_WEB.propiedades}</span>
                            
                            {(() => {
                                const customItems = (config?.custom_pages || []).filter((p: any) => p.is_active !== false && !p.parent_id);
                                const showBlog = config?.show_blog !== false;
                                // Count items to decide if we hide calculator
                                // Items: Inicio(1), Properties(1), Custom Pages(N), Blog(0/1), Contact(1)
                                const navCount = 3 + customItems.length + (showBlog ? 1 : 0);
                                const hideCalculator = navCount > 6;
                                
                                return (
                                    <>
                                        {customItems.map((p: any) => {
                                            const children = (config?.custom_pages || []).filter((c: any) => c.parent_id === p.id && c.is_active !== false);
                                            const isActive = view === 'custom' && (customPageId === p.id || children.some(c => c.id === customPageId));
                                            
                                            if (children.length > 0) {
                                                return (
                                                    <div key={p.id} className="nav-dropdown" style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
                                                        <span 
                                                            onClick={() => { setView('custom'); setCustomPageId(p.id); navigate(addTenant(`/p/${p.slug}`)); }} 
                                                            style={{ 
                                                                cursor: 'pointer', 
                                                                color: isActive ? accent : primary, 
                                                                borderBottom: isActive ? `2px solid ${accent}` : '2px solid transparent', 
                                                                paddingBottom: 4,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 4
                                                            }}
                                                        >
                                                            {lang !== 'es' ? p.title_ca : p.title_es}
                                                            <ChevronRight size={14} className="rotate-90" />
                                                        </span>
                                                        <div className="dropdown-menu" style={{ 
                                                            position: 'absolute', top: '100%', left: 0, 
                                                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, 
                                                            padding: 10, minWidth: 200, boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                                            opacity: 0, visibility: 'hidden', transform: 'translateY(10px)',
                                                            transition: 'all 0.2s', zIndex: 100
                                                        }}>
                                                            {children.map((c: any) => (
                                                                <div 
                                                                    key={c.id} 
                                                                    onClick={() => { setView('custom'); setCustomPageId(c.id); navigate(addTenant(`/p/${c.slug}`)); }}
                                                                    style={{ 
                                                                        padding: '10px 16px', borderRadius: 8, cursor: 'pointer', 
                                                                        color: customPageId === c.id ? accent : primary,
                                                                        background: customPageId === c.id ? `${accent}08` : 'transparent'
                                                                    }}
                                                                    onMouseEnter={e => (e.currentTarget.style.background = `${accent}08`)}
                                                                    onMouseLeave={e => (e.currentTarget.style.background = customPageId === c.id ? `${accent}08` : 'transparent')}
                                                                >
                                                                    {lang !== 'es' ? c.title_ca : c.title_es}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <span key={p.id} onClick={() => { setView('custom'); setCustomPageId(p.id); navigate(addTenant(`/p/${p.slug}`)); }} style={{ cursor: 'pointer', color: view === 'custom' && customPageId === p.id ? accent : primary, borderBottom: view === 'custom' && customPageId === p.id ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>
                                                    {lang !== 'es' ? p.title_ca : p.title_es}
                                                </span>
                                            );
                                        })}
                                        
                                        <span onClick={() => changeView('contact')} style={{ cursor: 'pointer', color: view === 'contact' ? accent : primary, borderBottom: view === 'contact' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>{T_WEB.contacto}</span>
                                        <span onClick={() => changeView('valuation')} style={{ cursor: 'pointer', color: view === 'valuation' ? accent : primary, borderBottom: view === 'valuation' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>{T_WEB.valoracion}</span>
                                        {showBlog && (
                                            <span onClick={() => changeView('blog')} style={{ cursor: 'pointer', color: view === 'blog' ? accent : primary, borderBottom: view === 'blog' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4 }}>{T_WEB.blog}</span>
                                        )}
                                        {!hideCalculator && (
                                            <div onClick={() => changeView('calculator')} style={{ cursor: 'pointer', color: view === 'calculator' ? accent : primary, borderBottom: view === 'calculator' ? `2px solid ${accent}` : '2px solid transparent', paddingBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Calculator size={16} /> 
                                                <span>{T_WEB.calculadora}</span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </nav>
                    </div>
                    <div className="header-actions-right" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div className="pc-only" style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                            {/* Modern Portals List from config */}
                            {(config?.portals_list || []).filter((p: any) => p.show_on_header && p.url && p.active).map((p: any) => {
                                const name = p.name.toLowerCase()
                                const isIdealista = name.includes('idealista')
                                const defaultLogo = name.includes('idealista') ? 'https://upload.wikimedia.org/wikipedia/commons/3/33/Idealista_logo.svg' :
                                    name.includes('pisos.com') ? 'https://www.pisos.com/comun/img/pisos-logo.svg' :
                                        name.includes('fotocasa') ? 'https://upload.wikimedia.org/wikipedia/commons/2/23/Logo_Fotocasa.svg' :
                                            name.includes('habitaclia') ? 'https://www.habitaclia.com/img/habitaclia-logo.svg' : null

                                return (
                                    <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        background: isIdealista ? '#e1ff00' : '#f8fafc',
                                        color: '#000',
                                        padding: '8px 16px', borderRadius: 12, fontWeight: 900, textDecoration: 'none', fontSize: 12,
                                        boxShadow: isIdealista ? '0 4px 12px rgba(225, 255, 0, 0.25)' : 'none',
                                        border: isIdealista ? 'none' : '1px solid #e2e8f0',
                                        height: 44
                                    }}>
                                        {(p.logo || defaultLogo) ? <img src={p.logo || defaultLogo} alt={p.name} style={{ height: 36, maxWidth: 120, objectFit: 'contain' }} /> : <span>{p.name}</span>}
                                    </a>
                                )
                            })}
                            {/* Legacy Idealista link if portals_list is empty/not configured */}
                            {(!config?.portals_list || config.portals_list.length === 0) && (config as any)?.portal_idealista_url && (
                                <a href={(config as any).portal_idealista_url} target="_blank" rel="noopener noreferrer" style={{
                                    display: 'flex', alignItems: 'center', gap: 8, background: '#e1ff00', color: '#000',
                                    padding: '8px 16px', borderRadius: 12, fontWeight: 900, textDecoration: 'none', fontSize: 13,
                                    boxShadow: '0 4px 12px rgba(225, 255, 0, 0.25)',
                                    height: 36
                                }}>
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/3/33/Idealista_logo.svg" alt="idealista" style={{ height: 24 }} />
                                </a>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 14, color: primary }}>
                                <Phone size={16} /> {config?.contact_phone}
                            </div>
                            <nav style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 4 }}>
                                <button onClick={() => changeLang('es')} style={{ border: 'none', background: lang === 'es' ? '#fff' : 'transparent', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>ES</button>
                                <button onClick={() => changeLang(config?.secondary_language || 'ca')} style={{ border: 'none', background: lang !== 'es' ? '#fff' : 'transparent', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>{(config?.secondary_language || 'ca').toUpperCase()}</button>
                            </nav>
                            <button
                                onClick={() => navigate('/admin')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, background: primary, color: '#fff',
                                    border: 'none', padding: '10px 20px', borderRadius: 12, fontWeight: 700,
                                    fontSize: 13, cursor: 'pointer', height: 44, boxShadow: `0 4px 12px ${primary}30`
                                }}
                            >
                                <Lock size={15} /> Admin
                            </button>

                        </div>
                        <div className="mobile-only-btn" style={{ display: 'none', gap: 8, alignItems: 'center' }}>
                                <button 
                                    onClick={() => changeView('calculator')} 
                                    aria-label={lang !== 'es' ? T_WEB.calculadora : 'Calculadora'}
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 10, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: primary }}
                                >
                                    <Calculator size={20} />
                                </button>
                                {config?.contact_whatsapp && (
                                    <button
                                        type="button"
                                        onClick={() => window.dispatchEvent(new CustomEvent('open-assistant'))}
                                        aria-label="WhatsApp Asistente"
                                        style={{ background: '#25D366', border: 'none', padding: 10, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', textDecoration: 'none', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.2)' }}
                                    >
                                        <WhatsAppIcon size={20} />
                                    </button>
                                )}
                                <a 
                                    href={`tel:${config?.contact_phone}`} 
                                    aria-label={lang === 'ca' ? 'Trucar' : 'Llamar'}
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 10, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: primary, textDecoration: 'none' }}
                                >
                                    <Phone size={20} />
                                </a>
                            <button 
                                onClick={() => setMobileMenuOpen(true)} 
                                aria-label={lang === 'ca' ? 'Obrir menú' : 'Abrir menú'}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 10, borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Menu size={24} color={primary} />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 2000, display: 'flex', flexDirection: 'column', padding: 30 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div 
                                onClick={() => { changeView('home'); setMobileMenuOpen(false); }} 
                                style={{ fontSize: 24, fontWeight: 900, color: primary, cursor: 'pointer' }}
                            >
                                {config?.agency_name}
                            </div>
                            <button 
                                onClick={() => setMobileMenuOpen(false)} 
                                aria-label={T_WEB.cerrar}
                                style={{ background: '#f1f5f9', border: 'none', padding: 12, borderRadius: '50%', cursor: 'pointer', color: primary }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 14, padding: 4, marginBottom: 30 }}>
                            <button onClick={() => changeLang('es')} style={{ flex: 1, border: 'none', background: lang === 'es' ? '#fff' : 'transparent', padding: '12px', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 12, color: lang === 'es' ? primary : '#475569', boxShadow: lang === 'es' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none' }}>CASTELLANO</button>
                            <button onClick={() => changeLang(config?.secondary_language || 'ca')} style={{ flex: 1, border: 'none', background: lang !== 'es' ? '#fff' : 'transparent', padding: '12px', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 12, color: lang !== 'es' ? primary : '#475569', boxShadow: lang !== 'es' ? '0 2px 8px rgba(0,0,0,0.05)' : 'none' }}>{(config?.secondary_language || 'ca') === 'ca' ? 'CATALÀ' : (config?.secondary_language || 'en') === 'en' ? 'ENGLISH' : (config?.secondary_language || 'fr') === 'fr' ? 'FRANÇAIS' : (config?.secondary_language || 'de') === 'de' ? 'DEUTSCH' : (config?.secondary_language || '').toUpperCase()}</button>
                        </div>
                        <nav style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <span onClick={() => { changeView('home'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'home' ? accent : primary }}>{T_WEB.inicio}</span>
                            <span onClick={() => { changeView('properties'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'properties' ? accent : primary }}>{T_WEB.propiedades}</span>

                            
                            {(config?.custom_pages || []).filter((p: any) => p.is_active !== false && !p.parent_id).map((p: any) => {
                                const children = (config?.custom_pages || []).filter((c: any) => c.parent_id === p.id && c.is_active !== false);
                                const isExpanded = openMobileSubmenus.includes(p.id);
                                return (
                                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span 
                                                onClick={() => { 
                                                    if (children.length > 0) {
                                                        setOpenMobileSubmenus(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]);
                                                    } else {
                                                        setView('custom'); setCustomPageId(p.id); navigate(`/p/${p.slug}`); setMobileMenuOpen(false); 
                                                    }
                                                }} 
                                                style={{ fontSize: 22, fontWeight: 800, color: (view === 'custom' && customPageId === p.id) ? accent : primary, flex: 1 }}
                                            >
                                                {lang === 'ca' ? p.title_ca : p.title_es}
                                            </span>
                                            {children.length > 0 && (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setOpenMobileSubmenus(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id]); }}
                                                    aria-label={lang === 'ca' ? 'Veure subpagines' : 'Ver subpáginas'}
                                                    aria-expanded={isExpanded}
                                                    style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: primary }}
                                                >
                                                    <ChevronRight size={20} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                </button>
                                            )}
                                        </div>
                                        {children.length > 0 && isExpanded && (
                                            <div style={{ paddingLeft: 16, borderLeft: `2px solid ${accent}44`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {children.map(child => (
                                                    <span key={child.id} onClick={() => { setView('custom'); setCustomPageId(child.id); navigate(addTenant(`/p/${child.slug}`)); setMobileMenuOpen(false); }} style={{ fontSize: 18, fontWeight: 700, opacity: 0.8, color: (view === 'custom' && customPageId === child.id) ? accent : primary }}>
                                                        {lang === 'ca' ? child.title_ca : child.title_es}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <span onClick={() => { changeView('valuation'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'valuation' ? accent : primary }}>{T_WEB.valoracion}</span>
                            <span onClick={() => { changeView('contact'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'contact' ? accent : primary }}>{T_WEB.contacto}</span>
                            {config?.show_blog !== false && (
                                <span onClick={() => { changeView('blog'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'blog' ? accent : primary }}>Blog</span>
                            )}
                            <span onClick={() => { changeView('calculator'); setMobileMenuOpen(false); }} style={{ fontSize: 22, fontWeight: 800, color: view === 'calculator' ? accent : primary }}>{lang === 'ca' ? 'Calculadora' : 'Calculadora'}</span>
                        </nav>
                        <div style={{ marginTop: 'auto', borderTop: '1px solid #f1f5f9', paddingTop: 30 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                                <Phone size={20} color={accent} />
                                <div style={{ fontSize: 18, fontWeight: 800 }}>{config?.contact_phone}</div>
                            </div>
                            <button onClick={() => { window.location.href = addTenant('/admin') }} style={{ width: '100%', padding: '16px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', fontWeight: 800, color: primary }}>Accés Admin</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <main style={{ paddingTop: 88, flex: 1, position: 'relative' }}>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={view + (view === 'custom' ? customPageId : '')}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                        {view === 'home' && (
                            <HomeView
                                config={config!}
                                properties={properties}
                                pageBlocks={(() => {
                                    let homeSeq = (config as any)?.default_blocks_visibility
                                    if (!Array.isArray(homeSeq) || homeSeq.length === 0) {
                                        homeSeq = [
                                            { id: 'hero', is_system: true, active: true },
                                            { id: 'stats', is_system: true, active: true },
                                            { id: 'services', is_system: true, active: true },
                                            { id: 'properties', is_system: true, active: true },
                                            { id: 'sell_cta', is_system: true, active: true },
                                            { id: 'contact_bar', is_system: true, active: true },
                                            { id: 'blog', is_system: true, active: true },
                                        ];
                                    }
                                    return homeSeq.map((s: any) => {
                                        if (s.is_system || ['hero', 'stats', 'services', 'properties', 'sell_cta', 'contact_banner', 'contact_bar', 'blog'].includes(s.id)) {
                                            return { id: s.id, is_system: true, is_active: s.active ?? true };
                                        }
                                        const b = pageBlocks.find((pb: any) => pb.id === s.id);
                                        if (b) return { ...b, is_active: s.active ?? true };
                                        return null;
                                    }).filter((b: any) => b && b.is_active !== false);
                                })()}
                                lang={lang}
                                setView={handleLinkClick}
                                setDetails={setDetailsProp}
                                onShowMap={setMapProp}
                                STATUS_BADGE={STATUS_BADGE}
                                onDossier={(p: any) => { setDetailsProp(p); setOpenPdfOnLoad(true); }}
                                onLightbox={(images: string[], index: number) => setLightbox({ open: true, images, index })}
                                posts={posts}
                                setSelectedPost={setSelectedPost}
                            />
                        )}
                        {view === 'custom' && (
                            <HomeView
                                config={config!}
                                properties={properties}
                                pageBlocks={(() => {
                                    const page = config?.custom_pages?.find((p: any) => p.id === customPageId);
                                    if (!page || !Array.isArray(page.sequence)) return [];
                                    return (page.sequence || []).map((s: any) => {
                                        if (s.is_system || ['hero', 'stats', 'services', 'properties', 'sell_cta', 'contact_banner', 'contact_bar', 'blog'].includes(s.id)) {
                                            return { id: s.id, is_system: true, is_active: s.active ?? true };
                                        }
                                        const b = pageBlocks.find((pb: any) => pb.id === s.id);
                                        if (b) return { ...b, is_active: s.active ?? true };
                                        return null;
                                    }).filter((b: any) => b && b.is_active !== false);
                                })()}
                                lang={lang}
                                setView={handleLinkClick}
                                setDetails={setDetailsProp}
                                onShowMap={setMapProp}
                                STATUS_BADGE={STATUS_BADGE}
                                onDossier={(p: any) => { setDetailsProp(p); setOpenPdfOnLoad(true); }}
                                onLightbox={(images: string[], index: number) => setLightbox({ open: true, images, index })}
                                posts={posts}
                                setSelectedPost={setSelectedPost}
                            />
                        )}
                        {view === 'properties' && <PropertiesView config={config!} properties={properties} lang={lang} setDetails={setDetailsProp} setView={changeView} onShowMap={setMapProp} STATUS_BADGE={STATUS_BADGE} onDossier={(p: any) => { setDetailsProp(p); setOpenPdfOnLoad(true); }} />}
                        {view === 'valuation' && <ValuationView config={config!} lang={lang} />}
                        {view === 'contact' && <ContactView config={config!} lang={lang} />}
                        {view === 'calculator' && <CalculatorView config={config!} lang={lang} />}
                        {view === 'blog' && <BlogView config={config!} lang={lang} accent={accent} primary={primary} posts={posts} selectedPost={selectedPost} setSelectedPost={setSelectedPost} />}
                    </motion.div>
                </AnimatePresence>
            </main>

            <AnimatePresence>
                {pdfProp && (
                    <PdfPreview
                        prop={pdfProp}
                        lang={lang}
                        config={config!}
                        onClose={() => setPdfProp(null)}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showCookieConsent && (
                    <div style={{ position: 'fixed', bottom: 30, left: 0, right: 0, zIndex: 5000, display: 'flex', justifyContent: 'center', padding: '0 20px', pointerEvents: 'none' }}>
                        <motion.div 
                            initial={{ y: 100, opacity: 0 }} 
                            animate={{ y: 0, opacity: 1 }} 
                            exit={{ y: 100, opacity: 0 }}
                            style={{ 
                                pointerEvents: 'auto',
                                background: '#fff', 
                                borderRadius: 28, 
                                boxShadow: '0 25px 60px -12px rgba(0,0,0,0.3)', 
                                padding: '32px 40px', 
                                border: '1px solid #f1f5f9',
                                width: '100%',
                                maxWidth: 640,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 24
                            }}
                        >
                             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 id="cookie-title" style={{ fontSize: 22, fontWeight: 900, color: primary, margin: 0 }}>{lang === 'ca' ? 'Preferències de Privadesa' : 'Preferencias de Privacidad'}</h3>
                                <button 
                                    onClick={() => setShowCookieConsent(false)} 
                                    aria-label={lang === 'ca' ? 'Tancar avís' : 'Cerrar aviso'}
                                    style={{ background: '#f8fafc', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
                                {lang === 'ca' 
                                    ? "Per oferir les millors experiències, utilitzem tecnologies com les cookies per emmagatzemar i/o accedir a la informació del dispositiu. El consentiment a aquestes tecnologies ens permetrà processar dades com el comportament de navegació. No consentir pot afectar negativament certes funcions."
                                    : "Para ofrecer las mejores experiencias, utilizamos tecnologías como las cookies para almacenar y/o acceder a la información del dispositivo. El consentimiento a estas tecnologías nos permitirá procesar datos como el comportamiento de navegación. No consentir puede afectar negativamente a ciertas funciones."
                                }
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                <button onClick={() => handleCookieAction('accepted')} style={{ background: accent, color: '#fff', border: 'none', padding: '14px 28px', borderRadius: 14, fontWeight: 800, cursor: 'pointer', flex: 1, minWidth: 120 }}>ACEPTAR</button>
                                <button onClick={() => handleCookieAction('declined')} style={{ background: '#fff', color: primary, border: `2px solid #f1f5f9`, padding: '14px 28px', borderRadius: 14, fontWeight: 800, cursor: 'pointer', flex: 1, minWidth: 120 }}>RECHAZAR</button>
                                <button onClick={() => setShowCookieConsent(false)} style={{ background: '#fff', color: '#475569', border: `2px solid #f1f5f9`, padding: '14px 28px', borderRadius: 14, fontWeight: 800, cursor: 'pointer', flex: 1, minWidth: 120 }}>CONFIGURAR</button>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 24, borderTop: '1px solid #f8fafc', paddingTop: 16 }}>
                                <span onClick={() => setLegalModal({ title: 'Política de Cookies', content: lang === 'ca' ? (config?.legal_cookies_ca || config?.legal_cookies_es || '') : (config?.legal_cookies_es || '') })} style={{ fontSize: 12, color: accent, fontWeight: 700, cursor: 'pointer' }}>Política de Cookies</span>
                                <span onClick={() => setLegalModal({ title: 'Política de Privacidad', content: lang === 'ca' ? (config?.legal_privacy_ca || config?.legal_privacy_es || '') : (config?.legal_privacy_es || '') })} style={{ fontSize: 12, color: accent, fontWeight: 700, cursor: 'pointer' }}>Política de privacitat</span>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {detailsProp && <DetailsModal prop={detailsProp} config={config!} properties={properties} openPdfOnLoad={openPdfOnLoad} onPdfOpened={() => setOpenPdfOnLoad(false)} onOpenPdf={setPdfProp} onClose={() => {
                    setDetailsProp(null)
                    setOpenPdfOnLoad(false)
                }} onShowMap={setMapProp} onSelectProp={setDetailsProp} currentView={view} lang={lang} navigate={navigate} />}
            </AnimatePresence>

            <AnimatePresence>
                {mapProp && (
                    <MapModal
                        prop={mapProp}
                        config={config!}
                        lang={lang}
                        onClose={() => setMapProp(null)}
                        onSelectProp={setDetailsProp}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {lightbox.open && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox({ ...lightbox, open: false })}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <button onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, open: false }) }} style={{ position: 'absolute', top: 30, right: 30, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={40} /></button>
                        <motion.img 
                            key={lightbox.index}
                            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                            src={lightbox.images[lightbox.index]} 
                            style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} 
                            onClick={e => e.stopPropagation()} 
                            alt=""
                        />
                        {lightbox.images.length > 1 && (
                            <>
                                <button onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length }) }} style={{ position: 'absolute', left: 30, background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 60, height: 60, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={30} /></button>
                                <button onClick={(e) => { e.stopPropagation(); setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length }) }} style={{ position: 'absolute', right: 30, background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 60, height: 60, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={30} /></button>
                                <div style={{ position: 'absolute', bottom: 30, color: '#fff', fontSize: 16, fontWeight: 700 }}>{lightbox.index + 1} / {lightbox.images.length}</div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {legalModal && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLegalModal(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
                        <motion.div initial={{ y: 50, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 50, opacity: 0, scale: 0.95 }}
                            style={{ position: 'relative', background: '#fff', borderRadius: 24, width: '100%', maxWidth: 800, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                            <div style={{ padding: '24px 32px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ fontSize: 20, fontWeight: 900, color: config?.primary_color || '#1e3a8a' }}>{legalModal.title}</h2>
                                <button onClick={() => setLegalModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer' }}><X size={18} /></button>
                            </div>
                            <div
                                className="legal-content-body"
                                style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    padding: '40px 48px',
                                    fontSize: 16,
                                    lineHeight: 1.8,
                                    color: '#334155',
                                    whiteSpace: 'pre-wrap'
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: autoFormatLegal(legalModal.content || (lang === 'ca' ? 'Contingut no disponible.' : 'Contenido no disponible.'))
                                }}
                            />
                            <div style={{ padding: '16px 32px', borderTop: '1px solid #f1f5f9', textAlign: 'right', background: '#f8fafc' }}>
                                <button onClick={() => setLegalModal(null)} style={{ background: config?.primary_color || '#1e3a8a', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{lang === 'ca' ? 'Entès' : 'Entendido'}</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            <style>{`
                /* Menu Visibility Fix */
                .pc-only { display: flex !important; }
                .mobile-only-btn { display: none !important; }

                @media (max-width: 1200px) {
                    .pc-only { display: none !important; }
                    .mobile-only-btn { display: flex !important; gap: 6px !important; }
                    .mobile-only-btn button, .mobile-only-btn a { padding: 8px !important; }
                    .public-header-inner { padding: 0 12px !important; }
                    .public-header-container { height: 80px !important; }
                    .main-header-logo { height: 48px !important; max-width: 140px !important; }
                    .public-hero-title { font-size: 36px !important; }
                    section { padding: 60px 20px !important; }
                    .header-actions-right { gap: 6px !important; }
                    .details-main-layout { grid-template-columns: 1fr !important; margin-top: 20px !important; }
                    .details-sidebar { order: -1; }
                    .details-hero-img { height: 350px !important; }
                    .details-stats-grid { grid-template-columns: 1fr 1fr !important; }
                    .details-title { font-size: 28px !important; }
                    .details-info-card { padding: 30px 20px !important; }
                    .details-equip-card { padding: 20px !important; }
                    .equip-grid { grid-template-columns: 1fr !important; }
                }

                /* Dropdown styles (Desktop) */
                .nav-dropdown { position: relative; display: flex; align-items: center; cursor: pointer; height: 100%; }
                .nav-dropdown .dropdown-menu {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    opacity: 0;
                    visibility: hidden;
                    transform: translateY(10px);
                    transition: all 0.2s ease;
                    pointer-events: none;
                }
                .nav-dropdown:hover .dropdown-menu {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0);
                    pointer-events: auto;
                }
                .rotate-90 { transition: transform 0.2s; }
                .nav-dropdown:hover .rotate-90 { transform: rotate(270deg); }

                /* Generic Utilities */
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .animate-spin { animation: spin 1s linear infinite; }

                /* Legal/Blog Content Styling */
                .legal-content-body h1 { font-size: 24px; font-weight: 900; margin: 32px 0 16px; color: ${primary}; }
                .legal-content-body h2 { font-size: 20px; font-weight: 800; margin: 28px 0 14px; color: ${primary}; }
                .legal-content-body h3, .blog-content h2 { font-size: 18px; font-weight: 700; margin: 24px 0 12px; color: ${primary}; }
                .legal-content-body p, .blog-content p { margin: 0 0 16px; line-height: 1.8; }
                .legal-content-body ul, .legal-content-body ol, .blog-content ul, .blog-content ol { padding-left: 24px; margin: 16px 0; }
                .legal-content-body li, .blog-content li { margin-bottom: 8px; }
                .blog-content blockquote { border-left: 3px solid ${accent}; padding-left: 16px; color: #64748b; font-style: italic; margin: 20px 0; }
            `}</style>
            <Footer config={config!} lang={lang} view={view} setView={changeView} customPageId={customPageId} setCustomPageId={setCustomPageId} navigate={navigate} />
            <WhatsAppWidget config={config!} lang={lang} />
        </div>
    )
}

function WhatsAppWidget({ config, lang }: { config: AgencyConfig; lang: 'es' | 'ca' }) {
    type Step = 'options' | 'details' | 'contact' | 'success';
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState<Step>('options');
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [details, setDetails] = useState({ zone: '', propertyType: '', budget: '', rooms: '' });
    const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
    const [isSaving, setIsSaving] = useState(false);

    // Permite abrir el asistente desde fuera (p.ej. icono de WhatsApp del header móvil)
    useEffect(() => {
        const handler = () => { setIsOpen(true); setStep('options'); };
        window.addEventListener('open-assistant', handler);
        return () => window.removeEventListener('open-assistant', handler);
    }, []);

    const rawWA = (config.contact_whatsapp || config.contact_phone || '').replace(/\D/g, '');
    if (!rawWA) return null;
    const whatsapp = rawWA.startsWith('34') ? rawWA : (rawWA.length === 9 ? `34${rawWA}` : rawWA);

    const primary = ensureContrast(config.primary_color || '#1e3a8a');
    const accent = ensureContrast(config.accent_color || '#1d4ed8');

    const L = (ca: string, es: string) => lang === 'ca' ? ca : es;

    const handleOptionClick = (type: string) => {
        setSelectedType(type);
        setStep('details');
    };

    const reset = () => {
        setStep('options');
        setSelectedType(null);
        setDetails({ zone: '', propertyType: '', budget: '', rooms: '' });
        setFormData({ name: '', phone: '', email: '' });
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.phone) return;
        setIsSaving(true);

        const detailType = selectedType === 'buy' ? L('Compra', 'Compra') :
                           selectedType === 'rent' ? L('Lloguer', 'Alquiler') :
                           L('Venda', 'Venta');

        const intro = L('Nou contacte des de la web', 'Nuevo contacto desde la web');
        const msg = `*${intro}*\n\n` +
                    `*${L('Nom', 'Nombre')}:* ${formData.name}\n` +
                    `*${L('Telèfon', 'Teléfono')}:* ${formData.phone}\n` +
                    (formData.email ? `*Email:* ${formData.email}\n` : '') +
                    `*${L('Interès', 'Interés')}:* ${detailType}\n` +
                    (details.zone ? `*${L('Zona / Municipi', 'Zona / Municipio')}:* ${details.zone}\n` : '') +
                    (details.propertyType ? `*${L('Tipus de Propietat', 'Tipo de Propiedad')}:* ${details.propertyType}\n` : '') +
                    (details.rooms ? `*${L('Habitacions', 'Habitaciones')}:* ${details.rooms}\n` : '') +
                    (details.budget ? `*${isSell ? L('Preu aproximat', 'Precio aproximado') : L('Pressupost màxim', 'Presupuesto máximo')}:* ${details.budget}\n` : '');

        // Generamos el UUID en cliente: la RLS de leads bloquea la lectura post-INSERT
        // para usuarios anónimos, así que .select().maybeSingle() devolvía null y
        // perdíamos el id. Generando aquí el UUID podemos usarlo en las llamadas
        // posteriores (auto-reply, notificación por email).
        const newLeadId = crypto.randomUUID()

        // Si el visitante está actualmente en la ficha pública de una propiedad
        // (URL tipo /es/propiedades/<slug>), intentamos vincular esa propiedad
        // al lead. La URL del momento de envío es la mejor heurística disponible
        // — el widget en sí es global pero a veces se abre desde una ficha.
        let detectedPropertyId: string | null = null
        try {
            const m = window.location.pathname.match(/\/(?:es|ca|en|fr|de)\/(?:propiedades|propietats|properties)\/([^\/?#]+)/i)
            if (m && m[1]) {
                // El slug acaba en "-<ref_code>" en minúsculas. Extraemos el ref
                // y buscamos la propiedad por agency_id + ref_code (la RLS pública
                // de SELECT en properties ya permite anon ver propiedades activas).
                const slug = m[1].toLowerCase()
                const refMatch = slug.match(/[a-z0-9]+(?:[-_][a-z0-9]+)*$/)
                if (refMatch) {
                    const possibleRef = refMatch[0]
                    const { data: pRow } = await supabase
                        .from('properties')
                        .select('id, ref_code')
                        .eq('agency_id', config.agency_id)
                        .ilike('ref_code', possibleRef)
                        .maybeSingle()
                    if (pRow?.id) detectedPropertyId = pRow.id
                }
            }
        } catch (e) {
            console.info('[WhatsApp Widget] No se pudo auto-detectar propiedad:', e)
        }

        try {
            // 1. Guardar el lead con todos los datos del interés ya estructurados.
            const { error: leadErr } = await supabase.from('leads').insert({
                id: newLeadId,
                agency_id: config.agency_id,
                property_id: detectedPropertyId,
                name: formData.name,
                phone: formData.phone,
                email: formData.email || null,
                message: msg,
                status: 'NUEVOS',
                source: 'whatsapp',
                type: detailType
            })
            if (leadErr) {
                console.error('[WhatsApp Widget] Error guardando lead:', leadErr.message, leadErr.details, leadErr.hint)
            } else {
                console.log('[WhatsApp Widget] Lead guardado con id:', newLeadId)
            }

            // 2. Auto-reply opcional vía Edge Function (Twilio)
            try {
                await supabase.functions.invoke('whatsapp-autorespond', {
                    body: {
                        to_phone: formData.phone,
                        client_name: formData.name,
                        agency_name: config.name || 'la agencia',
                        lang,
                        lead_id: newLeadId
                    }
                });
            } catch (fnErr) {
                console.info('[WhatsApp Widget] Auto-reply Twilio no configurado:', fnErr);
            }

            // 3. Notificación al email de contacto de la agencia (idempotente —
            //    si el trigger SQL ya la disparó, la función simplemente hace skip).
            try {
                await supabase.functions.invoke('send-lead-notification', {
                    body: { lead_id: newLeadId }
                })
            } catch (notifyErr) {
                console.info('[WhatsApp Widget] Notificación por email no disponible:', notifyErr)
            }

            // 4. Abrir WhatsApp con el mensaje preparado hacia la agencia
            window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
            setStep('success');

        } catch (err) {
            console.error('[WhatsApp Widget] Error inesperado:', err);
            // No bloqueamos al usuario: abrimos WhatsApp aunque algo falle en CRM
            window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
            setStep('success');
        } finally {
            setIsSaving(false);
        }
    };

    const INP = { width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' as const };
    const BTN_OUTLINE = { width: '100%', padding: '12px', borderRadius: 12, border: `1.5px solid ${primary}`, background: '#fff', color: primary, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 };
    const BTN_SOLID = { width: '100%', padding: '14px', borderRadius: 12, background: '#25D366', color: '#fff', border: 'none', fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 8px 20px rgba(37,211,102,0.3)' };
    const BUBBLE = { background: '#fff', padding: '14px 16px', borderRadius: '0 16px 16px 16px', fontSize: 13, color: '#1e293b', lineHeight: 1.6, marginBottom: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' };
    const BACK_BTN = { background: 'none', border: 'none', color: '#94a3b8', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 };

    const isSell = selectedType === 'sell';

    return (
        <div className="whatsapp-widget-container no-print" style={{ position: 'fixed', bottom: 30, right: 30, zIndex: 2000, fontFamily: 'Inter, sans-serif' }}>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="whatsapp-widget-panel"
                        initial={{ opacity: 0, scale: 0.85, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.85, y: 20 }}
                        style={{ position: 'absolute', bottom: 80, right: 0, width: 'min(330px, calc(100vw - 40px))', background: '#fff', borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', border: '1px solid #f1f5f9' }}
                    >
                        {/* Header */}
                        <div style={{ background: primary, padding: '18px 20px', color: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <User size={18} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 14 }}>{L('Assistent Online', 'Asistente Online')}</div>
                                <div style={{ fontSize: 11, opacity: 0.8 }}>{L('Responem ràpidament', 'Respondemos rápido')} ⚡</div>
                            </div>
                            {/* Step indicator */}
                            <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
                                {['options','details','contact'].map((s,i) => (
                                    <div key={s} style={{ width: 6, height: 6, borderRadius: '50%', background: ['options','details','contact','success'].indexOf(step) > i ? '#fff' : 'rgba(255,255,255,0.3)', transition: 'all 0.3s' }} />
                                ))}
                            </div>
                            <button onClick={() => { setIsOpen(false); reset(); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4 }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '20px 20px 16px', background: '#f8fafc', maxHeight: 'min(75vh, 620px)', overflowY: 'auto' }}>
                            <AnimatePresence mode="wait">

                                {/* STEP 1: Intent */}
                                {step === 'options' && (
                                    <motion.div key="options" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                        <div style={BUBBLE}>{L('Hola! 👋 En que puc ajudar-te avui? Vols comprar, llogar o vendre una propietat?', 'Hola! 👋 ¿En qué puedo ayudarte hoy? ¿Quieres comprar, alquilar o vender una propiedad?')}</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <button style={BTN_OUTLINE} onClick={() => handleOptionClick('buy')}><Home size={15}/> {L('Vull Comprar', 'Quiero Comprar')}</button>
                                            <button style={BTN_OUTLINE} onClick={() => handleOptionClick('rent')}><Key size={15}/> {L('Vull Llogar', 'Quiero Alquilar')}</button>
                                            <button style={{ ...BTN_OUTLINE, background: accent, color: '#fff', border: `1.5px solid ${accent}`, boxShadow: `0 4px 12px ${accent}30` }} onClick={() => handleOptionClick('sell')}><TrendingUp size={15}/> {L('Vull Vendre', 'Quiero Vender')}</button>
                                        </div>
                                    </motion.div>
                                )}

                                {/* STEP 2: Property Details */}
                                {step === 'details' && (
                                    <motion.div key="details" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                        <div style={BUBBLE}>
                                            {isSell
                                                ? L('Perfecte! Explica\'m una mica la propietat que vols posar a la venda:', 'Perfecto! Cuéntame un poco sobre la propiedad que quieres poner en venta:')
                                                : L('Genial! Diga\'m una mica el que busques:', 'Genial! Cuéntame un poco lo que buscas:')}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>{L('Zona o Municipi', 'Zona o Municipio')}</div>
                                                <input style={INP} placeholder={L('Ex: Tarragona, Reus...', 'Ej: Barcelona, Tarragona...')} value={details.zone} onChange={e => setDetails({...details, zone: e.target.value})} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>{L('Tipus de propietat', 'Tipo de propiedad')}</div>
                                                <select style={INP} value={details.propertyType} onChange={e => setDetails({...details, propertyType: e.target.value})}>
                                                    <option value="">{L('Selecciona...', 'Selecciona...')}</option>
                                                    <option>{L('Pis', 'Piso')}</option>
                                                    <option>{L('Casa / Xalet', 'Casa / Chalet')}</option>
                                                    <option>{L('Local Comercial', 'Local Comercial')}</option>
                                                    <option>{L('Oficina', 'Oficina')}</option>
                                                    <option>{L('Solar / Terreny', 'Solar / Terreno')}</option>
                                                    <option>{L('Garatge', 'Garaje')}</option>
                                                </select>
                                            </div>
                                            {!isSell && (
                                                <div>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>{L('Habitacions (aprox.)', 'Habitaciones (aprox.)')}</div>
                                                    <select style={INP} value={details.rooms} onChange={e => setDetails({...details, rooms: e.target.value})}>
                                                        <option value="">{L('Indiferent', 'Indiferente')}</option>
                                                        <option>1</option><option>2</option><option>3</option><option>4+</option>
                                                    </select>
                                                </div>
                                            )}
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
                                                    {isSell ? L('Preu aproximat', 'Precio aproximado') : L('Pressupost màxim', 'Presupuesto máximo')}
                                                </div>
                                                <select style={INP} value={details.budget} onChange={e => setDetails({...details, budget: e.target.value})}>
                                                    <option value="">{L('Selecciona...', 'Selecciona...')}</option>
                                                    {isSell ? (
                                                        <>
                                                            <option>{'< 100.000€'}</option><option>100.000€ - 200.000€</option>
                                                            <option>200.000€ - 350.000€</option><option>350.000€ - 500.000€</option>
                                                            <option>{'> 500.000€'}</option>
                                                        </>
                                                    ) : selectedType === 'rent' ? (
                                                        <>
                                                            <option>{'< 500€/mes'}</option><option>500€ - 800€/mes</option>
                                                            <option>800€ - 1.200€/mes</option><option>{'> 1.200€/mes'}</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option>{'< 100.000€'}</option><option>100.000€ - 200.000€</option>
                                                            <option>200.000€ - 350.000€</option><option>350.000€ - 500.000€</option>
                                                            <option>{'> 500.000€'}</option>
                                                        </>
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                            <button style={BACK_BTN} onClick={() => setStep('options')}>← {L('Tornar', 'Volver')}</button>
                                            <button style={{ ...BTN_SOLID, flex: 1, padding: '12px', fontSize: 13 }} onClick={() => setStep('contact')}>
                                                {L('Continuar', 'Continuar')} →
                                            </button>
                                        </div>
                                    </motion.div>
                                )}

                                {/* STEP 3: Contact Form */}
                                {step === 'contact' && (
                                    <motion.div key="contact" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                                        <div style={BUBBLE}>{L('Quasi llest! Deixa\'m les teves dades per contactar amb tu:', 'Casi listo! Déjame tus datos para que un comercial te contacte enseguida:')}</div>
                                        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <input required style={INP} placeholder={L('Nom complet *', 'Nombre completo *')} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                            <input required type="tel" style={INP} placeholder={L('Telèfon *', 'Teléfono *')} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                                            <input type="email" style={INP} placeholder={L('Email (opcional)', 'Email (opcional)')} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                            <button type="submit" disabled={isSaving} style={{ ...BTN_SOLID, marginTop: 8 }}>
                                                {isSaving ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                                                {L('Enviar i obrir WhatsApp', 'Enviar y abrir WhatsApp')}
                                            </button>
                                            <button type="button" style={BACK_BTN} onClick={() => setStep('details')}>← {L('Tornar', 'Volver')}</button>
                                        </form>
                                    </motion.div>
                                )}

                                {/* STEP 4: Success */}
                                {step === 'success' && (
                                    <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: 'center', padding: '20px 10px' }}>
                                        <div style={{ width: 64, height: 64, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                            <svg viewBox="0 0 24 24" width="32" height="32" fill="#16a34a"><path d="M20 6L9 17l-5-5"/><path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" fill="none"/></svg>
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>{L('Missatge enviat! 🎉', '¡Mensaje enviado! 🎉')}</div>
                                        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
                                            {L('Un comercial et contactarà molt aviat. Si tens pressa, WhatsApp ja és obert!', 'Un comercial te contactará muy pronto. Si tienes prisa, ¡WhatsApp ya está abierto!')}
                                        </div>
                                        <button style={{ ...BTN_OUTLINE, fontSize: 12 }} onClick={() => { reset(); setIsOpen(false); }}>{L('Tancar', 'Cerrar')}</button>
                                    </motion.div>
                                )}

                            </AnimatePresence>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                className="whatsapp-widget-trigger"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                    if (isOpen) {
                        setIsOpen(false);
                        setStep('options');
                    } else {
                        setIsOpen(true);
                    }
                }}
                style={{
                    width: 68, height: 68, borderRadius: '50%', background: '#25D366',
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 30px rgba(37, 211, 102, 0.5)', color: '#fff', position: 'relative',
                    padding: 0
                }}
            >
                <svg viewBox="0 0 448 512" width="38" height="38" fill="currentColor" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>
                    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-5.5-2.8-23.4-8.6-44.6-27.6-16.5-14.7-27.6-32.8-30.8-38.4-3.2-5.6-.3-8.6 2.5-11.4 2.5-2.5 5.5-6.5 8.3-9.7 2.8-3.2 3.7-5.6 5.6-9.3 1.9-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 13.2 5.7 23.5 9.2 31.6 11.8 13.3 4.2 25.4 3.6 35 2.2 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                </svg>
                {!isOpen && (
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="whatsapp-badge"
                        style={{
                            position: 'absolute', top: -5, right: -5, width: 22, height: 22,
                            background: accent, borderRadius: '50%', color: '#fff', fontSize: 11,
                            fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)', border: '2px solid #fff'
                        }}
                    >
                        1
                    </motion.div>
                )}
            </motion.button>
        </div>
    );
}

export function HomeView({ config, properties, pageBlocks, lang, setView, setDetails, onShowMap, STATUS_BADGE, onDossier, onLightbox, posts, setSelectedPost }: any) {
    const accent = config.accent_color || '#1d4ed8'
    const primary = config.primary_color || '#1e3a8a'
    const [heroIdx, setHeroIdx] = useState(0)
    const [featTab, setFeatTab] = useState<'todos' | 'venda' | 'lloguer'>('todos')

    const banners = config.banners_list && config.banners_list.length > 0
        ? config.banners_list
        : [{ title_es: 'Expertos inmobiliarios a tu servicio', subtitle_es: 'Asesoramiento profesional y personalizado en cada etapa del proceso', img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1600' }]

    useEffect(() => {
        if (banners.length <= 1) return
        const t = setInterval(() => setHeroIdx(i => (i + 1) % banners.length), 6000)
        return () => clearInterval(t)
    }, [banners.length])

    const banner = banners[heroIdx]
    const title = lang === 'ca' ? (banner.title_ca || banner.title_es) : banner.title_es
    const subtitle = lang === 'ca' ? (banner.subtitle_ca || banner.subtitle_es) : banner.subtitle_es

    const featuredProps = (featTab === 'todos'
        ? properties
        : properties.filter((p: any) => p.operation === featTab)
    ).slice(0, 6)

    const totalActive = properties.length
    const forSale = properties.filter((p: any) => p.operation === 'venda').length
    const forRent = properties.filter((p: any) => p.operation === 'lloguer').length
    const sysVis = (config as any)?.default_blocks_visibility;

    // Define the renderer for each system block
    const renderSystemBlock = (id: string) => {
        if (id === 'hero') {
            return (
                <div style={{ height: '80vh', minHeight: 500, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AnimatePresence mode="wait">
                        <motion.div key={heroIdx} initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1 }} style={{ position: 'absolute', inset: 0, background: '#f1f5f9' }}>
                            {banner?.img && <img src={optimizeImg(banner.img, 1200, 85)} width="1200" height="800" style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={title || 'Banner principal'} fetchPriority="high" />}
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 100%)' }} />
                        </motion.div>
                    </AnimatePresence>
                    <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', color: '#fff', maxWidth: 860, padding: '0 20px' }}>
                        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
                            style={{ display: 'inline-block', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 30, padding: '6px 20px', fontSize: 13, fontWeight: 700, marginBottom: 24, letterSpacing: '0.05em' }}
                        >
                            {totalActive} {lang === 'ca' ? 'propietats disponibles' : 'propiedades disponibles'}
                        </motion.div>
                        <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                            className="public-hero-title"
                            style={{ fontSize: 'clamp(2.2rem, 5vw, 4.5rem)', fontWeight: 900, lineHeight: 1.1, marginBottom: 20, textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
                        >
                            {title}
                        </motion.h1>
                        <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                            style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', fontWeight: 400, marginBottom: 48, opacity: 0.9, lineHeight: 1.6 }}
                        >
                            {subtitle}
                        </motion.p>
                        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
                            style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}
                        >
                            <button onClick={() => setView('properties')} style={{
                                background: accent, color: '#fff', border: 'none',
                                padding: '16px 36px', borderRadius: 50, fontWeight: 800, fontSize: 15,
                                cursor: 'pointer', boxShadow: `0 8px 32px ${accent}60`
                            }}>
                                {lang === 'ca' ? 'Veure Propietats →' : 'Ver Propiedades →'}
                            </button>
                            <button onClick={() => setView('valuation')} style={{
                                background: 'rgba(255,255,255,0.15)', color: '#fff',
                                border: '1.5px solid rgba(255,255,255,0.5)',
                                padding: '16px 36px', borderRadius: 50, fontWeight: 700, fontSize: 15,
                                cursor: 'pointer', backdropFilter: 'blur(8px)'
                            }}>
                                {lang === 'ca' ? 'Valorar la meva Casa' : 'Valorar mi Casa'}
                            </button>
                        </motion.div>
                    </div>
                    {banners.length > 1 && (
                        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, zIndex: 10 }}>
                            {banners.map((_: any, i: number) => (
                                <button key={i} onClick={() => setHeroIdx(i)}
                                    aria-label={`${lang === 'ca' ? 'Anar al bàner' : 'Ir al banner'} ${i + 1}`}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        padding: '16px 8px', background: 'none', border: 'none', cursor: 'pointer'
                                    }}>
                                    <div style={{
                                        width: i === heroIdx ? 28 : 12, height: 12,
                                        borderRadius: 6,
                                        background: i === heroIdx ? '#fff' : 'rgba(255,255,255,0.4)',
                                        transition: 'all 0.3s'
                                    }} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        if (id === 'stats') {
            return (
                <div style={{ background: primary, color: '#fff', padding: '28px 40px' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 20, textAlign: 'center' }}>
                        {[
                            { val: totalActive, label: lang === 'ca' ? 'Propietats Actives' : 'Propiedades Activas', icon: '🏠' },
                            { val: forSale, label: lang === 'ca' ? 'En Venda' : 'En Venta', icon: '🏷️' },
                            { val: forRent, label: lang === 'ca' ? 'De Lloguer' : 'En Alquiler', icon: '🔑' },
                            { val: '15+', label: lang === 'ca' ? 'Anys d\'Experiència' : 'Años de Experiencia', icon: '⭐' },
                        ].map((s, i) => (
                            <div key={i}>
                                <div style={{ fontSize: 28, marginBottom: 4 }}>{s.icon}</div>
                                <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: '-0.5px' }}>{s.val}</div>
                                <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        if (id === 'services') {
            return (
                <section style={{ background: '#f8fafc', padding: '80px 40px' }} aria-labelledby="services-title">
                    <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
                        <h2 id="services-title" style={{ fontSize: 32, fontWeight: 900, color: primary, marginBottom: 12 }}>
                            {lang === 'ca' ? 'La Teva Immobiliària de Confiança' : 'Tu Inmobiliaria de Confianza'}
                        </h2>
                        <p style={{ color: '#475569', fontSize: 15, marginBottom: 60, maxWidth: 600, margin: '0 auto 60px' }}>
                            {lang === 'ca' ? 'Combinem experiència local amb un enfocament modern i personalitzat.' : 'Combinamos experiencia local con un enfoque moderno y personalizado.'}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24 }}>
                            {[
                                { icon: <Home size={24} color="#fff" />, title: lang === 'ca' ? 'Compra i Venda' : 'Compra y Venta', desc: lang === 'ca' ? 'T\'ajudem a trobar la propietat perfecta o vendre el teu immoble al mejor preu del mercat.' : 'Te ayudamos a encontrar la propiedad perfecta o vender tu inmueble al mejor precio del mercado.' },
                                { icon: <Key size={24} color="#fff" />, title: lang === 'ca' ? 'Lloguer' : 'Alquiler', desc: lang === 'ca' ? 'Gestió integral de lloguers garantint la teva màxima seguretat i rendibilitat.' : 'Gestión integral de alquileres garantizando tu máxima seguridad y rentabilidad.' },
                                { icon: <TrendingUp size={24} color="#fff" />, title: lang === 'ca' ? 'Valoracions' : 'Valoraciones', desc: lang === 'ca' ? 'Valorem la teva propietat amb un sistema professional per oferir-te el millor preu.' : 'Valoramos tu propiedad con un sistema profesional para ofrecerte el mejor precio.' },
                                { icon: <FileText size={24} color="#fff" />, title: lang === 'ca' ? 'Assessorament Legal' : 'Asesoramiento Legal', desc: lang === 'ca' ? 'Acompanyament expert en tots els tràmits legals, fiscals i administratius.' : 'Acompañamiento experto en todos los trámites legales, fiscales y administrativos.' },
                            ].map((s, i) => (
                                <article key={i} className="icon-card" style={{ padding: '20px' }}>
                                    <div className="icon-circle" style={{ background: accent, marginBottom: '15px' }} aria-hidden="true">{s.icon}</div>
                                    <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '10px' }}>{s.title}</h3>
                                    <p style={{ fontSize: '14px', color: '#475569' }}>{s.desc}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            );
        }
        if (id === 'properties') {
            return (
                <div style={{ padding: '80px 40px', background: '#fff' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, flexWrap: 'wrap', gap: 16 }}>
                            <div>
                                <h2 style={{ fontSize: 32, fontWeight: 900, color: primary, marginBottom: 4 }}>{lang === 'ca' ? 'Propietats Destacades' : 'Propiedades Destacadas'}</h2>
                                <p style={{ color: '#475569', fontSize: 14 }}>{lang === 'ca' ? 'Descobreix les nostres millors propietats disponibles' : 'Descubre nuestras mejores propiedades disponibles'}</p>
                            </div>
                            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 12, padding: 4, gap: 4 }}>
                                {(['todos', 'venda', 'lloguer'] as const).map(tab => (
                                    <button key={tab} onClick={() => setFeatTab(tab)} style={{
                                        background: featTab === tab ? '#fff' : 'transparent',
                                        padding: '10px 24px', borderRadius: 10, fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer', transition: '0.2s',
                                        color: featTab === tab ? primary : '#4b5563',
                                        boxShadow: featTab === tab ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                    }}>
                                        {tab === 'todos' ? (lang === 'ca' ? 'Totes' : 'Todas') : (lang === 'ca' ? OP_LABEL_CA[tab] : OP_LABEL[tab])}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {featuredProps.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#475569' }}>{lang === 'ca' ? 'No hi ha propietats en aquesta categoria.' : 'No hay propiedades en esta categoría.'}</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24, marginBottom: 40 }}>
                                {featuredProps.map((p: any) => (
                                    <PropertyCard key={p.id} prop={p} onClick={() => setDetails(p)} onShowMap={() => onShowMap(p)}
                                        onDossier={onDossier}
                                        primary={primary} accent={accent}
                                        opColor={OP_COLOR[p.operation] || accent} lang={lang}
                                        opLabel={lang === 'ca' ? (OP_LABEL_CA[p.operation] || (p.operation || '').toUpperCase()) : (OP_LABEL[p.operation] || (p.operation || '').toUpperCase())}
                                        STATUS_BADGE={STATUS_BADGE}
                                        config={config}
                                    />
                                ))}
                            </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                            <button onClick={() => setView('properties')} style={{
                                background: primary, color: '#fff', border: 'none',
                                padding: '14px 36px', borderRadius: 50, fontWeight: 800, fontSize: 14,
                                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8
                            }}>
                                {lang === 'ca' ? 'Veure Totes les Propietats →' : 'Ver Todas las Propiedades →'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        if (id === 'sell_cta') {
            return (
                <div style={{ background: `linear-gradient(135deg, ${primary} 0%, #1d4ed8 100%)`, color: '#fff', padding: '80px 40px', textAlign: 'center' }}>
                    <div style={{ maxWidth: 900, margin: '0 auto' }}>
                        <h2 style={{ fontSize: 36, fontWeight: 900, marginBottom: 16 }}>{lang === 'ca' ? 'Vols Vendre o Llogar el teu Immoble?' : '¿Quieres Vender o Alquilar tu Inmueble?'}</h2>
                        <p style={{ fontSize: 16, opacity: 0.85, marginBottom: 48, lineHeight: 1.6 }}>{lang === 'ca' ? "El nostre equip expert s'encarregarà de tot el procés perquè no t'hagis de preocupar de res." : 'Nuestro equipo experto se encargará de todo el proceso para que no te preocupes de nada.'}</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginBottom: 48 }}>
                            {[
                                { icon: <TrendingUp size={22} color="rgba(255,255,255,0.7)" />, title: lang === 'ca' ? 'Valoració Exacta' : 'Valoración Exacta', desc: lang === 'ca' ? 'Anàlisi del mercat per aconseguir el millor preu.' : 'Análisis del mercado para conseguir el mejor precio.' },
                                { icon: <ShieldCheck size={22} color="rgba(255,255,255,0.7)" />, title: lang === 'ca' ? 'Gestió Segura' : 'Gestión Segura', desc: lang === 'ca' ? 'Acompanyament legal amb total transparència.' : 'Acompañamiento legal con total transparencia.' },
                                { icon: <Eye size={22} color="rgba(255,255,255,0.7)" />, title: lang === 'ca' ? 'Visibilitat Total' : 'Visibilidad Total', desc: lang === 'ca' ? 'Destaquem la teva propietat en els principals portals.' : 'Destacamos tu propiedad en los principales portales.' },
                            ].map((s, i) => (
                                <div key={i} style={{ background: 'rgba(255,255,255,0.08)', padding: '24px 20px', borderRadius: 16, textAlign: 'left', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <div style={{ marginBottom: 10 }}>{s.icon}</div>
                                    <h3 style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>{s.title}</h3>
                                    <p style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{s.desc}</p>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setView('valuation')} style={{
                            background: '#fff', color: primary, border: 'none',
                            padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16,
                            cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                        }}>
                            {lang === 'ca' ? 'Començar Valoració →' : 'Comenzar Valoración →'}
                        </button>
                    </div>
                </div>
            );
        }
        if (id === 'blog') {
            if (config?.show_blog === false) return null;
            const latestPosts = posts.filter(p => p.is_published).slice(0, 3);
            
            if (latestPosts.length === 0) {
                return (
                    <div style={{ padding: '80px 40px', background: '#f8fafc', textAlign: 'center' }}>
                        <div style={{ maxWidth: 800, margin: '0 auto' }}>
                           <h2 style={{ fontSize: 32, fontWeight: 900, color: primary, marginBottom: 12 }}>{lang === 'ca' ? 'Pròximament Blog' : 'Próximamente Blog'}</h2>
                           <p style={{ color: '#475569', fontSize: 16 }}>{lang === 'ca' ? 'Estem preparant els millors continguts per a tu.' : 'Estamos preparando los mejores contenidos para ti.'}</p>
                        </div>
                    </div>
                );
            }

            return (
                <div style={{ padding: '80px 40px', background: '#f8fafc' }}>
                    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, flexWrap: 'wrap', gap: 16 }}>
                            <div>
                                <h2 style={{ fontSize: 32, fontWeight: 900, color: primary, marginBottom: 4 }}>{lang === 'ca' ? 'Blog i Notícies' : 'Blog y Noticias'}</h2>
                                <p style={{ color: '#475569', fontSize: 14 }}>{lang === 'ca' ? 'Descobreix les últimes novetats del sector immobiliari' : 'Descubre las últimas novedades del sector inmobiliario'}</p>
                            </div>
                            <button onClick={() => setView('blog')} style={{
                                background: 'transparent', color: primary, border: `1.5px solid ${primary}`,
                                padding: '10px 24px', borderRadius: 50, fontWeight: 800, fontSize: 13, cursor: 'pointer'
                            }}>
                                {lang === 'ca' ? 'Veure tot el Blog →' : 'Ver todo el Blog →'}
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
                            {latestPosts.map((post: any) => (
                                <motion.article key={post.id}
                                    whileHover={{ y: -8 }}
                                    onClick={() => { setSelectedPost(post); setView('blog'); }}
                                    style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', cursor: 'pointer', border: '1px solid #f1f5f9' }}>
                                    <div style={{ height: 200, position: 'relative' }}>
                                        <img src={optimizeImg(post.cover_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=800', 600, 75)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                                    </div>
                                    <div style={{ padding: 24 }}>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                            {(post.tags || []).slice(0, 2).map((tag: string, i: number) => (
                                                <span key={i} style={{ background: `${accent}10`, color: accent, padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{tag}</span>
                                            ))}
                                        </div>
                                        <h3 style={{ fontSize: 18, fontWeight: 800, color: primary, marginBottom: 10, lineHeight: 1.4 }}>{lang === 'ca' ? (post.title_ca || post.title_es) : (post.title_es || post.title_ca)}</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 12, fontWeight: 600 }}>
                                            <CalendarDays size={14} />
                                            {new Date(post.published_at || post.created_at).toLocaleDateString(lang === 'ca' ? 'ca-ES' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }
        if (id === 'contact_bar') {
            if (!config.contact_phone && !config.contact_email) return null;
            return (
                <div style={{ background: '#f8fafc', padding: '40px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
                        <div>
                            <h3 style={{ fontSize: 20, fontWeight: 900, color: primary, marginBottom: 4 }}>{lang === 'ca' ? 'Tens alguna pregunta?' : '¿Tienes alguna pregunta?'}</h3>
                            <div style={{ color: '#475569', fontSize: 14 }}>{lang === 'ca' ? 'Estem disponibles per ajudar-te de dilluns a divendres.' : 'Estamos disponibles para ayudarte de lunes a viernes.'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {config.contact_phone && (
                                <a href={`tel:${config.contact_phone.replace(/\D/g, '')}`} style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: primary, color: '#fff', textDecoration: 'none',
                                    padding: '12px 24px', borderRadius: 10, fontWeight: 800, fontSize: 14
                                }}>
                                    <Phone size={16} /> {config.contact_phone}
                                </a>
                            )}
                            {config.contact_phone && (
                                <a href={`https://wa.me/${(config.contact_whatsapp || config.contact_phone || '').replace(/\D/g, '')}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ 
                                        display: 'flex', alignItems: 'center', gap: 8, 
                                        background: '#075e54', // Dark WhatsApp green (accessible with white)
                                        color: '#ffffff', 
                                        textDecoration: 'none', 
                                        padding: '12px 24px', 
                                        borderRadius: 10, 
                                        fontWeight: 800, 
                                        fontSize: 14 
                                    }}>
                                    <WhatsAppIcon size={16} /> WhatsApp
                                </a>
                            )}
                            {config.contact_email && (
                                <a href={`mailto:${config.contact_email}`} style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: '#fff', color: primary, textDecoration: 'none',
                                    padding: '12px 24px', borderRadius: 10, fontWeight: 800, fontSize: 14,
                                    border: `1.5px solid ${primary}`
                                }}>
                                    <Mail size={16} /> {lang === 'ca' ? 'Contactar' : 'Contactar'}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    const defaultBlocksItems = [
        { id: 'hero', active: true, is_system: true },
        { id: 'stats', active: true, is_system: true },
        { id: 'services', active: true, is_system: true },
        { id: 'properties', active: true, is_system: true },
        { id: 'blog', active: true, is_system: true },
        { id: 'sell_cta', active: true, is_system: true },
        { id: 'contact_bar', active: true, is_system: true },
    ];

    const blocksToRender = pageBlocks || [];

    return (
        <div style={{ background: '#fff' }}>
            {blocksToRender.map((b: any) => {
                const isActive = b.active ?? b.is_active ?? true;
                if (!isActive) return null;

                const isSystem = b.is_system || ['hero', 'stats', 'services', 'properties', 'blog', 'sell_cta', 'contact_bar'].includes(b.id);

                if (isSystem) {
                    return <div key={b.id}>{renderSystemBlock(b.id)}</div>;
                } else {
                    const blockData = (pageBlocks || []).find((pb: any) => pb.id === b.id);
                    if (blockData && blockData.is_active) {
                        return (
                            <DynamicBlockRenderer
                                key={blockData.id}
                                block={blockData}
                                lang={lang}
                                primary={primary}
                                accent={accent}
                                setView={setView}
                                properties={properties}
                                setDetails={setDetails}
                                onShowMap={onShowMap}
                                onDossier={onDossier}
                                config={config}
                                onLightbox={onLightbox}
                            />
                        );
                    }
                }
                return null;
            })}

            <style>{`.icon-card { background: #fff; padding: 40px 30px; border-radius: 16px; text-align: center; box-shadow: 0 4px 20px rgba(0,0,0,0.04); border: 1px solid #f1f5f9; transition: transform 0.2s, box-shadow 0.2s; } .icon-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.08); } .icon-card h3 { font-size: 16px; font-weight: 800; color: #1e3a8a; margin-bottom: 12px; margin-top: 0; } .icon-card p { font-size: 13px; color: #64748b; line-height: 1.6; margin: 0; } .icon-circle { width: 52px; height: 52px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }`}</style>
        </div>
    )
}

function CountdownComponent({ block, data, style, primary }: any) {
    const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });

    useEffect(() => {
        const targetDate = data.url ? new Date(data.url).getTime() : 0;
        if (!targetDate) return;

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const difference = targetDate - now;

            if (difference > 0) {
                setTimeLeft({
                    d: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    h: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    m: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                    s: Math.floor((difference % (1000 * 60)) / 1000)
                });
            } else {
                clearInterval(timer);
                setTimeLeft({ d: 0, h: 0, m: 0, s: 0 });
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [data.url]);

    return (
        <div style={{ ...style, padding: '80px 20px', textAlign: 'center' }}>
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
                {(data.title || data.subtitle) && (
                    <div style={{ marginBottom: 40 }}>
                        {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: style.color || primary }}>{data.title}</h2>}
                        {data.subtitle && <p style={{ fontSize: 16, opacity: 0.8, marginTop: 12, color: style.color }}>{data.subtitle}</p>}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(10px, 4vw, 20px)', flexWrap: 'wrap' }}>
                    {[
                        { label: 'DIES', val: timeLeft.d },
                        { label: 'HORES', val: timeLeft.h },
                        { label: 'MINUTS', val: timeLeft.m },
                        { label: 'SEGONS', val: timeLeft.s }
                    ].map((unit, i) => (
                        <div key={i} style={{ background: 'rgba(255,255,255,0.1)', padding: '20px', borderRadius: 24, minWidth: 100, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)', flex: '1 1 100px' }}>
                            <div style={{ fontSize: 40, fontWeight: 900, color: style.color || '#fff' }}>{String(unit.val).padStart(2, '0')}</div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: style.color ? `${style.color}cc` : 'rgba(255,255,255,0.6)', marginTop: 4 }}>{unit.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   LEAD FORM COMPONENT — Specialized form for leads
═══════════════════════════════════════════════════════════ */
function LeadFormBlock({ title, subtitle, primary, accent, lang, agencyId }: any) {
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            // ¡Atención! La columna correcta en `leads` es `name`, NO `full_name`.
            // Con full_name el INSERT fallaba silenciosamente y NO se disparaba
            // el trigger de notificación por email a la agencia.
            const { error } = await (supabase as any).from('leads').insert({
                agency_id: agencyId,
                name: form.name,
                email: form.email,
                phone: form.phone,
                message: form.message,
                source: 'web_builder_lead_form',
                status: 'new'
            });
            if (error) throw error;
            setSent(true);
        } catch (err) {
            console.error(err);
            alert(lang === 'ca' ? 'Error al enviar el formulari.' : 'Error al enviar el formulario.');
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: 'center', padding: '60px 40px', background: '#fff', borderRadius: 32, border: '2px solid #f1f5f9' }}>
                <div style={{ background: '#2563eb', color: '#10b981', width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}><CheckCircle2 size={40} /></div>
                <h3 style={{ fontSize: 28, fontWeight: 900, color: primary, marginBottom: 12 }}>{lang === 'ca' ? 'Enviat correctament!' : '¡Enviado correctamente!'}</h3>
                <p style={{ color: '#475569', fontSize: 16 }}>{lang === 'ca' ? 'Ens posarem en contacte amb vostè el més aviat possible.' : 'Nos pondremos en contacto con usted lo antes posible.'}</p>
            </motion.div>
        );
    }

    const inp = { width: '100%', padding: '16px 20px', borderRadius: 16, border: '1px solid #e2e8f0', fontSize: 15, background: '#f8fafc', outline: 'none', transition: 'all 0.2s' };
    const lbl = { display: 'block', fontSize: 13, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase' as const };

    return (
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ background: '#fff', padding: '60px', borderRadius: 40, boxShadow: '0 30px 60px rgba(0,0,0,0.1)', border: '1px solid #f1f5f9' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <div style={{ display: 'inline-flex', padding: '8px 16px', background: `${accent}12`, borderRadius: 12, color: accent, fontWeight: 800, fontSize: 12, marginBottom: 16, textTransform: 'uppercase' }}>{lang === 'ca' ? 'Lead Qualificat' : 'Lead Cualificado'}</div>
                <h2 style={{ fontSize: 36, fontWeight: 900, color: primary, margin: '0 0 12px' }}>{title || (lang === 'ca' ? 'Demana més informació' : 'Pide más información')}</h2>
                <p style={{ color: '#475569', fontSize: 18 }}>{subtitle || (lang === 'ca' ? 'Estem aquí per ajudar-te a trobar la llar dels teus somnis.' : 'Estamos aquí para ayudarte a encontrar el hogar de tus sueños.')}</p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
                <div style={{ gridColumn: 'span 1' }}>
                    <label style={lbl}>{lang === 'ca' ? 'Nom Complet' : 'Nombre Completo'}</label>
                    <input required style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Marc Ros" />
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                    <label style={lbl}>{lang === 'ca' ? 'Correu Electrònic' : 'Correo Electrónico'}</label>
                    <input required type="email" style={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Ex: marc@exemple.com" />
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                    <label style={lbl}>{lang === 'ca' ? 'Telèfon' : 'Teléfono'}</label>
                    <input required type="tel" style={inp} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Ex: 600 000 000" />
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                   <label style={lbl}>{lang === 'ca' ? 'Motiu d\'Interès' : 'Motivo de Interés'}</label>
                   <select required style={inp} onChange={e => setForm({ ...form, message: `Interès en: ${e.target.value}` })}>
                        <option value="">{lang === 'ca' ? 'Selecciona...' : 'Selecciona...'}</option>
                        <option value="Comprar">{lang === 'ca' ? 'Vull Comprar' : 'Quiero Comprar'}</option>
                        <option value="Vendre">{lang === 'ca' ? 'Vull Vendre' : 'Quiero Vender'}</option>
                        <option value="Invertir">{lang === 'ca' ? 'Inversió' : 'Inversión'}</option>
                        <option value="Altres">{lang === 'ca' ? 'Altres consultes' : 'Otras consultas'}</option>
                   </select>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={lbl}>{lang === 'ca' ? 'Missatge (Opcional)' : 'Mensaje (Opcional)'}</label>
                    <textarea style={{ ...inp, height: 120, resize: 'none' }} value={form.message.replace('Interès en: ', '')} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={lang === 'ca' ? 'Com el podem ajudar?' : '¿Cómo podemos ayudarte?'} />
                </div>
                <div style={{ gridColumn: 'span 2', marginTop: 12 }}>
                    <button 
                        disabled={loading}
                        style={{ width: '100%', padding: '18px', borderRadius: 16, background: accent, color: '#fff', border: 'none', fontWeight: 900, fontSize: 17, cursor: 'pointer', boxShadow: `0 10px 25px ${accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
                    >
                        {loading ? <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /> : (lang === 'ca' ? 'Enviar sol·licitud' : 'Enviar solicitud')}
                    </button>
                    <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#475569' }}>
                       {lang === 'ca' ? 'En enviar acceptes la nostra política de privadesa.' : 'Al enviar aceptas nuestra política de privacidad.'}
                    </p>
                </div>
            </form>
        </motion.div>
    );
}

export function DynamicBlockRenderer({ block, lang, primary, accent, setView, properties, setDetails, onShowMap, onDossier, config, onLightbox }: any) {
    if (!block.is_active) return null;
    const data = (lang === 'ca' ? block.content_ca : block.content_es) || {};
    const settings = block.settings || {};

    const renderIcon = (name: string, color: string) => {
        const icons: any = { Home, Key, TrendingUp, FileText, ShieldCheck, Eye, Phone, MessageCircle, Mail };
        const IconComponent = icons[name] || Home;
        return <IconComponent size={24} color={color} />;
    };

    const getBgStyle = (typeDefault: string) => {
        const bg = settings.bg || typeDefault;
        if (bg === 'white') return { background: '#ffffff', color: '#1e293b' };
        if (bg === 'offwhite') return { background: '#f8fafc', color: '#1e293b' };
        if (bg === 'primary') return { background: primary, color: '#ffffff' };
        if (bg === 'accent') return { background: accent, color: '#ffffff' };
        if (bg === 'dark') return { background: '#0f172a', color: '#ffffff' };
        if (bg === 'gradient') return { background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`, color: '#ffffff' };
        return { background: '#ffffff', color: '#1e293b' };
    };

    if (block.type === 'features_grid') {
        const style = getBgStyle('offwhite');
        const isDark = style.color === '#ffffff';
        return (
            <section style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
                    {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 12 }}>{data.title}</h2>}
                    {data.subtitle && <p style={{ color: isDark ? 'rgba(255,255,255,0.8)' : '#475569', fontSize: 15, marginBottom: 60, maxWidth: 600, margin: '0 auto 60px' }}>{data.subtitle}</p>}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                        {(data.cards || []).map((card: any, i: number) => (
                            <article key={i} className="icon-card" style={{ padding: '30px', background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f1f5f9' }}>
                                <div className="icon-circle" style={{ background: isDark ? 'rgba(255,255,255,0.1)' : accent, marginBottom: '20px' }} aria-hidden="true">
                                    {renderIcon(card.icon, isDark ? '#fff' : '#fff')}
                                </div>
                                <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '12px', color: isDark ? '#fff' : primary }}>{card.title}</h3>
                                <p style={{ fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.7)' : '#475569' }}>{card.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (block.type === 'hero_cta') {
        const style = getBgStyle('gradient');
        const isDark = style.color === '#ffffff';
        return (
            <div style={{ ...style, padding: '100px 40px', textAlign: 'center' }}>
                <div style={{ maxWidth: 900, margin: '0 auto' }}>
                    {data.title && <h2 style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', fontWeight: 900, marginBottom: 20, lineHeight: 1.1 }}>{data.title}</h2>}
                    {data.subtitle && <p style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', opacity: 0.9, marginBottom: 48, lineHeight: 1.6, maxWidth: 700, margin: '0 auto 48px' }}>{data.subtitle}</p>}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                        {data.buttonText && (
                            <button onClick={() => setView('valuation')} style={{
                                background: '#fff', color: primary, border: 'none',
                                padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16,
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                            }}>
                                {data.buttonText}
                            </button>
                        )}
                        {data.buttonText2 && (
                            <button onClick={() => setView('properties')} style={{
                                background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                                padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16,
                                cursor: 'pointer', backdropFilter: 'blur(10px)'
                            }}>
                                {data.buttonText2}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'reviews') {
        const style = getBgStyle('offwhite');
        const isDark = style.color === '#ffffff';
        return (
            <section style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
                    {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 16 }}>{data.title}</h2>}
                    {data.subtitle && <p style={{ color: isDark ? 'rgba(255,255,255,0.8)' : '#475569', fontSize: 15, marginBottom: 60, maxWidth: 600, margin: '0 auto 60px' }}>{data.subtitle}</p>}
                    <div className="reviews-scroll-container" style={{ display: 'flex', overflowX: 'auto', gap: 24, padding: '10px 0 30px', scrollSnapType: 'x mandatory' }}>
                        {(data.cards || []).map((rev: any, i: number) => (
                            <article key={i} style={{ flex: '0 0 320px', background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', padding: '30px', borderRadius: 24, textAlign: 'left', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', scrollSnapAlign: 'start' }}>
                                <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
                                    {[1, 2, 3, 4, 5].map(s => <Star key={s} size={16} fill={s <= (rev.rating || 5) ? '#facc15' : 'transparent'} color={s <= (rev.rating || 5) ? '#facc15' : '#e2e8f0'} />)}
                                </div>
                                <p style={{ fontSize: '15px', color: isDark ? 'rgba(255,255,255,0.9)' : '#475569', fontStyle: 'italic', marginBottom: 24, lineHeight: 1.6 }}>"{rev.text}"</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: accent }}>{rev.author?.[0]}</div>
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: isDark ? '#fff' : primary }}>{rev.author}</div>
                                        <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.6)' : '#475569', fontWeight: 800 }}>VERIFIED</div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                    {data.url && (
                        <div style={{ textAlign: 'center', marginTop: 32 }}>
                            <a href={data.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', padding: '12px 24px', borderRadius: 50, fontWeight: 800, textDecoration: 'none', fontSize: 14 }}>
                                <Globe size={18} color="#4285F4" /> {lang === 'ca' ? 'Veure a Google Maps' : 'Ver en Google Maps'}
                            </a>
                        </div>
                    )}
                </div>
            </section>
        );
    }

    if (block.type === 'team') {
        const style = getBgStyle(settings.bg || 'white');
        const isDark = style.color === '#ffffff';
        const layout = settings.layout || 'grid';
        const shape = settings.cardShape || 'rounded';
        const scale = (settings.cardScale || 100) / 100;
        const animType = settings.animation || 'fade';
        
        const filteredCards = (data.cards || []).filter((m: any) => 
            m.email !== 'carlespontsalvado@gmail.com' && 
            m.email !== 'carlespont@pontsoft.cat'
        );

        const getAnim = (i: number) => {
            if (animType === 'none') return {};
            if (animType === 'slideUp') return { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { delay: i * 0.1, duration: 0.6 } };
            if (animType === 'zoom') return { initial: { opacity: 0, scale: 0.8 }, whileInView: { opacity: 1, scale: 1 }, viewport: { once: true }, transition: { delay: i * 0.1, duration: 0.5 } };
            return { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true }, transition: { delay: i * 0.1, duration: 0.8 } }; // fade default
        };

        const imgRadius = shape === 'circle' ? '50%' : shape === 'square' ? '0' : '32px';

        return (
            <section style={{ ...style, padding: '100px 20px', overflow: 'hidden' }}>
                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 60 }}>
                        <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 20 }}>{data.title || 'Nuestro Equipo'}</h2>
                        {data.subtitle && <p style={{ fontSize: 18, opacity: 0.7, maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>{data.subtitle}</p>}
                    </div>

                    {layout === 'list' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 1000 * scale, margin: '0 auto' }}>
                            {filteredCards.map((m: any, i: number) => (
                                <motion.div key={i} {...getAnim(i)} style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderRadius: 24, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', transform: `scale(${scale})` }}>
                                    <div style={{ width: 220, height: 220, flexShrink: 0, padding: shape === 'circle' ? 12 : 0 }}>
                                        <img src={m.img || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: imgRadius }} alt={m.name} />
                                    </div>
                                    <div style={{ padding: 32, flex: 1, minWidth: 280 }}>
                                        <div style={{ fontSize: 24, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 4 }}>{m.name}</div>
                                        <div style={{ fontSize: 14, color: accent, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 20 }}>{m.role}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                                            {m.email && (
                                                <a href={`mailto:${m.email}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: isDark ? '#cbd5e1' : '#475569', fontSize: 14, textDecoration: 'none', fontWeight: 600 }}>
                                                    <Mail size={16} color={accent} /> {m.email}
                                                </a>
                                            )}
                                            {m.phone && (
                                                <a href={`tel:${m.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: isDark ? '#cbd5e1' : '#475569', fontSize: 14, textDecoration: 'none', fontWeight: 600 }}>
                                                    <Phone size={16} color={accent} /> {m.phone}
                                                </a>
                                            )}
                                            {m.linkedin && (
                                                <a href={m.linkedin} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0077b5', fontSize: 14, textDecoration: 'none', fontWeight: 600 }} target="_blank" rel="noreferrer">
                                                    <Linkedin size={16} /> LinkedIn
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: layout === 'small' ? `repeat(auto-fit, minmax(${200 * scale}px, 1fr))` : `repeat(auto-fit, minmax(${320 * scale}px, 1fr))`, 
                            gap: (layout === 'small' ? 24 : 40) * scale,
                            maxWidth: 1400,
                            margin: '0 auto'
                        }}>
                            {filteredCards.map((m: any, i: number) => (
                                <motion.div key={i} {...getAnim(i)} style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', borderRadius: shape === 'circle' ? '40px' : 32, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', position: 'relative' }}>
                                    <div style={{ height: (layout === 'small' ? 220 : 380) * scale, overflow: 'hidden', position: 'relative', padding: shape === 'circle' ? 20 : 0 }}>
                                        <img src={m.img || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: imgRadius }} alt={m.name} />
                                    </div>
                                    <div style={{ 
                                        padding: (layout === 'small' ? 20 : 32) * scale, 
                                        textAlign: 'center', 
                                        position: 'relative', 
                                        marginTop: -40, 
                                        background: isDark ? '#1e293b' : '#fff', 
                                        margin: layout === 'small' ? '0 10px 10px' : '0 20px 20px', 
                                        borderRadius: 24, 
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                    }}>
                                        <div style={{ fontSize: (layout === 'small' ? 16 : 22) * scale, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 4 }}>{m.name}</div>
                                        <div style={{ fontSize: (layout === 'small' ? 11 : 14) * scale, color: accent, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>{m.role}</div>
                                        
                                        <div style={{ marginTop: layout === 'small' ? 12 : 24, display: 'flex', flexDirection: 'column', gap: layout === 'small' ? 6 : 12, alignItems: 'center' }}>
                                            {(m.email || m.phone || m.linkedin) && (
                                                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', borderTop: '1px solid #f1f5f9', paddingTop: 16, width: '100%' }}>
                                                    {m.email && <a href={`mailto:${m.email}`} title={m.email} style={{ color: isDark ? '#cbd5e1' : '#475569' }}><Mail size={layout === 'small' ? 14 : 18} /></a>}
                                                    {m.phone && <a href={`tel:${m.phone}`} title={m.phone} style={{ color: isDark ? '#cbd5e1' : '#475569' }}><Phone size={layout === 'small' ? 14 : 18} /></a>}
                                                    {m.linkedin && <a href={m.linkedin} style={{ color: '#0077b5' }} target="_blank" rel="noreferrer"><Linkedin size={layout === 'small' ? 14 : 18} /></a>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        );
    }

    if (block.type === 'contact_banner') {
        const style = getBgStyle('gradient');
        const isDark = style.color === '#ffffff';
        return (
            <div style={{ ...style, padding: '80px 40px', textAlign: 'center' }}>
                <div style={{ maxWidth: 900, margin: '0 auto' }}>
                    <h2 style={{ fontSize: 36, fontWeight: 900, marginBottom: 16 }}>{data.title}</h2>
                    <p style={{ fontSize: 16, opacity: 0.85, marginBottom: 48, lineHeight: 1.6 }}>{data.subtitle}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginBottom: 48 }}>
                        {(data.cards || []).map((card: any, i: number) => (
                            <div key={i} style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc', padding: '24px 20px', borderRadius: 16, textAlign: 'left', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0' }}>
                                <div style={{ marginBottom: 10 }}>{renderIcon(card.icon, isDark ? 'rgba(255,255,255,0.7)' : accent)}</div>
                                <h3 style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>{card.title}</h3>
                                <p style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{card.text}</p>
                            </div>
                        ))}
                    </div>
                    {data.buttonText && (
                        <button onClick={() => setView(data.buttonLink || 'contact')} style={{
                            background: isDark ? '#fff' : primary, color: isDark ? primary : '#fff', border: 'none',
                            padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16, cursor: 'pointer'
                        }}>
                            {data.buttonText}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (block.type === 'text_image') {
        const style = getBgStyle('white');
        const isDark = style.color === '#ffffff';
        const imgSide = settings.imageSide || 'right';
        return (
            <div style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'center' }}>
                    <div style={{ order: imgSide === 'left' ? 2 : 1 }}>
                        <h2 style={{ fontSize: 32, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 16 }}>{data.title}</h2>
                        <p style={{ fontSize: 16, color: isDark ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.6, marginBottom: 32 }}>{data.subtitle}</p>
                        {data.buttonText && (
                            <button onClick={() => setView(data.buttonLink || 'properties')} style={{ background: primary, color: '#fff', border: 'none', padding: '14px 36px', borderRadius: 50, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                                {data.buttonText}
                            </button>
                        )}
                    </div>
                    <div style={{ order: imgSide === 'left' ? 1 : 2 }}>
                        <div style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
                            <img src={data.img || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'} style={{ width: '100%', display: 'block' }} alt="" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'featured_properties') {
        const style = getBgStyle('white');
        const isDark = style.color === '#ffffff';
        const featuredProps = properties ? properties.slice(0, 6) : [];
        return (
            <div style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 40 }}>
                        <h2 style={{ fontSize: 32, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 4 }}>{data.title || (lang === 'ca' ? 'Propietats Destacades' : 'Propiedades Destacadas')}</h2>
                        <p style={{ color: isDark ? 'rgba(255,255,255,0.8)' : '#475569', fontSize: 14 }}>{data.subtitle || (lang === 'ca' ? 'Descobreix les nostres millors propietats' : 'Descubre nuestras mejores propiedades')}</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24, marginBottom: 40 }}>
                        {featuredProps.map((p: any) => (
                            <PropertyCard key={p.id} prop={p} onClick={() => setDetails(p)} onShowMap={() => onShowMap(p)} onDossier={onDossier} primary={primary} accent={accent} opColor={OP_COLOR[p.operation] || accent} lang={lang} opLabel={lang === 'ca' ? (OP_LABEL_CA[p.operation] || (p.operation || '').toUpperCase()) : (OP_LABEL[p.operation] || (p.operation || '').toUpperCase())} config={config} STATUS_BADGE={STATUS_BADGE} />
                        ))}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <button onClick={() => setView('properties')} style={{ background: isDark ? '#fff' : primary, color: isDark ? primary : '#fff', border: 'none', padding: '14px 36px', borderRadius: 50, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                            {lang === 'ca' ? 'Veure Totes' : 'Ver Todas'} →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'banners_carousel') {
        return <BannersCarouselBlock data={data} settings={settings} lang={lang} />;
    }

    if (block.type === 'stats_banner') {
        const style = getBgStyle('primary');
        return (
            <div style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40 }}>
                        {(data.stats || data.cards || []).map((s: any, i: number) => (
                            <div key={i}>
                                <div style={{ fontSize: 48, fontWeight: 900, marginBottom: 8 }}>{s.value || s.text}</div>
                                <div style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', opacity: 0.8 }}>{s.label || s.title}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'features_grid') {
        const style = getBgStyle(settings.bg || 'white');
        return (
            <div style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    {(data.title || data.subtitle) && (
                        <div style={{ textAlign: 'center', marginBottom: 60 }}>
                            {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: primary, marginBottom: 15 }}>{data.title}</h2>}
                            {data.subtitle && <p style={{ fontSize: 16, color: '#475569', maxWidth: 700, margin: '0 auto' }}>{data.subtitle}</p>}
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 30 }}>
                        {(data.cards || []).map((card: any, i: number) => (
                            <div key={i} style={{ padding: 30, background: '#fff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                                <div style={{ background: accent, width: 50, height: 50, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, color: '#fff' }}>
                                    {renderIcon(card.icon, '#fff')}
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: primary, marginBottom: 10 }}>{card.title}</h3>
                                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{card.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'hero_cta') {
        const style = getBgStyle(settings.bg || 'gradient');
        return (
            <div style={{ ...style, padding: '100px 40px', textAlign: 'center' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 900, marginBottom: 20, lineHeight: 1.1 }}>{data.title}</h2>
                    <p style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', opacity: 0.9, marginBottom: 40, lineHeight: 1.6 }}>{data.subtitle}</p>
                    {data.buttonText && (
                        <button
                            onClick={() => setView(data.buttonLink === 'properties' ? 'properties' : 'valuation')}
                            style={{ background: '#fff', color: primary, border: 'none', padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                        >
                            {data.buttonText}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (block.type === 'spacer') {
        return <div style={{ height: settings.height || 80, background: settings.bg === 'transparent' ? 'transparent' : getBgStyle(settings.bg || 'white').background, width: '100%' }} />;
    }

    if (block.type === 'columns_split') {
        const style = getBgStyle(settings.bg || 'white');
        const count = settings.grid || 2;
        return (
            <div style={{ ...style, padding: '80px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 40, alignItems: 'start' }}>
                        {(data.cards || []).map((col: any, i: number) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {col.title && <h3 style={{ fontSize: 24, fontWeight: 900, color: style.color || primary }}>{col.title}</h3>}
                                {col.text && <p style={{ fontSize: 16, color: '#475569', lineHeight: 1.6 }}>{col.text}</p>}
                                {col.html && <div style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: col.html }} />}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'heading') {
        const style = getBgStyle(settings.bg || 'white');
        return (
            <div style={{ ...style, padding: '80px 40px', textAlign: 'center' }}>
                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                    <h2 style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 900, color: style.color || primary, lineHeight: 1.1, marginBottom: 24 }}>{data.title}</h2>
                    {data.subtitle && <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', opacity: 0.85, maxWidth: 1000, margin: '0 auto', lineHeight: 1.7 }}>{data.subtitle}</p>}
                </div>
            </div>
        );
    }

    if (block.type === 'rich_text' || block.type === 'custom_html' || block.type === 'html_custom' || block.type === 'raw_code') {
        const style = getBgStyle(settings.bg || 'white');
        const code = data.html || data.url || data.code || '';
        return (
            <div style={{ ...style, padding: '80px 0' }}>
                <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 40px' }}>
                    <div className="rich-text-content" dangerouslySetInnerHTML={{ __html: code }} />
                </div>
            </div>
        );
    }

    if (block.type === 'image_block' || block.type === 'video_player') {
        const style = getBgStyle(settings.bg || 'white');
        const animationType = settings.animation || 'none';
        
        const getAnimationProps = () => {
            switch(animationType) {
                case 'fade': return { initial: { opacity: 0 }, whileInView: { opacity: 1 }, transition: { duration: 1 } };
                case 'zoom': return { initial: { opacity: 0, scale: 0.8 }, whileInView: { opacity: 1, scale: 1 }, transition: { duration: 0.8 } };
                case 'slideLeft': return { initial: { opacity: 0, x: -100 }, whileInView: { opacity: 1, x: 0 }, transition: { duration: 0.8 } };
                case 'slideRight': return { initial: { opacity: 0, x: 100 }, whileInView: { opacity: 1, x: 0 }, transition: { duration: 0.8 } };
                case 'slideUp': return { initial: { opacity: 0, y: 50 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.8 } };
                case 'float': return { animate: { y: [0, -20, 0] }, transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' } };
                default: return {};
            }
        };

        return (
            <div style={{ ...style, padding: '60px 20px', textAlign: 'center' }}>
                <motion.div 
                    {...getAnimationProps()}
                    viewport={{ once: true }}
                    style={{ maxWidth: 1000, margin: '0 auto', borderRadius: 24, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                >
                    {block.type === 'image_block' && data.img && (
                        <img 
                            src={data.img} 
                            style={{ width: '100%', display: 'block' }} 
                            alt={settings.imgAlt || ""} 
                        />
                    )}
                    {block.type === 'video_player' && data.url && (
                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                           {(() => {
                                let finalUrl = data.url;
                                const isYoutube = finalUrl.includes('youtube.com') || finalUrl.includes('youtu.be');
                                const isAutoplay = settings.autoplay;
                                const isLoop = settings.loop;

                                if (isYoutube) {
                                    try {
                                        // Handle various youtube formats
                                        let videoId = '';
                                        if (finalUrl.includes('embed/')) videoId = finalUrl.split('embed/')[1].split('?')[0];
                                        else if (finalUrl.includes('v=')) videoId = finalUrl.split('v=')[1].split('&')[0];
                                        else if (finalUrl.includes('youtu.be/')) videoId = finalUrl.split('youtu.be/')[1].split('?')[0];
                                        
                                        const urlObj = new URL(`https://www.youtube.com/embed/${videoId}`);
                                        if (isAutoplay) urlObj.searchParams.set('autoplay', '1');
                                        if (isLoop) {
                                            urlObj.searchParams.set('loop', '1');
                                            urlObj.searchParams.set('playlist', videoId);
                                        }
                                        finalUrl = urlObj.toString();
                                    } catch(e) {}
                                }
                                
                                return (
                                    <iframe 
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                        src={finalUrl} 
                                        frameBorder="0" 
                                        allow={isAutoplay ? "autoplay; encrypted-media; picture-in-picture" : "encrypted-media; picture-in-picture"}
                                        allowFullScreen 
                                        title={data.title || "Video player"}
                                    ></iframe>
                                );
                           })()}
                        </div>
                    )}
                    {(!data.img && !data.url) && <div style={{ height: 300, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>{lang === 'ca' ? 'No hi ha contingut multimèdia' : 'Sin contenido multimedia'}</div>}
                </motion.div>
            </div>
        );
    }

    if (block.type === 'button_cta') {
        const style = getBgStyle(settings.bg || 'white');
        return (
            <div style={{ ...style, padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, marginBottom: 16 }}>{data.title}</h2>}
                    {data.subtitle && <p style={{ fontSize: 18, opacity: 0.8, marginBottom: 32 }}>{data.subtitle}</p>}
                    {data.buttonText && (
                        <button onClick={() => setView(data.buttonLink)} style={{
                            background: primary, color: '#fff', border: 'none', padding: '16px 40px', borderRadius: 50, fontWeight: 900, fontSize: 16, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                        }}>
                            {data.buttonText}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (block.type === 'contact_form') {
        const style = getBgStyle(settings.bg || 'offwhite');
        return (
            <div style={{ ...style, padding: '80px 20px' }}>
                <div style={{ maxWidth: 600, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                    <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8, color: primary, textAlign: 'center' }}>{data.title || (lang === 'ca' ? "Contacta'ns" : "Contáctanos")}</h2>
                    <p style={{ color: '#475569', marginBottom: 32, textAlign: 'center' }}>{data.subtitle || (lang === 'ca' ? "T'ajudem a trobar el teu lloc." : "Te ayudamos a encontrar tu lugar.")}</p>
                    <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <input placeholder={lang === 'ca' ? "Nom complet" : "Nombre completo"} style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 14 }} />
                        <input type="email" placeholder="Email" style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 14 }} />
                        <textarea placeholder={lang === 'ca' ? "Com et podem ajudar?" : "¿En qué te podemos ayudar?"} rows={4} style={{ padding: '14px 16px', borderRadius: 12, border: '1px solid #e2e8f0', resize: 'vertical', fontSize: 14 }} />
                        <button type="button" style={{ padding: 16, borderRadius: 12, border: 'none', background: accent, color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 15 }}>{data.buttonText || (lang === 'ca' ? "Enviar missatge" : "Enviar mensaje")}</button>
                    </form>
                </div>
            </div>
        );
    }

    if (block.type === 'pricing_table') {
        const style = getBgStyle(settings.bg || 'offwhite');
        return (
            <div style={{ ...style, padding: '80px 20px' }}>
                <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                    {(data.title || data.subtitle) && (
                        <div style={{ textAlign: 'center', marginBottom: 50 }}>
                            {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: style.color || primary }}>{data.title}</h2>}
                            {data.subtitle && <p style={{ fontSize: 16, opacity: 0.8, marginTop: 12 }}>{data.subtitle}</p>}
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 30 }}>
                        {(data.cards || []).map((c: any, i: number) => {
                            const isFeatured = !!c.featured;
                            return (
                                <div key={i} style={{ background: '#fff', borderRadius: 24, padding: 40, boxShadow: isFeatured ? '0 20px 40px rgba(0,0,0,0.1)' : '0 10px 30px rgba(0,0,0,0.05)', border: isFeatured ? `2px solid ${accent}` : '1px solid #e2e8f0', position: 'relative', display: 'flex', flexDirection: 'column', transform: isFeatured ? 'scale(1.05)' : 'none', zIndex: isFeatured ? 2 : 1 }}>
                                    {isFeatured && <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: accent, color: '#fff', fontSize: 12, fontWeight: 800, padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap' }}>{lang === 'ca' ? 'RECOMANAT' : 'RECOMENDADO'}</div>}
                                    <h3 style={{ fontSize: 20, fontWeight: 800, color: primary, margin: 0 }}>{c.title}</h3>
                                    <div style={{ fontSize: 40, fontWeight: 900, color: primary, margin: '16px 0 24px' }}>{c.text}</div>
                                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                                        {(c.list || []).map((li: string, j: number) => <li key={j} style={{ color: '#475569', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ color: accent }}>✓</span> {li}</li>)}
                                    </ul>
                                    <button style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: isFeatured ? accent : '#f1f5f9', color: isFeatured ? '#fff' : primary, fontWeight: 800, cursor: 'pointer' }}>{c.button || (lang === 'ca' ? 'Escollir' : 'Elegir')}</button>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'faq_accordion') {
        const style = getBgStyle(settings.bg || 'white');
        return (
            <div style={{ ...style, padding: '80px 20px' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    {(data.title || data.subtitle) && (
                        <div style={{ textAlign: 'center', marginBottom: 50 }}>
                            {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: style.color || primary }}>{data.title}</h2>}
                            {data.subtitle && <p style={{ fontSize: 16, opacity: 0.8, marginTop: 12 }}>{data.subtitle}</p>}
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {(data.cards || []).map((c: any, i: number) => (
                            <details key={i} style={{ background: '#fff', padding: '20px 24px', borderRadius: 16, border: '1px solid #e2e8f0', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                <summary style={{ fontSize: 16, fontWeight: 800, color: primary, outline: 'none' }}>{c.title}</summary>
                                <p style={{ color: '#475569', marginTop: 12, lineHeight: 1.6, fontSize: 14 }}>{c.text}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    if (block.type === 'company_history') {
        const cards = data.cards || [];
        const style = getBgStyle(settings.bg || 'white');
        const isDark = style.color === '#ffffff';
        const isHorizontal = settings.layout === 'horizontal';
        
        return (
            <section className="premium-history-section" style={{ 
                ...style,
                padding: '80px 0', 
                position: 'relative',
                overflow: 'hidden',
                backgroundImage: data.img ? `linear-gradient(rgba(${isDark ? '15, 23, 42, 0.85' : '255, 255, 255, 0.85'}), rgba(${isDark ? '15, 23, 42, 0.85' : '255, 255, 255, 0.85'})), url(${data.img})` : style.backgroundImage,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}>
                {/* Decorative background elements */}
                {!data.img && (
                    <>
                        <div style={{ position: 'absolute', top: '-10%', left: '-10%', width: '40%', height: '40%', background: `radial-gradient(circle, ${accent}10 0%, transparent 70%)`, zIndex: 0 }} />
                        <div style={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '40%', height: '40%', background: `radial-gradient(circle, ${primary}10 0%, transparent 70%)`, zIndex: 0 }} />
                    </>
                )}

                <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1, padding: '0 20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: isHorizontal ? 40 : 50 }}>
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, color: isDark ? '#fff' : primary, lineHeight: 1.1, marginBottom: 20 }}
                        >
                            {data.title || (lang === 'ca' ? 'La nostra Història' : 'Nuestra Historia')}
                        </motion.h2>
                        {data.subtitle && (
                            <motion.p 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                style={{ fontSize: 18, color: isDark ? '#cbd5e1' : '#475569', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}
                            >
                                {data.subtitle}
                            </motion.p>
                        )}
                    </div>

                    {!isHorizontal ? (
                        <div className="history-timeline-vertical" style={{ position: 'relative', padding: '10px 0', maxWidth: 1000, margin: '0 auto' }}>
                            {/* Center Path */}
                            <div className="history-timeline-path" style={{ 
                                position: 'absolute', top: 0, bottom: 0, 
                                left: '50%', transform: 'translateX(-50%)', 
                                width: 2, background: `linear-gradient(to bottom, transparent, ${accent}40 10%, ${accent}40 90%, transparent)` 
                            }} />

                            {cards.map((card: any, i: number) => {
                                const isEven = i % 2 === 0;
                                return (
                                    <motion.div 
                                        key={i}
                                        initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true, margin: '-10% 0px' }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                        style={{ 
                                            display: 'flex', 
                                            justifyContent: isEven ? 'flex-end' : 'flex-start', 
                                            padding: '25px 0', 
                                            width: '100%',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{ 
                                            position: 'absolute', left: '50%', top: '50%', 
                                            transform: 'translate(-50%, -50%)', 
                                            width: 14, height: 14, borderRadius: '50%', 
                                            background: '#fff', border: `3px solid ${accent}`,
                                            boxShadow: `0 0 15px ${accent}60`,
                                            zIndex: 10
                                        }}>
                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 30, height: 30, borderRadius: '50%', background: `${accent}20`, zIndex: -1 }}></div>
                                        </div>

                                        <div className="history-card" style={{ 
                                            width: '44%', 
                                            background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)', 
                                            backdropFilter: 'blur(8px)',
                                            padding: '24px 32px', 
                                            borderRadius: 24, 
                                            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                                            boxShadow: isDark ? '0 15px 35px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.06)',
                                            textAlign: isEven ? 'right' : 'left',
                                            position: 'relative'
                                        }}>
                                            <div style={{ 
                                                display: 'inline-block', 
                                                background: isDark ? `${accent}30` : `${accent}15`,
                                                color: accent,
                                                padding: '4px 14px',
                                                borderRadius: 10,
                                                fontSize: 18,
                                                fontWeight: 900,
                                                marginBottom: 12
                                            }}>
                                                {card.year}
                                            </div>
                                            <h3 style={{ fontSize: 22, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 8, lineHeight: 1.2 }}>{card.title}</h3>
                                            <p style={{ fontSize: 15, color: isDark ? '#cbd5e1' : '#475569', lineHeight: 1.6, margin: 0 }}>{card.text}</p>
                                        </div>
                                    </motion.div>
                                )
                            })}
                        </div>

                    ) : (
                        <div className="history-timeline-horizontal" style={{ padding: '40px 0', overflow: 'visible' }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: 40, 
                                overflowX: 'auto', 
                                padding: '20px 0 60px',
                                scrollSnapType: 'x mandatory',
                                scrollBehavior: 'smooth',
                                className: 'no-scrollbar'
                            }}>
                                {cards.map((card: any, i: number) => (
                                    <motion.div 
                                        key={i}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: i * 0.1 }}
                                        style={{ 
                                            flex: '0 0 320px',
                                            scrollSnapAlign: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: -24, 
                                            left: 0, 
                                            right: 0, 
                                            height: 2, 
                                            background: `linear-gradient(to right, ${accent}40, ${accent}40)`,
                                            zIndex: 0
                                        }} />
                                        <div style={{ 
                                            position: 'absolute', 
                                            top: -31, 
                                            left: '20px', 
                                            width: 14, 
                                            height: 14, 
                                            borderRadius: '50%', 
                                            background: '#fff', 
                                            border: `3px solid ${accent}`,
                                            zIndex: 1,
                                            boxShadow: `0 0 10px ${accent}40`
                                        }} />

                                        <div className="history-card" style={{ 
                                            background: isDark ? 'rgba(30, 41, 59, 1)' : 'rgba(255, 255, 255, 1)', 
                                            padding: 28, 
                                            borderRadius: 28, 
                                            border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #f1f5f9',
                                            boxShadow: isDark ? '0 15px 35px rgba(0,0,0,0.2)' : '0 10px 25px rgba(0,0,0,0.03)',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <div style={{ 
                                                display: 'inline-block', 
                                                color: accent,
                                                fontSize: 24,
                                                fontWeight: 900,
                                                marginBottom: 16
                                            }}>
                                                {card.year}
                                            </div>
                                            <h3 style={{ fontSize: 20, fontWeight: 900, color: isDark ? '#fff' : primary, marginBottom: 12 }}>{card.title}</h3>
                                            <p style={{ fontSize: 15, color: isDark ? '#cbd5e1' : '#475569', lineHeight: 1.6, margin: 0 }}>{card.text}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <style>{`
                    .no-scrollbar::-webkit-scrollbar { display: none; }
                    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    
                    @media (max-width: 768px) {
                        .history-timeline-vertical .history-timeline-path {
                            left: 20px !important;
                            transform: none !important;
                        }
                        .history-timeline-vertical .history-item {
                            justify-content: flex-start !important;
                            padding-left: 50px !important;
                        }
                        .history-timeline-vertical .history-item > div:first-child {
                            left: 20px !important;
                        }
                        .history-timeline-vertical .history-card {
                            width: 100% !important;
                            text-align: left !important;
                        }
                    }
                `}</style>
            </section>
        );
    }

    if (block.type === 'text_image_movement') {
        const style = getBgStyle(settings.bg || 'white');
        const imagePos = settings.imagePos || 'left';
        const isVertical = imagePos === 'top' || imagePos === 'bottom';
        const animType = settings.animation || 'slide';

        const getImgAnimation = () => {
            if (animType === 'fade') return { initial: { opacity: 0 }, whileInView: { opacity: 1 }, transition: { duration: 1 } };
            if (animType === 'zoom') return { initial: { opacity: 0, scale: 0.8 }, whileInView: { opacity: 1, scale: 1 }, transition: { duration: 0.8 } };
            const x = imagePos === 'left' ? -100 : imagePos === 'right' ? 100 : 0;
            const y = imagePos === 'top' ? -50 : imagePos === 'bottom' ? 50 : 0;
            return { initial: { opacity: 0, x, y }, whileInView: { opacity: 1, x: 0, y: 0 }, transition: { duration: 0.8, ease: 'easeOut' } };
        };

        return (
            <section style={{ ...style, padding: '100px 20px', overflow: 'hidden' }}>
                <div style={{ 
                    maxWidth: 1200, 
                    margin: '0 auto', 
                    display: 'flex', 
                    flexDirection: isVertical 
                        ? (imagePos === 'bottom' ? 'column' : 'column-reverse') 
                        : (imagePos === 'right' ? 'row-reverse' : 'row'), 
                    alignItems: 'center', 
                    gap: isVertical ? '40px' : 'clamp(20px, 8vw, 80px)', 
                    flexWrap: 'wrap' 
                }}>
                    <motion.div
                        {...getImgAnimation()}
                        viewport={{ once: true }}
                        style={{ flex: isVertical ? 'none' : '1 1 500px', maxWidth: '100%', width: isVertical ? '100%' : 'auto' }}
                    >
                        <div style={{ borderRadius: 30, overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.12)', border: '1px solid rgba(255,255,255,0.2)', maxWidth: isVertical ? 800 : 'none', margin: '0 auto' }}>
                            <img 
                                src={data.img || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200'} 
                                style={{ width: '100%', display: 'block', objectFit: 'cover' }} 
                                alt={settings.imgAlt || data.title || ""} 
                            />
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        style={{ flex: isVertical ? 'none' : '1 1 400px', maxWidth: isVertical ? '800px' : '100%', minWidth: '300px', textAlign: isVertical ? 'center' : 'left' }}
                    >
                        {data.subtitle && <span style={{ color: accent, fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16, display: 'block' }}>{data.subtitle}</span>}
                        <h2 style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', fontWeight: 900, marginBottom: 28, lineHeight: 1.1, color: style.color || primary }}>{data.title || (lang === 'ca' ? 'Nou Bloc' : 'Nuevo Bloque')}</h2>
                        <div className="rich-text-content" style={{ fontSize: 18, lineHeight: 1.8, opacity: 0.85, color: style.color }} dangerouslySetInnerHTML={{ __html: data.text || '' }} />
                    </motion.div>
                </div>
            </section>
        );
    }

    if (block.type === 'countdown') {
        const style = getBgStyle(settings.bg || 'primary');
        return <CountdownComponent block={block} data={data} style={style} primary={primary} />;
    }

    if (block.type === 'progress_bar' || block.type === 'progress_bars') {
        const style = getBgStyle(settings.bg || 'white');
        const isDark = style.color === '#ffffff';
        return (
            <div style={{ ...style, padding: '100px 20px' }}>
                <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 50 }}>
                        <h2 style={{ fontSize: 'clamp(2rem, 5vw, 2.5rem)', fontWeight: 900, color: isDark ? '#fff' : primary }}>{data.title || (lang === 'ca' ? 'Nou Bloc' : 'Nuevo Bloque')}</h2>
                        {data.subtitle && <p style={{ fontSize: 18, opacity: 0.8, marginTop: 12, color: isDark ? '#cbd5e1' : '#475569' }}>{data.subtitle}</p>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
                        {(data.cards || []).map((bar: any, i: number) => (
                            <div key={i}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 15, fontWeight: 900, color: isDark ? '#fff' : primary }}>
                                    <span>{bar.title}</span>
                                    <span style={{ color: accent }}>{bar.text}%</span>
                                </div>
                                <div style={{ height: 14, background: isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9', borderRadius: 20, overflow: 'hidden', border: isDark ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                                    <motion.div 
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${bar.text}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1.5, ease: "easeOut" }}
                                        style={{ height: '100%', background: accent, borderRadius: 20 }}
                                    ></motion.div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'map_embed') {
        const style = getBgStyle(settings.bg || 'white');
        const mapUrl = data.url && data.url.includes('google.com/maps') && !data.url.includes('embed') 
            ? data.url.replace('/maps/', '/maps/embed/') 
            : data.url;

        return (
            <div style={{ ...style, padding: '80px 20px' }}>
                <div style={{ maxWidth: 1400, margin: '0 auto', borderRadius: 24, overflow: 'hidden', height: 500, boxShadow: '0 30px 60px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    {mapUrl ? (
                        <iframe
                            src={mapUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen={true}
                            loading="lazy"
                            title="Localització"
                        ></iframe>
                    ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 16 }}>
                            <MapPin size={48} />
                            <span style={{ fontWeight: 800, fontSize: 16 }}>Google Maps No Configurat</span>
                            <span style={{ fontSize: 14 }}>Afegeix una URL al panell d'administració per mostrar el mapa.</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (block.type === 'image_gallery') {
        const style = getBgStyle(settings.bg || 'white');
        const images = (data.banners || []).map((b: any) => b.img).filter(Boolean);
        
        const handleClick = (img: any, i: number) => {
            if (img.linkType === 'property' && img.linkValue) {
                const prop = properties.find((p: any) => p.id === img.linkValue);
                if (prop) {
                    setDetails(prop);
                    setView('detail');
                }
            } else if (img.linkType === 'external' && img.linkValue) {
                window.open(img.linkValue, '_blank');
            } else {
                onLightbox(images, i);
            }
        };

        return (
            <div style={{ ...style, padding: '80px 20px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    {(data.title || data.subtitle) && (
                        <div style={{ textAlign: 'center', marginBottom: 50 }}>
                            {data.title && <h2 style={{ fontSize: 32, fontWeight: 900, color: style.color || primary }}>{data.title}</h2>}
                            {data.subtitle && <p style={{ fontSize: 16, opacity: 0.8, marginTop: 12 }}>{data.subtitle}</p>}
                        </div>
                    )}
                    <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        justifyContent: 'center', 
                        gap: 20 
                    }}>
                        {(data.banners || []).map((img: any, i: number) => (
                            <motion.div 
                                key={i} 
                                whileHover={{ scale: 1.02 }}
                                onClick={() => handleClick(img, i)}
                                style={{ 
                                    borderRadius: 16, 
                                    overflow: 'hidden', 
                                    cursor: 'pointer', 
                                    boxShadow: '0 10px 20px rgba(0,0,0,0.05)', 
                                    position: 'relative', 
                                    aspectRatio: '4/3',
                                    width: '100%',
                                    maxWidth: (data.banners || []).length === 1 ? '800px' : '350px',
                                    flex: (data.banners || []).length === 1 ? '0 1 800px' : '1 1 300px'
                                }}
                            >
                                <img src={img.img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt={img[`title_${lang}`] || ""} />
                                {(img[`title_${lang}`] || img.linkType === 'property' || img.linkType === 'external') && (
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '15px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{img[`title_${lang}`] || ""}</span>
                                        {(img.linkType === 'property' || img.linkType === 'external') && <ExternalLink size={14} />}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (block.type === 'lead_form') {
        const style = getBgStyle(settings.bg || 'offwhite');
        return (
            <div style={{ ...style, padding: '100px 40px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                   <LeadFormBlock 
                        title={data.title} 
                        subtitle={data.subtitle} 
                        primary={primary} 
                        accent={accent} 
                        lang={lang} 
                        agencyId={config.agency_id} 
                    />
                </div>
            </div>
        );
    }

    return null;
}

function PropertiesView({ config, properties, lang, setDetails, setView, onShowMap, STATUS_BADGE, onDossier }: any) {
    const accent = config.accent_color || '#1d4ed8'
    const primary = config.primary_color || '#1e3a8a'
    const [types, setTypes] = useState<any[]>([])
    const [zones, setZones] = useState<any[]>([])
    const [conservations, setConservations] = useState<any[]>([])
    const [operations, setOperations] = useState<string[]>([])

    useEffect(() => {
        supabase.from('property_types').select('*').eq('agency_id', config.agency_id).then(({ data }) => setTypes(data || []))
        supabase.from('zones').select('*').eq('agency_id', config.agency_id).then(({ data }) => setZones(data || []))
        supabase.from('conservation_states').select('*').eq('agency_id', config.agency_id).then(({ data }) => setConservations(data || []))
        supabase.from('properties').select('operation').eq('agency_id', config.agency_id).eq('status', 'active')
            .then(({ data }) => {
                const unique = [...new Set((data || []).map((p: any) => p.operation).filter(Boolean))] as string[]
                setOperations(unique.length > 0 ? unique : ['venda', 'lloguer'])
            })
    }, [config])

    const PRICE_MAX = 1000000
    const [filters, setFilters] = useState({
        op: 'todos', type: 'todos', zone: 'todos', conservation: 'todos',
        rooms: 'todos', baths: 'todos',
        minPrice: 0, maxPrice: PRICE_MAX,
        ascensor: false, garaje: false, parking: false,
        aire: false, piscina: false, calefaccion: false,
        armarios: false, terraza: false
    })

    const filtered = properties.filter((p: any) => {
        if (filters.op !== 'todos' && p.operation !== filters.op) return false;
        if (filters.type !== 'todos' && p.type !== filters.type) return false;
        if (filters.zone !== 'todos' && p.location_zone !== filters.zone) return false;
        if (filters.conservation !== 'todos' && (p.conservation_state || '') !== filters.conservation) return false;
        if (filters.rooms !== 'todos' && (p.rooms || 0) < parseInt(filters.rooms)) return false;
        if (filters.baths !== 'todos' && (p.baths || 0) < parseInt(filters.baths)) return false;
        if ((p.price || 0) < filters.minPrice || (p.price || 0) > filters.maxPrice) return false;
        if (filters.ascensor && !p.has_elevator) return false;
        if (filters.garaje && !p.has_garage) return false;
        if (filters.parking && !p.has_parking) return false;
        const feat = typeof p.features === 'string' ? JSON.parse(p.features || '{}') : (p.features || {})
        if (filters.aire && !feat.has_air_conditioning) return false;
        if (filters.piscina && !feat.has_pool) return false;
        if (filters.calefaccion && !feat.has_heating) return false;
        if (filters.armarios && !feat.has_wardrobes) return false;
        if (filters.terraza && !feat.has_terrace) return false;
        return true;
    })

    function resetFilters() {
        setFilters({
            op: 'todos', type: 'todos', zone: 'todos', conservation: 'todos', rooms: 'todos', baths: 'todos', minPrice: 0, maxPrice: PRICE_MAX,
            ascensor: false, garaje: false, parking: false, aire: false, piscina: false, calefaccion: false, armarios: false, terraza: false
        })
    }
    const pf = (k: string, v: any) => setFilters(f => ({ ...f, [k]: v }))

    return (
        <div style={{ background: '#f4f6f9', minHeight: '100vh', paddingBottom: 80 }}>
            <h1 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}>
                {lang === 'ca' ? 'Catàleg de Propietats Immobiliàries' : 'Catálogo de Propiedades Inmobiliarias'}
            </h1>
            <div style={{ position: 'fixed', top: 90, right: 24, zIndex: 900 }}>
                <button onClick={() => setView('calculator')} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: '#fff', color: primary, border: `1.5px solid ${primary}`,
                    padding: '10px 18px', borderRadius: 10, fontWeight: 800, fontSize: 13,
                    cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}>
                    <Calculator size={16} /> {lang === 'ca' ? 'Calcular Hipoteca' : 'Calcular Hipoteca'}
                </button>
            </div>
            <div className="public-properties-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 32px 0', display: 'flex', gap: 28, alignItems: 'start' }}>
                <div className="public-properties-sidebar" style={{
                    width: 270, background: '#fff', borderRadius: 16,
                    border: '1px solid #e2e8f0', flexShrink: 0,
                    position: 'sticky', top: 96, boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                }}>
                    <div style={{ padding: '20px 20px 0', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 15, color: primary }}>
                        <SlidersHorizontal size={17} color={accent} /> {lang === 'ca' ? 'Filtres' : 'Filtros'}
                    </div>
                    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div className="prv-filt">
                            <label htmlFor="filter-op">{lang === 'ca' ? 'Tipus d\'Operació' : 'Tipo de Operación'}</label>
                            <select id="filter-op" value={filters.op} onChange={e => pf('op', e.target.value)}>
                                <option value="todos">{lang === 'ca' ? 'Tots' : 'Todos'}</option>
                                {operations.map(op => (
                                    <option key={op} value={op}>{lang === 'ca' ? (OP_LABEL_CA[op] || op.toUpperCase()) : (OP_LABEL[op] || op.toUpperCase())}</option>
                                ))}
                            </select>
                        </div>
                        <div className="prv-filt">
                            <label htmlFor="filter-type">{lang === 'ca' ? 'Tipus de Propietat' : 'Tipo de Propiedad'}</label>
                            <select id="filter-type" value={filters.type} onChange={e => pf('type', e.target.value)}>
                                <option value="todos">{lang === 'ca' ? 'Tots' : 'Todos'}</option>
                                {types.map(t => <option key={t.id} value={t.name}>{lang === 'ca' ? (t.name_ca || t.name) : (t.name_es || t.name)}</option>)}
                            </select>
                        </div>
                        <div className="prv-filt">
                            <label htmlFor="filter-zone">{lang === 'ca' ? 'Zona' : 'Zona'}</label>
                            <select id="filter-zone" value={filters.zone} onChange={e => pf('zone', e.target.value)}>
                                <option value="todos">{lang === 'ca' ? 'Qualsevol' : 'Cualquiera'}</option>
                                {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
                            </select>
                        </div>
                        <div className="prv-filt">
                            <label htmlFor="filter-conservation">{lang === 'ca' ? 'Estat de conservació' : 'Estado de conservación'}</label>
                            <select id="filter-conservation" value={filters.conservation} onChange={e => pf('conservation', e.target.value)}>
                                <option value="todos">{lang === 'ca' ? 'Qualsevol' : 'Cualquiera'}</option>
                                {conservations.map(c => <option key={c.id} value={c.name}>{lang === 'ca' ? (c.name_ca || c.name) : (c.name_es || c.name)}</option>)}
                            </select>
                        </div>
                        <div className="prv-filt">
                            <label htmlFor="filter-maxPrice-range" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{lang === 'ca' ? 'Preu' : 'Precio'} (0 € - {PRICE_MAX.toLocaleString()} €)</span>
                            </label>
                            <input id="filter-maxPrice-range" type="range" min={0} max={PRICE_MAX} step={10000}
                                value={filters.maxPrice}
                                onChange={e => pf('maxPrice', Number(e.target.value))}
                                style={{ width: '100%', accentColor: accent, margin: '8px 0 4px' }}
                            />
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input id="filter-minPrice" type="number" aria-label={lang === 'ca' ? 'Preu Mínim' : 'Precio Mínimo'} placeholder={lang === 'ca' ? 'Preu Mínim' : 'Precio Mínimo'} value={filters.minPrice || ''}
                                    onChange={e => pf('minPrice', Number(e.target.value) || 0)}
                                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }} />
                                <input id="filter-maxPrice" type="number" aria-label={lang === 'ca' ? 'Preu Màxim' : 'Precio Máximo'} placeholder={lang === 'ca' ? 'Preu Màxim' : 'Precio Máximo'} value={filters.maxPrice >= PRICE_MAX ? '' : filters.maxPrice}
                                    onChange={e => pf('maxPrice', Number(e.target.value) || PRICE_MAX)}
                                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12 }} />
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div className="prv-filt">
                                <label htmlFor="filter-rooms">{lang === 'ca' ? 'Habitacions' : 'Habitaciones'}</label>
                                <select id="filter-rooms" value={filters.rooms} onChange={e => pf('rooms', e.target.value)}>
                                    <option value="todos">{lang === 'ca' ? 'Totes' : 'Todos'}</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option><option value="4">4+</option>
                                </select>
                            </div>
                            <div className="prv-filt">
                                <label htmlFor="filter-baths">{lang === 'ca' ? 'Banys' : 'Baños'}</label>
                                <select id="filter-baths" value={filters.baths} onChange={e => pf('baths', e.target.value)}>
                                    <option value="todos">{lang === 'ca' ? 'Tots' : 'Todos'}</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option>
                                </select>
                            </div>
                        </div>
                        <div className="prv-filt" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.ascensor} onChange={e => pf('ascensor', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Ascensor' : 'Ascensor'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.aire} onChange={e => pf('aire', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Aire Condicionat' : 'Aire Acondicionado'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.piscina} onChange={e => pf('piscina', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Piscina' : 'Piscina'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.calefaccion} onChange={e => pf('calefaccion', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Calefacció' : 'Calefacción'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.garaje} onChange={e => pf('garaje', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Garatge Inclòs' : 'Garaje Incluido'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.parking} onChange={e => pf('parking', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Pàrquing Inclòs' : 'Parking Incluido'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.armarios} onChange={e => pf('armarios', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Armaris de Paret' : 'Armarios Empotrados'}
                            </label>
                            <label style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                <input type="checkbox" checked={filters.terraza} onChange={e => pf('terraza', e.target.checked)} style={{ accentColor: accent, width: 14, height: 14 }} />
                                {lang === 'ca' ? 'Terrassa i balcó' : 'Terraza y balcón'}
                            </label>
                        </div>
                        <button onClick={resetFilters} style={{
                            width: '100%', padding: '10px 0', background: '#f8fafc', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 700, fontSize: 13,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                        }}>
                            <RotateCcw size={13} /> {lang === 'ca' ? 'Restablir Filtres' : 'Restablecer Filtros'}
                        </button>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 24 }}>
                        <h2 style={{ fontSize: 24, fontWeight: 900, color: primary, marginBottom: 8 }}>
                            {lang === 'ca' ? 'Catàleg de Propietats' : 'Catálogo de Propiedades'}
                        </h2>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>
                            <span style={{ color: primary, fontWeight: 900, fontSize: 18 }}>{filtered.length}</span> {lang === 'ca' ? 'propietats trobades' : 'propiedades encontradas'}
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
                        {filtered.map((p: any) => (
                            <PropertyCard
                                key={p.id}
                                prop={p}
                                onClick={() => setDetails(p)}
                                onShowMap={() => onShowMap(p)}
                                onDossier={onDossier}
                                primary={primary}
                                accent={accent}
                                opColor={OP_COLOR[p.operation] || accent}
                                opLabel={lang === 'ca' ? (OP_LABEL_CA[p.operation] || (p.operation || '').toUpperCase()) : (OP_LABEL[p.operation] || (p.operation || '').toUpperCase())}
                                lang={lang}
                                STATUS_BADGE={STATUS_BADGE}
                                config={config}
                            />
                        ))}
                    </div>
                    {filtered.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#475569' }}>
                            <Search size={40} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                            <p style={{ fontSize: 16, fontWeight: 600 }}>{lang === 'ca' ? 'No hi ha propietats que coincideixin amb els filtres.' : 'No hay propiedades que coincidan con los filtros.'}</p>
                            <button onClick={resetFilters} style={{ marginTop: 16, background: accent, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{lang === 'ca' ? 'Veure totes' : 'Ver todas'}</button>
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                .prv-filt { }
                .prv-filt label { display: block; font-size: 11px; font-weight: 800; color: #475569; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
                .prv-filt select, .prv-filt input[type=text], .prv-filt input[type=number] { width: 100%; padding: 9px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; font-weight: 600; outline: none; background: #fff; color: #1e293b; }
                .prv-filt select:focus { border-color: ${accent}; }
            `}</style>
        </div>
    )
}

function PropertyCard({ prop, onClick, onShowMap, onDossier, primary, accent, opColor, opLabel, lang, STATUS_BADGE, config }: any) {
    const fmtPrice = (p: number) => {
        if (!p) return '—'
        return p.toLocaleString('es-ES') + ' €'
    }
    const isAlquiler = prop.operation === 'lloguer'
    const propTitle = lang === 'ca' ? (prop.title_ca || prop.title_es) : (prop.title_es || prop.title_ca || `${prop.type} en ${prop.location_city}`)
    const priceText = fmtPrice(prop.price) + (isAlquiler ? '/mes' : '')
    const waText = `Hola, m'interessa aquesta propietat: *${propTitle}*\n*Ref:* ${prop.ref_code}\n*Preu:* ${priceText}\n\n*Agència:* ${config?.agency_name || ''}`
    const rawWA = (config?.contact_whatsapp || config?.contact_phone || '').replace(/\D/g, '')
    // Modal de contacto WhatsApp para esta card (pide datos antes de abrir WA).
    const [waModalOpen, setWaModalOpen] = useState(false)
    const waPhone = rawWA.startsWith('34') ? rawWA : `34${rawWA}`
    const waUrl = `https://wa.me/${waPhone}?text=${encodeURIComponent(waText)}`

    const handleWhatsAppLead = async () => {
        try {
            await supabase.from('leads').insert({
                agency_id: config?.agency_id || (prop as any).agency_id,
                property_id: prop.id,
                name: 'Interés WhatsApp',
                source: 'whatsapp',
                status: 'new',
                message: `Interés por WhatsApp en: ${propTitle} (Ref: ${prop.ref_code})`,
                type: 'Información',
                ...(prop.agent_id ? { assigned_to: prop.agent_id } : {})
            })
        } catch (e) { console.error('Error saving WA lead:', e) }
    }

    return (
        <motion.div
            onClick={onClick}
            whileHover={{ y: -4, boxShadow: '0 24px 48px -12px rgba(0,0,0,0.14)' }}
            className="property-card"
            style={{
                background: '#fff', borderRadius: 20, overflow: 'hidden',
                cursor: 'pointer', border: '1px solid #e2e8f0',
                display: 'flex', flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
                height: '100%',
                position: 'relative'
            }}
        >
            <div style={{ position: 'relative', height: 210, overflow: 'hidden' }}>
                <img
                    src={optimizeImg(prop.cover_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=800', 500, 75)}
                    width="350"
                    height="210"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    alt={propTitle}
                    loading="lazy"
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)' }} />
                <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 5, flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{
                        background: opColor, color: '#fff',
                        padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 900,
                        letterSpacing: '0.05em'
                    }}>{opLabel}</span>
                    {prop.type && (
                        <span style={{
                            background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(4px)',
                            color: '#fff', padding: '3px 9px', borderRadius: 6, fontSize: 10, fontWeight: 800
                        }}>{prop.type.toUpperCase()}</span>
                    )}
                </div>
                <button
                    onClick={e => { e.stopPropagation(); onShowMap(); }}
                    aria-label={(lang === 'ca' ? 'Veure al mapa: ' : 'Ver en el mapa: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                    title={lang === 'ca' ? 'Veure al mapa' : 'Ver en el mapa'}
                    style={{
                        position: 'absolute', bottom: 12, right: 12,
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.95)', border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: accent, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 2
                    }}
                >
                    <MapPin size={18} aria-hidden="true" />
                </button>
                <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    {prop.location_zone && (
                        <div style={{
                            background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
                            color: '#0f172a', padding: '4px 12px', borderRadius: 8, fontSize: 10, fontWeight: 800,
                            letterSpacing: '0.04em', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            textTransform: 'uppercase'
                        }}>{prop.location_zone}</div>
                    )}
                    {prop.status && STATUS_BADGE[prop.status] && (
                        <div style={{
                            background: STATUS_BADGE[prop.status].bg,
                            color: STATUS_BADGE[prop.status].color,
                            padding: prop.status === 'sold' ? '6px 14px' : '3px 9px',
                            borderRadius: prop.status === 'sold' ? 8 : 6,
                            fontSize: prop.status === 'sold' ? 13 : 10,
                            fontWeight: 900,
                            border: `1px solid ${STATUS_BADGE[prop.status].color}30`,
                            textTransform: 'uppercase',
                            boxShadow: prop.status === 'sold' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'
                        }}>{lang === 'ca' ? STATUS_BADGE[prop.status].label_ca : STATUS_BADGE[prop.status].label_es}</div>
                    )}
                    {/* Badge "Rebajada" — visible cuando previous_price > price */}
                    {prop.previous_price && Number(prop.previous_price) > Number(prop.price) && prop.status !== 'sold' && (
                        <div style={{
                            background: '#fef3c7',
                            color: '#b45309',
                            padding: '4px 10px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 900,
                            border: '1px solid #fcd34d',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                            boxShadow: '0 2px 6px rgba(180, 83, 9, 0.15)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                        }}>⬇ {lang === 'ca' ? 'Rebaixada' : 'Rebajada'}</div>
                    )}
                </div>
                <div style={{
                    position: 'absolute', bottom: 10, left: 12,
                    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap'
                }}>
                    {/* Precio anterior tachado, si está rebajada */}
                    {prop.previous_price && Number(prop.previous_price) > Number(prop.price) && (
                        <div style={{
                            fontSize: 13, fontWeight: 700,
                            color: 'rgba(255,255,255,0.85)',
                            textDecoration: 'line-through',
                            textShadow: '0 1px 4px rgba(0,0,0,0.4)'
                        }}>
                            {fmtPrice(prop.previous_price)}
                        </div>
                    )}
                    <div style={{ fontSize: 22, fontWeight: 900, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                        {fmtPrice(prop.price)}{isAlquiler ? <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>/mes</span> : ''}
                    </div>
                    {(prop.m2_built || prop.m2_useful) && !isAlquiler && (
                        <div style={{
                            fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.9)',
                            background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
                            padding: '2px 8px', borderRadius: 6, textShadow: 'none'
                        }}>
                            {Math.round(prop.price / (prop.m2_built || prop.m2_useful))} €/m²
                        </div>
                    )}
                </div>
            </div>
            {prop.status === 'sold' && (
                <div style={{
                    position: 'absolute',
                    top: 25,
                    right: -30,
                    background: '#b91c1c', // Darker red for contrast
                    color: '#fff',
                    padding: '8px 40px',
                    fontWeight: 900,
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 12px rgba(185, 28, 28, 0.4)',
                    zIndex: 10
                }}>
                    {lang === 'ca' ? 'VENUT' : 'VENDIDA'}
                </div>
            )}
            <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 0 }}>
                <h3 style={{
                    fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 6,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', lineHeight: 1.4
                }}>
                    {propTitle}
                </h3>
                <div style={{ display: 'flex', gap: 5, color: '#475569', fontSize: 12, fontWeight: 600, alignItems: 'center', marginBottom: 14 }}>
                    <MapPin size={13} color={accent} />
                    {[prop.location_address ? prop.location_address.split(',')[0] : null, prop.location_city].filter(Boolean).join(', ')}
                </div>
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                    paddingTop: 12, borderTop: '1px solid #f1f5f9',
                    marginBottom: 14
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#334155', fontWeight: 800, fontSize: 13 }}>
                            <Bed size={15} color={accent} /> {prop.rooms ?? 0}
                        </div>
                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{lang === 'ca' ? 'Habitacions' : 'Habitaciones'}</div>
                    </div>
                    <div style={{ textAlign: 'center', borderLeft: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#334155', fontWeight: 800, fontSize: 13 }}>
                            <Bath size={15} color={accent} /> {prop.baths ?? 0}
                        </div>
                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{lang === 'ca' ? 'Banys' : 'Baños'}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#334155', fontWeight: 800, fontSize: 13 }}>
                            <Maximize2 size={14} color={accent} /> {prop.m2_built ?? 0}
                        </div>
                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{lang === 'ca' ? 'm² Constr.' : 'm² Const.'}</div>
                    </div>
                </div>
                <div style={{ marginTop: 'auto' }}>
                    <button
                        onClick={onClick}
                        aria-label={(lang === 'ca' ? 'Veure detalls de: ' : 'Ver detalles de: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                        style={{
                            width: '100%', background: '#1e293b', color: '#fff',
                            padding: '10px 0', borderRadius: 9, fontWeight: 800, fontSize: 13,
                            border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            marginBottom: 8, letterSpacing: '0.01em'
                        }}
                    >
                        {lang === 'ca' ? 'Veure Detalls' : 'Ver Detalles'} <ArrowLeft size={13} style={{ transform: 'rotate(180deg)' }} aria-hidden="true" />
                    </button>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
                        <a
                            href={prop.idealista_url || '#'}
                            target="_blank" rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            title={(lang === 'ca' ? 'Veure a Idealista: ' : 'Ver en Idealista: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            aria-label={(lang === 'ca' ? 'Veure a Idealista: ' : 'Ver en Idealista: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            style={{
                                background: '#e1f54a', border: '1px solid #c9d53c',
                                padding: '10px 0', borderRadius: 12, color: '#334155',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                            }}
                        >
                            <ExternalLink size={18} aria-hidden="true" />
                        </a>
                        <button
                            onClick={e => { e.stopPropagation(); onDossier(prop); }}
                            title={lang === 'ca' ? 'Veure Dossier PDF' : 'Ver Dossier PDF'}
                            aria-label={(lang === 'ca' ? 'Veure dossier PDF de: ' : 'Ver dossier PDF de: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            style={{
                                background: '#2563eb', border: 'none',
                                padding: '10px 0', borderRadius: 12, fontSize: 13, fontWeight: 900,
                                color: '#fff', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 6, cursor: 'pointer'
                            }}
                        >
                            <FileText size={16} aria-hidden="true" /> PDF
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); onDossier(prop); setTimeout(() => window.print(), 1200); }}
                            title={lang === 'ca' ? 'Imprimir Dossier' : 'Imprimir Dossier'}
                            aria-label={(lang === 'ca' ? 'Imprimir dossier de: ' : 'Imprimir dossier de: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            style={{
                                background: '#fff', border: `1.5px solid #2563eb`,
                                padding: '10px 0', borderRadius: 12, fontSize: 13, fontWeight: 900,
                                color: '#2563eb', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 6, cursor: 'pointer'
                            }}
                        >
                            <Printer size={16} aria-hidden="true" /> {lang === 'ca' ? 'Impr.' : 'Impr.'}
                        </button>
                        <button
                            type="button"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); setWaModalOpen(true); }}
                            onMouseDown={e => e.stopPropagation()}
                            onTouchStart={e => e.stopPropagation()}
                            title={(lang === 'ca' ? 'Contactar per WhatsApp per: ' : 'Contactar por WhatsApp por: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            aria-label={(lang === 'ca' ? 'Contactar per WhatsApp per: ' : 'Contactar por WhatsApp por: ') + propTitle + ' (Ref: ' + prop.ref_code + ')'}
                            style={{
                                background: '#25D366', // WhatsApp green oficial
                                border: 'none',
                                padding: '10px 0', borderRadius: 12, color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                            }}
                        >
                            <WhatsAppIcon size={20} />
                        </button>
                    </div>
                    {/* Modal renderizado vía Portal a document.body para salir
                        del árbol DOM del card. Así el click DENTRO del modal NUNCA
                        se interpreta como click sobre el card (que abriría también
                        DetailsModal). */}
                    {waModalOpen && createPortal(
                        <ContactPropertyModal prop={prop} config={config} lang={lang} mode="whatsapp" onClose={() => setWaModalOpen(false)} />,
                        document.body
                    )}
                </div>
            </div>
        </motion.div>
    )
}

function ValuationView({ config, lang }: any) {
    const primary = config.primary_color || '#1e3a8a'
    const accent = config.accent_color || '#1d4ed8'
    const [num1] = useState(() => Math.floor(Math.random() * 10) + 1)
    const [num2] = useState(() => Math.floor(Math.random() * 10) + 1)
    const [sumAns, setSumAns] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [dragOver, setDragOver] = useState(false)
    const [uploadedImgs, setUploadedImgs] = useState<{ url: string; name: string }[]>([])
    const [uploading, setUploading] = useState(false)
    const [propTypes, setPropTypes] = useState<any[]>([])
    const [zones, setZones] = useState<any[]>([])
    const [conservations, setConservations] = useState<any[]>([])
    useEffect(() => {
        supabase.from('property_types').select('*').eq('agency_id', config.agency_id).then(({ data }) => setPropTypes(data || []))
        supabase.from('zones').select('*').eq('agency_id', config.agency_id).then(({ data }) => setZones(data || []))
        supabase.from('conservation_states').select('*').eq('agency_id', config.agency_id).then(({ data }) => setConservations(data || []))
    }, [config.agency_id])
    const [form, setForm] = useState({
        address: '', type: '', operation: 'Venta',
        zone: '', condition: '', year_built: '',
        m2_useful: '', m2_built: '', floor: '',
        estimated_price: '', regimen: 'Libre',
        community_fee: '', ibi: '',
        features: { elevator: false, garage: false, parking: false, air_conditioning: false, pool: false, heating: false, wardrobes: false, terrace: false },
        extras: { es: '', ca: '' }, name: '', email: '', phone: ''
    })
    const [featuredImg, setFeaturedImg] = useState('')
    async function handleFiles(files: FileList | null) {
        if (!files || files.length === 0) return
        setUploading(true)
        const results: { url: string; name: string }[] = []
        for (const file of Array.from(files)) {
            if (!file.type.startsWith('image/')) continue
            const ext = file.name.split('.').pop()
            const path = `valuations/${config.agency_id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
            const { error: upErr } = await supabase.storage.from('property_images').upload(path, file, { cacheControl: '3600', upsert: false })
            if (upErr) {
                console.error('Error uploading image:', upErr)
                alert((lang === 'ca' ? 'Error al pujar l\'imatge: ' : 'Error al subir la imagen: ') + upErr.message)
                continue
            }
            const { data: urlData } = supabase.storage.from('property_images').getPublicUrl(path)
            results.push({ url: urlData.publicUrl, name: file.name })
        }
        setUploadedImgs(prev => {
            const up = [...prev, ...results]
            if (!featuredImg && up.length > 0) setFeaturedImg(up[0].url)
            return up
        })
        setUploading(false)
    }
    async function handleSubmit(e: any) {
        e.preventDefault()
        if (parseInt(sumAns) !== num1 + num2) {
            alert(lang === 'ca' ? 'La suma de seguretat és incorrecta.' : 'La suma de seguridad es incorrecta.')
            return
        }
        setSending(true)
        try {
            const { error } = await supabase.from('valuator_requests').insert({
                agency_id: config.agency_id,
                name: form.name,
                email: form.email,
                phone: form.phone,
                location_city: form.address,
                location_zone: form.zone,
                property_type: form.type || propTypes[0]?.name || 'Casa/Chalet',
                condition: form.condition || conservations[0]?.name || 'Buen estado',
                m2: parseInt(form.m2_built) || null,
                estimated_value: parseInt(form.estimated_price) || null,
                status: 'pending',
                lead_status: 'new',
                property_data: {
                    address: form.address,
                    operation: form.operation,
                    year_built: form.year_built,
                    m2_useful: form.m2_useful,
                    floor: form.floor,
                    regimen: form.regimen,
                    community_fee: form.community_fee,
                    ibi: form.ibi,
                    features: form.features,
                    extras: form.extras,
                    image_urls: uploadedImgs.map(i => i.url),
                    featured_image: featuredImg || uploadedImgs[0]?.url || ''
                }
            })
            if (error) throw error

            // Insertamos también en `leads` para que el trigger SQL
            // (notify_new_lead) dispare la notificación por email a
            // contact_email vía Resend. valuator_requests no tiene trigger.
            try {
                const summary = [
                    form.address && `Dirección: ${form.address}`,
                    form.type && `Tipo: ${form.type}`,
                    form.operation && `Operación: ${form.operation}`,
                    form.m2_built && `m² construidos: ${form.m2_built}`,
                    form.estimated_price && `Valor estimado: ${form.estimated_price} €`,
                ].filter(Boolean).join(' · ')
                await supabase.from('leads').insert({
                    agency_id: config.agency_id,
                    name: form.name,
                    email: form.email || null,
                    phone: form.phone || null,
                    message: `Solicitud de valoración — ${summary || 'sin detalles'}`,
                    source: 'valuation',
                    status: 'new'
                })
            } catch (leadErr) {
                console.warn('Valoración guardada pero notificación de lead falló:', leadErr)
            }

            setSent(true)
        } catch (err: any) {
            console.error(err)
            alert((lang === 'ca' ? 'Error en desar: ' : 'Error al guardar: ') + (err.message || (lang === 'ca' ? 'Error desconegut' : 'Error desconocido')))
        } finally {
            setSending(false)
        }
    }
    const inp: any = { width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: 9, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', fontFamily: 'inherit' }
    const lbl: any = { display: 'block', fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }
    return (
        <div style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)', minHeight: '100vh', paddingBottom: 80 }}>
            <div style={{ background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`, color: '#fff', padding: '60px 20px', textAlign: 'center' }}>
                <h1 style={{ fontSize: 36, fontWeight: 900, marginBottom: 10 }}>{lang === 'ca' ? 'Valoració d\'Immobles' : 'Valoración de Inmuebles'}</h1>
                <p style={{ fontSize: 16, opacity: 0.85 }}>{lang === 'ca' ? 'Obté una estimació professional del valor de la teva propietat' : 'Obtén una estimación profesional del valor de tu propiedad'}</p>
            </div>
            <div style={{ maxWidth: 820, margin: '-30px auto 0', background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', padding: '40px', position: 'relative' }}>
                {sent ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <CheckCircle2 size={64} color="#16a34a" style={{ margin: '0 auto 20px' }} />
                        <h2 style={{ fontSize: 26, fontWeight: 900, color: '#16a34a', marginBottom: 10 }}>{lang === 'ca' ? 'Sol·licitud enviada!' : '¡Solicitud enviada!'}</h2>
                        <button onClick={() => { setSent(false); setUploadedImgs([]) }} style={{ marginTop: 24, background: primary, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>{lang === 'ca' ? 'Valorar una altra propietat' : 'Valorar otra propiedad'}</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: primary, fontWeight: 800, fontSize: 13, marginBottom: 20, paddingBottom: 10, borderBottom: `2px solid ${primary}20` }}>
                                <Home size={16} /> {lang === 'ca' ? 'DETALLS DE LA PROPIETAT' : 'DETALLES DE LA PROPIEDAD'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={lbl}>{lang === 'ca' ? 'Adreça de la Propietat *' : 'Dirección de la Propiedad *'}</label>
                                    <input required style={inp} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Tipus de Propietat *' : 'Tipo de Propiedad *'}</label>
                                        <select required style={inp} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                            <option value="">{lang === 'ca' ? 'Selecciona tipus...' : 'Selecciona tipo...'}</option>
                                            {propTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Tipus d\'Interès / Valoració *' : 'Tipo de Interés / Valoración *'}</label>
                                        <select required style={inp} value={form.operation} onChange={e => setForm({ ...form, operation: e.target.value })}>
                                            <option value="Venta">{lang === 'ca' ? 'Venda' : 'Venta'}</option>
                                            <option value="Alquiler">{lang === 'ca' ? 'Lloguer' : 'Alquiler'}</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Zona *' : 'Zona *'}</label>
                                        <select required style={inp} value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })}>
                                            <option value="">{lang === 'ca' ? 'Selecciona zona...' : 'Selecciona zona...'}</option>
                                            {zones.map((z: any) => <option key={z.id} value={z.name}>{z.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Estat de conservació *' : 'Estado de conservación *'}</label>
                                        <select required style={inp} value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}>
                                            <option value="">{lang === 'ca' ? 'Selecciona estat...' : 'Selecciona estado...'}</option>
                                            {conservations.map((c: any) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Any de construcció' : 'Año de construcción'}</label>
                                        <input type="number" style={inp} value={form.year_built} onChange={e => setForm({ ...form, year_built: e.target.value })} />
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Sup. Útil (m²)' : 'Sup. Útil (m²)'}</label>
                                        <input type="number" style={inp} value={form.m2_useful} onChange={e => setForm({ ...form, m2_useful: e.target.value })} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Sup. Construïda (m²)' : 'Sup. Construida (m²)'}</label>
                                        <input type="number" style={inp} value={form.m2_built} onChange={e => setForm({ ...form, m2_built: e.target.value })} />
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Planta' : 'Planta'}</label>
                                        <input style={inp} value={form.floor} onChange={e => setForm({ ...form, floor: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label style={lbl}>{lang === 'ca' ? 'Característiques Addicionals' : 'Características Adicionales'}</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px 20px', padding: '18px', background: '#f8fafc', borderRadius: 12, border: '1.5px solid #e2e8f0' }}>
                                        {[
                                            { key: 'elevator', label: lang === 'ca' ? '🏙️ Ascensor' : '🏙️ Ascensor' },
                                            { key: 'garage', label: lang === 'ca' ? '🚗 Garatge Inclòs' : '🚗 Garaje Incluido' },
                                            { key: 'parking', label: lang === 'ca' ? '🅿️ Pàrquing Inclòs' : '🅿️ Parking Incluido' },
                                            { key: 'air_conditioning', label: lang === 'ca' ? '❄️ Aire Condicionat' : '❄️ Aire Acondicionado' },
                                            { key: 'heating', label: lang === 'ca' ? '🔥 Calefacció' : '🔥 Calefacción' },
                                            { key: 'pool', label: lang === 'ca' ? '🏊 Piscina' : '🏊 Piscina' },
                                            { key: 'wardrobes', label: lang === 'ca' ? '👔 Armaris de paret' : '👔 Armarios Empotrados' },
                                            { key: 'terrace', label: lang === 'ca' ? '🌅 Terrassa i balcó' : '🌅 Terraza y balcón' }
                                        ].map(({ key, label }) => (
                                            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#475569', cursor: 'pointer', fontWeight: 600 }}>
                                                <input type="checkbox" checked={(form.features as any)[key]} onChange={e => setForm({ ...form, features: { ...form.features, [key]: e.target.checked } })} />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Altres Extres - Castellano' : 'Otros Extras - Castellano'}</label>
                                        <textarea style={{ ...inp, height: 80, resize: 'none' }} value={form.extras.es} onChange={e => setForm({ ...form, extras: { ...form.extras, es: e.target.value } })} />
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Altres Extres - Català' : 'Otros Extras - Catalán'}</label>
                                        <textarea style={{ ...inp, height: 80, resize: 'none' }} value={form.extras.ca} onChange={e => setForm({ ...form, extras: { ...form.extras, ca: e.target.value } })} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: primary, fontWeight: 800, fontSize: 13, marginBottom: 16, paddingBottom: 10, borderBottom: `2px solid ${primary}20` }}>
                                <FileText size={16} /> {lang === 'ca' ? 'FOTOS DE LA PROPIETAT (opcional)' : 'FOTOS DE LA PROPIEDAD (opcional)'}
                            </div>
                            <div
                                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files) }}
                                onClick={() => document.getElementById('val-img-input')?.click()}
                                style={{
                                    border: `2px dashed ${dragOver ? accent : '#cbd5e1'}`,
                                    borderRadius: 12, padding: '32px 20px', textAlign: 'center',
                                    cursor: 'pointer', background: dragOver ? `${accent}08` : '#f8fafc',
                                    transition: 'all 0.2s', position: 'relative'
                                }}
                            >
                                <input id="val-img-input" type="file" multiple accept="image/*" style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer', width: '100%', height: '100%' }} onChange={e => handleFiles(e.target.files)} />
                                {uploading ? <div style={{ color: accent, fontWeight: 700 }}><Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />{lang === 'ca' ? 'Pujant imatges...' : 'Subiendo imágenes...'}</div> : (
                                    <>
                                        <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                                        <div style={{ fontWeight: 800, color: primary, marginBottom: 4 }}>{lang === 'ca' ? 'Arrossega imatges aquí' : 'Arrastra imágenes aquí'}</div>
                                        <div style={{ fontSize: 12, color: '#475569' }}>{lang === 'ca' ? 'o fes clic per seleccionar-les' : 'o haz clic para seleccionarlas'}</div>
                                    </>
                                )}
                            </div>
                            {uploadedImgs.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 12, marginTop: 20 }}>
                                    {uploadedImgs.map((img, i) => (
                                        <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1.5px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                            <img src={img.url} alt="upload" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <button 
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setUploadedImgs(prev => prev.filter((_, idx) => idx !== i)) }}
                                                aria-label={lang === 'ca' ? 'Eliminar imatge' : 'Eliminar imagen'}
                                                style={{ position: 'absolute', top: 4, right: 4, background: '#b91c1c', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: primary, fontWeight: 800, fontSize: 13, marginBottom: 20, paddingBottom: 10, borderBottom: `2px solid ${primary}20` }}>
                                <UserPlus size={16} /> {lang === 'ca' ? 'INFORMACIÓ DE CONTACTE' : 'INFORMACIÓN DE CONTACTO'}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={lbl}>{lang === 'ca' ? 'Nom del propietari *' : 'Nombre del propietario *'}</label>
                                    <input required style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={lbl}>Email *</label>
                                        <input required type="email" style={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                    </div>
                                    <div>
                                        <label style={lbl}>{lang === 'ca' ? 'Telèfon (opcional)' : 'Teléfono (opcional)'}</label>
                                        <input style={inp} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: '#f8fafc', borderRadius: 12, border: '1.5px solid #e2e8f0' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: 6 }}>{lang === 'ca' ? 'Verificació de seguretat *' : 'Verificación de seguridad *'}</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: primary }}>{num1} + {num2} = ?</div>
                            </div>
                            <input required type="number" value={sumAns} onChange={e => setSumAns(e.target.value)} style={{ width: 80, padding: '12px 6px', borderRadius: 10, border: '1.5px solid #d1d5db', textAlign: 'center', fontSize: 18, fontWeight: 900, outline: 'none' }} />
                        </div>
                        <button disabled={sending || uploading} style={{
                            width: '100%', background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
                            color: '#fff', border: 'none', padding: '16px 0', borderRadius: 12,
                            fontWeight: 900, fontSize: 16, cursor: 'pointer',
                            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10,
                            boxShadow: `0 8px 24px ${accent}40`
                        }}>
                            {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
                            {sending ? (lang === 'ca' ? 'Enviant...' : 'Enviando...') : (lang === 'ca' ? 'Enviar Sol·licitud de Valoració' : 'Enviar Solicitud de Valoración')}
                        </button>
                    </form>
                )}
            </div>
        </div>
    )
}

function ContactView({ config, lang }: any) {
    const primary = config.primary_color || '#1e3a8a'
    const accent = config.accent_color || '#1d4ed8'
    const [num1] = useState(Math.floor(Math.random() * 10) + 1)
    const [num2] = useState(Math.floor(Math.random() * 10) + 1)
    const [sumAns, setSumAns] = useState('')
    const [form, setForm] = useState({ name: '', email: '', phone: '', msg: '' })
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    async function handleSubmit(e: any) {
        e.preventDefault()
        if (parseInt(sumAns) !== num1 + num2) {
            alert(lang === 'ca' ? 'La suma de seguretat és incorrecta.' : 'La suma de seguridad es incorrecta.')
            return
        }
        setSending(true)
        try {
            const { error } = await supabase.from('leads').insert({
                agency_id: config.agency_id,
                name: form.name,
                email: form.email,
                phone: form.phone,
                message: form.msg,
                source: 'web_contact',
                status: 'new'
            })
            if (error) throw error
            setSent(true)
            setForm({ name: '', email: '', phone: '', msg: '' })
            setSumAns('')
        } catch (err) {
            console.error('Error enviando contacto:', err)
            alert(lang === 'ca' ? 'Error en enviar el missatge.' : 'Error al enviar el mensaje.')
        } finally {
            setSending(false)
        }
    }
    const cleanAddress = (config.contact_address || '').split('-')[0].trim()
    const mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(cleanAddress)}&t=&z=18&ie=UTF8&iwloc=A&output=embed`
    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: 80 }}>
            <div style={{ background: primary, color: '#fff', padding: '80px 40px', textAlign: 'center' }}>
                <h1 style={{ fontSize: 40, fontWeight: 950, marginBottom: 12 }}>{lang === 'ca' ? 'Contacta amb Nosaltres' : 'Contacta con Nosotros'}</h1>
            </div>
            <div style={{ maxWidth: 1200, margin: '-40px auto 0', padding: '0 40px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 40, alignItems: 'start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    <div style={{ background: '#fff', padding: 32, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                        <h2 style={{ fontSize: 20, fontWeight: 900, color: primary, marginBottom: 24 }}>{lang === 'ca' ? 'Informació de Contacte' : 'Información de Contacto'}</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}><div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent }}><MapPin size={20} /></div><div><div style={{ fontSize: 13, fontWeight: 900, color: primary }}>{lang === 'ca' ? 'Adreça' : 'Dirección'}</div><div style={{ fontSize: 13, color: '#475569' }}>{config.contact_address}</div></div></div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}><div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent }}><Phone size={20} /></div><div><div style={{ fontSize: 13, fontWeight: 900, color: primary }}>{lang === 'ca' ? 'Telèfon' : 'Teléfono'}</div><div style={{ fontSize: 13, color: '#475569' }}>{config.contact_phone}</div></div></div>
                        </div>
                    </div>
                    <div style={{ background: '#fff', padding: 32, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                        <h2 style={{ fontSize: 20, fontWeight: 900, color: primary, marginBottom: 24 }}>{lang === 'ca' ? 'Envia\'ns un Missatge' : 'Envíanos un Mensaje'}</h2>
                        {sent ? <div style={{ padding: 30, textAlign: 'center', background: '#f0fdf4', borderRadius: 12 }}>{lang === 'ca' ? 'Missatge enviat!' : '¡Mensaje enviado!'}</div> : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <input required className="c-in" placeholder={lang === 'ca' ? 'Nom' : 'Nombre'} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                <input required type="email" className="c-in" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                <input className="c-in" placeholder={lang === 'ca' ? 'Telèfon (opcional)' : 'Teléfono (opcional)'} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                                <textarea required className="c-in" style={{ height: 100 }} placeholder={lang === 'ca' ? 'Missatge' : 'Mensaje'} value={form.msg} onChange={e => setForm({ ...form, msg: e.target.value })} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ fontSize: 14, fontWeight: 900, color: primary, background: '#f8fafc', padding: '10px 16px', borderRadius: 8 }}>{num1} + {num2} = ?</div>
                                    <input required type="number" value={sumAns} onChange={e => setSumAns(e.target.value)} style={{ width: 80, padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} />
                                </div>
                                <button disabled={sending} style={{ background: primary, color: '#fff', border: 'none', padding: 16, borderRadius: 8, fontWeight: 900, cursor: 'pointer' }}>{lang === 'ca' ? 'Enviar Missatge' : 'Enviar Mensaje'}</button>
                            </form>
                        )}
                    </div>
                </div>
                <div style={{ background: '#fff', padding: 16, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', position: 'sticky', top: 100 }}>
                    <div style={{ borderRadius: 12, overflow: 'hidden', height: 650, background: '#f8fafc' }}>
                        <iframe title="Mapa" width="100%" height="100%" style={{ border: 0 }} src={mapUrl} allowFullScreen></iframe>
                    </div>
                </div>
            </div>
            <style>{`.c-in { width: 100%; padding: 12px 16px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; outline: none; background: #fff; }`}</style>
        </div>
    )
}

// Tarjeta de gastos del alquiler — sustituye la calculadora de hipoteca cuando es lloguer
function RentalCostsCard({ prop, lang, accent, primary }: any) {
    const L = (ca: string, es: string) => lang === 'ca' ? ca : es
    const feats = prop.features || {}
    const communityFee = prop.community_fee || 0          // €/mes
    const ibi = prop.ibi || 0                              // €/año
    const garbageFee = feats.garbage_fee || 0              // €/año
    const otherFees = feats.other_fees || 0                // €/mes

    const items = [
        { label: L('Comunitat', 'Comunidad'), value: communityFee, unit: L('€/mes', '€/mes') },
        { label: L('Brossa', 'Basura'), value: garbageFee, unit: L('€/any', '€/año') },
        { label: 'IBI', value: ibi, unit: L('€/any', '€/año') },
        { label: L('Altres', 'Otros'), value: otherFees, unit: L('€/mes', '€/mes') },
    ].filter(i => i.value > 0)

    if (items.length === 0) return null

    return (
        <div style={{ background: '#fff', borderRadius: 24, padding: 32, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${accent}18`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={22} />
                </div>
                <div>
                    <h3 style={{ fontSize: 20, fontWeight: 900, color: primary, margin: 0 }}>{L('Despeses del Lloguer', 'Gastos del Alquiler')}</h3>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{L('Despeses no incloses en la renda', 'Gastos no incluidos en la renta')}</div>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 24 }}>
                {items.map((item, i) => (
                    <div key={i} style={{ background: '#f8fafc', padding: '14px 16px', borderRadius: 14, border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                            {item.label}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: primary, lineHeight: 1.1 }}>
                            {item.value.toLocaleString('es-ES')} €
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{item.unit}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function MortgageCalculator({ config, lang }: any) {
    const primary = config.primary_color || '#2563eb'
    const accent = config.accent_color || '#1d4ed8'
    const [price, setPrice] = useState<number>(config.mortgage_default_price || 600000)
    const [downPaymentPct, setDownPaymentPct] = useState<number>(20)
    const [interest, setInterest] = useState<number>(config.mortgage_default_interest || 2.5)
    const [years, setYears] = useState<number>(config.mortgage_default_years || 20)

    const downPaymentAmount = (price * downPaymentPct) / 100
    const loanAmount = price - downPaymentAmount
    const monthlyRate = (interest / 100) / 12
    const totalMonths = years * 12

    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1) || 0
    const totalPaid = (monthlyPayment * totalMonths) || 0
    const totalInterest = (totalPaid - loanAmount) || 0

    const fmt = (v: number) => v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

    return (
        <div style={{ background: '#fff', borderRadius: 24, border: '1px solid #e2e8f0', padding: '40px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 32 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Calculator size={22} /></div>
                <div>
                    <h3 style={{ fontSize: 20, fontWeight: 900, color: primary, marginBottom: 2 }}>{lang === 'ca' ? 'Calculadora d\'Hipoteca' : 'Calculadora de Hipoteca'}</h3>
                    <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{lang === 'ca' ? 'Estima la teva quota mensual' : 'Estima tu cuota mensual'}</div>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Row 1: Price */}
                <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>{lang === 'ca' ? 'Preu de la Propietat' : 'Precio de la Propiedad'}</label>
                    <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569', fontWeight: 800 }}>€</div>
                        <input type="number" value={price} onChange={e => setPrice(Number(e.target.value))}
                            style={{ width: '100%', padding: '14px 14px 14px 35px', borderRadius: 12, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                </div>

                {/* Row 2: Downpayment & Loan Amount */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>{lang === 'ca' ? 'Entrada (%)' : 'Entrada (%)'} ({downPaymentPct}%)</label>
                        <input type="range" min="0" max="100" value={downPaymentPct} onChange={e => setDownPaymentPct(Number(e.target.value))}
                            style={{ width: '100%', accentColor: primary, marginBottom: 8 }} />
                        <div style={{ fontSize: 13, color: '#475569', fontWeight: 700 }}>{fmt(downPaymentAmount)}</div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>{lang === 'ca' ? 'Import del Prèstec' : 'Importe del Préstamo'}</label>
                        <div style={{ padding: '14px', borderRadius: 12, border: '1px solid #d1d5db', background: '#f8fafc', fontSize: 16, fontWeight: 900, color: primary }}>
                            {fmt(loanAmount)}
                        </div>
                    </div>
                </div>

                {/* Row 3: Interest & Years */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>{lang === 'ca' ? 'Tipus d\'Interès (%)' : 'Tipo de Interés (%)'}</label>
                        <div style={{ position: 'relative' }}>
                            <input type="number" step="0.1" value={interest} onChange={e => setInterest(Number(e.target.value))}
                                style={{ width: '100%', padding: '14px 35px 14px 14px', borderRadius: 12, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }} />
                            <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569', fontWeight: 800 }}>%</div>
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>{lang === 'ca' ? 'Termini (anys)' : 'Plazo (años)'}</label>
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }}><Calculator size={16} color="#475569" /></div>
                            <input type="number" value={years} onChange={e => setYears(Number(e.target.value))}
                                style={{ width: '100%', padding: '14px 14px 14px 40px', borderRadius: 12, border: '1px solid #d1d5db', fontSize: 15, fontWeight: 700, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                    </div>
                </div>

                {/* Result Breakdown Card */}
                <div style={{ background: '#f8fafc', borderRadius: 20, border: '1px solid #e2e8f0', padding: '32px', marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 30 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><FileText size={16} /></div>
                        <h4 style={{ fontSize: 15, fontWeight: 900, color: '#1e293b' }}>{lang === 'ca' ? 'Desglossament de Pagaments' : 'Desglose de Pagos'}</h4>
                    </div>

                    <div style={{ textAlign: 'center', marginBottom: 40 }}>
                        <div style={{ fontSize: 13, color: '#475569', fontWeight: 700, marginBottom: 8 }}>{lang === 'ca' ? 'Quota Mensual' : 'Cuota Mensual'}</div>
                        <div style={{ fontSize: 52, fontWeight: 950, color: primary }}>{fmt(monthlyPayment)}</div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, borderTop: '1px solid #e2e8f0', paddingTop: 25 }}>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase' }}>{lang === 'ca' ? 'TOTAL INTERESSOS' : 'TOTAL INTERESES'}</div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#1e293b' }}>{fmt(totalInterest)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', marginBottom: 8, textTransform: 'uppercase' }}>{lang === 'ca' ? 'TOTAL A PAGAR' : 'TOTAL A PAGAR'}</div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#1e293b' }}>{fmt(totalPaid)}</div>
                        </div>
                    </div>
                </div>

                <p style={{ fontSize: 11, color: '#475569', fontStyle: 'italic', lineHeight: 1.6 }}>
                    * {lang === 'ca' ? 'Aquesta calculadora proporciona una estimació aproximada. Els tipus d\'interès i condicions finals poden variar segons l\'entitat bancària i el teu perfil financer. Et recomanem consultar amb un assessor professional.' : 'Esta calculadora proporciona una estimación aproximada. Los tipos de interés y condiciones finales pueden variar según la entidad bancaria y tu perfil financiero. Te recomendamos consultar con un asesor profesional.'}
                </p>
            </div>
        </div>
    )
}

function CalculatorView({ config, lang }: any) {
    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '100%', maxWidth: 860 }}><MortgageCalculator config={config} lang={lang} /></div>
        </div>
    )
}

function EnergyCertificateBadge({ consumption, consumptionRating, emissions, emissionsRating, lang }: any) {
    const scale = [
        { letter: 'A', color: '#00A650', width: '25%' },
        { letter: 'B', color: '#50B848', width: '35%' },
        { letter: 'C', color: '#BFD730', width: '45%' },
        { letter: 'D', color: '#FFF200', width: '55%' },
        { letter: 'E', color: '#FDB913', width: '65%' },
        { letter: 'F', color: '#F37021', width: '75%' },
        { letter: 'G', color: '#ED1C24', width: '85%' },
    ];
    const RatingColumn = ({ title, rating }: any) => (
        <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 12 }}>{title}</div>
            {scale.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', height: 26 }}>
                    <div style={{ width: s.width, height: '100%', background: s.color, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 13, fontWeight: 900 }}>{s.letter}</div>
                    {rating === s.letter && <div style={{ marginLeft: 8, background: '#111', color: '#fff', padding: '0 10px', borderRadius: 4, fontSize: 13 }}>{s.letter}</div>}
                </div>
            ))}
        </div>
    );
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
            <RatingColumn title={lang === 'ca' ? 'Consum d\'energia' : 'Consumo de energía'} rating={consumptionRating} />
            <RatingColumn title={lang === 'ca' ? 'Emissions CO₂' : 'Emisiones CO₂'} rating={emissionsRating} />
        </div>
    )
}

function DetailsModal({ prop, config, onClose, onOpenPdf, lang, openPdfOnLoad, onPdfOpened, properties, onSelectProp, navigate }: any) {
    const accent = config.accent_color || '#1d4ed8'
    const primary = config.primary_color || '#1e293b'
    const [imgIdx, setImgIdx] = useState(0)
    // null = cerrado, 'info' = formulario "Solicitar Información",
    // 'whatsapp' = formulario que abre WhatsApp al guardar.
    const [showContact, setShowContact] = useState<null | 'info' | 'whatsapp'>(null)
    const [mapCoords, setMapCoords] = useState<any>(null)
    const allImgs = (prop.images || []).length > 0 ? [...prop.images].sort((a: any, b: any) => (b.is_cover ? 1 : 0) - (a.is_cover ? 1 : 0)) : [{ url: prop.cover_url }]
    const fmtPrice = (p: number) => p ? p.toLocaleString('es-ES') + ' €' : '—'
    const isAlquiler = prop.operation === 'lloguer'
    const propTitle = lang === 'ca' ? (prop.title_ca || prop.title_es) : (prop.title_es || prop.title_ca || `${prop.type} en ${prop.location_city}`)
    // Priorizamos contact_whatsapp ("Teléfono WhatsApp" en config); si la
    // agencia no lo tiene definido, caemos a contact_phone.
    const rawWA = (config.contact_whatsapp || config.contact_phone || '').replace(/\D/g, '')
    const waPhone = rawWA.startsWith('34') || rawWA.length < 10 ? rawWA : `34${rawWA}`

    // Al pulsar "WhatsApp Directe" insertamos también un lead para que el
    // trigger SQL dispare la notificación por email a contact_email vía Resend.
    const handleWhatsAppLead = async () => {
        try {
            await supabase.from('leads').insert({
                agency_id: config?.agency_id || (prop as any).agency_id,
                property_id: prop.id,
                name: 'Interés WhatsApp',
                source: 'whatsapp',
                status: 'new',
                message: `Interés por WhatsApp en: ${propTitle} (Ref: ${prop.ref_code})`,
                ...(prop.agent_id ? { assigned_to: prop.agent_id } : {})
            })
        } catch (e) { console.error('Error saving WA lead:', e) }
    }

    useEffect(() => {
        if (openPdfOnLoad) {
            onOpenPdf(prop)
            onPdfOpened()
        }
    }, [openPdfOnLoad, prop, onOpenPdf, onPdfOpened])

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [onClose])

    useEffect(() => {
        async function fetchMap() {
            setMapCoords(null);
            // 1) Si la propiedad ya tiene lat/lng guardado (geocodificado desde admin), úsalos
            if (prop.latitude != null && prop.longitude != null) {
                setMapCoords({ lat: parseFloat(prop.latitude), lon: parseFloat(prop.longitude) });
                return;
            }
            const city = (prop.location_city || '').trim();
            const zone = (prop.location_zone || '').trim();
            const addr = (prop.location_address || '').replace(/,/g, ' ').trim();
            if (!city && !addr && !zone) return setMapCoords('error');
            // Intenta dirección completa → zona+ciudad → ciudad solo
            const queries = [
                [addr, city, 'España'].filter(Boolean).join(', '),
                [zone, city, 'España'].filter(Boolean).join(', '),
                [city, 'España'].filter(Boolean).join(', '),
            ].filter(q => q && q.length > 5);
            try {
                for (const q of queries) {
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
                    const data = await res.json();
                    if (data && data.length > 0) {
                        setMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
                        return;
                    }
                }
                setMapCoords('error');
            } catch (e) { setMapCoords('error'); }
        }
        fetchMap();
    }, [prop.id, prop.latitude, prop.longitude]);

    const nextImg = () => setImgIdx((i) => (i + 1) % allImgs.length)
    const prevImg = () => setImgIdx((i) => (i - 1 + allImgs.length) % allImgs.length)

    // Calculate related props (by type or same city)
    const related = (properties || []).filter((p: any) => p.id !== prop.id && (p.type === prop.type || p.location_city === prop.location_city)).slice(0, 3)

    // Equipment and Extras mapping
    const features = typeof prop.features === 'string' ? JSON.parse(prop.features || '{}') : (prop.features || {})
    const equipItems = [
        prop.has_elevator && { label: lang === 'ca' ? 'Ascensor' : 'Ascensor', icon: <ArrowUp size={16} /> },
        prop.orientacion && { label: (lang === 'ca' ? 'Orientació: ' : 'Orientación: ') + prop.orientacion, icon: <Compass size={16} /> },
        prop.conservation_state && { label: (lang === 'ca' ? 'Estat: ' : 'Estado: ') + prop.conservation_state, icon: <Clock size={16} /> },
        prop.construction_year && { label: (lang === 'ca' ? 'Any de constr.: ' : 'Año de constr.: ') + prop.construction_year, icon: <CalendarDays size={16} /> },
        (features.has_heating || prop.has_heating) && { label: lang === 'ca' ? 'Calefacció' : 'Calefacción', icon: <Flame size={16} /> },
        (features.has_air_conditioning || prop.has_air_conditioning) && { label: lang === 'ca' ? 'Aire Condicionat' : 'Aire Acondicionado', icon: <Snowflake size={16} /> },
        (features.has_pool || prop.has_pool) && { label: lang === 'ca' ? 'Piscina' : 'Piscina', icon: <Waves size={16} /> },
        (features.has_garden || prop.has_garden) && { label: lang === 'ca' ? 'Jardí' : 'Jardín', icon: <Trees size={16} /> },
        (prop.has_garage || features.has_parking) && { label: lang === 'ca' ? 'Garatge Inclòs' : 'Garaje Incluido', icon: <Car size={16} /> },
        (features.has_terrace || prop.has_terrace) && { label: lang === 'ca' ? 'Terrassa' : 'Terraza', icon: <Layout size={16} /> },
        (features.has_balcony || prop.has_balcony) && { label: lang === 'ca' ? 'Balcó' : 'Balcón', icon: <Maximize2 size={16} /> },
        (features.equipped_kitchen || prop.kitchen_equipped) && { label: lang === 'ca' ? 'Cuina equipada' : 'Cocina equipada', icon: <ChefHat size={16} /> },
        (features.furnished || prop.is_furnished) && { label: lang === 'ca' ? 'Moblat' : 'Amueblado', icon: <Armchair size={16} /> },
        (features.has_storage || prop.has_storage) && { label: lang === 'ca' ? 'Traster' : 'Trastero', icon: <Key size={16} /> },
        (features.has_laundry || prop.has_laundry) && { label: lang === 'ca' ? 'Safareig' : 'Lavadero', icon: <Waves size={16} /> },
        (features.has_fireplace || prop.has_fireplace) && { label: lang === 'ca' ? 'Llar de foc' : 'Chimenea', icon: <Flame size={16} /> },
        (features.has_wardrobes || prop.has_wardrobes) && { label: lang === 'ca' ? 'Armaris de paret' : 'Armarios empotrados', icon: <SlidersHorizontal size={16} /> },
    ].filter(Boolean)

    // Parse Altres Extres from JSON
    const extras = typeof prop.extras === 'string' ? { es: prop.extras, ca: prop.extras } : (prop.extras || {})
    const extraString = lang === 'ca' ? (extras.ca || extras.list || '') : (extras.es || extras.list || '')
    const extraItems = extraString.split(',').map((s: string) => s.trim()).filter(Boolean)


    return (
        <motion.div className="no-print" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', inset: 0, zIndex: 950, background: '#f8fafc', overflowY: 'auto' }}>
            {/* Header / Top Bar */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                <div style={{ maxWidth: 1400, margin: '0 auto', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => { onClose(); navigate(addTenant(`/${lang}`)); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {config?.logo_url ? (
                          <img src={config.logo_url} alt={config?.agency_name} style={{ height: 45, width: 'auto' }} />
                        ) : (
                          <div style={{ fontWeight: 800, fontSize: 24, color: accent }}>{config?.agency_name || 'Inmobiliaria'}</div>
                        )}
                    </button>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#475569' }}>
                        <ArrowLeft size={20} /> {lang === 'ca' ? 'Tornar al llistat' : 'Volver al listado'}
                    </button>
                    <button onClick={() => onOpenPdf(prop)} style={{ background: '#2563eb', color: '#fff', padding: '10px 24px', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}>
                        <FileText size={18} /> {lang === 'ca' ? 'Veure PDF / Imprimir' : 'Ver PDF / Imprimir'}
                    </button>
                </div>
            </div>

            <div className="details-main-layout" style={{ maxWidth: 1400, margin: '40px auto', padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 420px', gap: 30 }}>
                {/* Main Content */}
                <div className="main-content-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
                    {/* Gallery Card */}
                    <div className="details-gallery-card" style={{ background: '#fff', borderRadius: 24, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                        <div className="details-hero-img" style={{ height: 600, position: 'relative', background: '#000' }}>
                            <img src={optimizeImg(allImgs[imgIdx]?.url, 1200, 85)} width="800" height="600" style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Propiedad" fetchPriority="high" />
                            {allImgs.length > 1 && (
                                <>
                                    <button onClick={prevImg} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111' }}><ChevronLeft size={24} /></button>
                                    <button onClick={nextImg} style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.9)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111' }}><ChevronRight size={24} /></button>
                                </>
                            )}
                            <div style={{ position: 'absolute', top: 20, left: 20, display: 'flex', gap: 8 }}>
                                <span style={{ background: accent, color: '#fff', padding: '4px 12px', borderRadius: 6, fontSize: 10, fontWeight: 900 }}>{prop.operation?.toUpperCase()}</span>
                                <span style={{ background: '#fff', color: '#1e293b', padding: '4px 12px', borderRadius: 6, fontSize: 10, fontWeight: 900 }}>{prop.type?.toUpperCase()}</span>
                            </div>
                        </div>
                        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, background: '#fff' }}>
                            {allImgs.map((img: any, i: number) => (
                                <img key={i} src={optimizeImg(img.url, 400, 75)} width="130" height="95" onClick={() => setImgIdx(i)} style={{ width: '100%', height: 95, borderRadius: 12, objectFit: 'contain', background: '#f8fafc', cursor: 'pointer', border: imgIdx === i ? `3px solid ${accent}` : '3px solid transparent', transition: '0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }} loading="lazy" />
                            ))}
                        </div>
                    </div>

                    {/* Info Card */}
                    <div className="details-info-card" style={{ background: '#fff', borderRadius: 24, padding: '40px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                        <h1 className="details-title" style={{ fontSize: 36, fontWeight: 950, color: '#1e293b', marginBottom: 10 }}>{propTitle}</h1>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 16, fontWeight: 600, marginBottom: 40 }}>
                            <MapPin size={18} color={accent} /> {prop.location_address}, {prop.location_city}
                        </div>

                        {/* Property Stats Bar */}
                        <div className="details-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: '#f1f5f9', borderRadius: 16, overflow: 'hidden', border: '1px solid #f1f5f9', marginBottom: 40 }}>
                            {[
                                { label: lang === 'ca' ? 'SUPERFÍCIE' : 'SUPERFICIE', val: `${prop.m2_built || 0} m²` },
                                { label: lang === 'ca' ? 'HAB.' : 'HAB.', val: prop.rooms || 0 },
                                { label: lang === 'ca' ? 'BANYS' : 'BAÑOS', val: prop.baths || 0 },
                                { label: lang === 'ca' ? 'PLANTA' : 'PLANTA', val: prop.floor || '—' }
                            ].map((s, i) => (
                                <div key={i} style={{ background: '#fff', padding: '20px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#475569', marginBottom: 6, letterSpacing: '0.05em' }}>{s.label}</div>
                                    <div style={{ fontSize: 20, fontWeight: 900, color: '#2563eb' }}>{s.val}</div>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginBottom: 40 }}>
                            <h2 style={{ fontSize: 22, fontWeight: 900, color: '#1e293b', marginBottom: 15 }}>{lang === 'ca' ? 'Descripció' : 'Descripción'}</h2>
                            <div style={{ color: '#475569', lineHeight: 2, fontSize: 16, whiteSpace: 'pre-line' }}>{lang === 'ca' ? (prop.description_ca || prop.description_es) : (prop.description_es || prop.description_ca)}</div>
                        </div>

                        {/* Equipment Section */}
                        {(equipItems.length > 0 || extraItems.length > 0) && (
                            <div className="details-equip-card" style={{ marginBottom: 40, padding: 30, background: '#fafafa', borderRadius: 20, border: '1px solid #f1f5f9' }}>
                                <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1e293b', marginBottom: 20 }}>{lang === 'ca' ? 'Equipament i Extras' : 'Equipamiento y Extras'}</h2>
                                <div className="equip-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '30px 20px' }}>
                                    {equipItems.map((item: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#475569', fontSize: 13, background: '#fff', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                            <div style={{ color: accent, display: 'flex' }}>{item.icon}</div>
                                            <span style={{ fontWeight: 600 }}>{item.label}</span>
                                        </div>
                                    ))}
                                    {extraItems.map((item: string, i: number) => (
                                        <div key={'extra-' + i} style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#475569', fontSize: 13, background: '#fff', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                                            <CheckCircle2 size={16} color={accent} />
                                            <span style={{ fontWeight: 600 }}>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Calculadora de Hipoteca (solo para venta) o Gastos (solo para alquiler) */}
                        <div style={{ marginTop: 40, borderTop: '1px solid #f1f5f9', paddingTop: 40 }}>
                            {isAlquiler ? (
                                <RentalCostsCard prop={prop} lang={lang} accent={accent} primary={primary} />
                            ) : (
                                <MortgageCalculator config={{ ...config, mortgage_default_price: prop.price }} lang={lang} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="details-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Price Card */}
                    <div className="details-price-card" style={{ background: '#fff', padding: 32, borderRadius: 24, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang === 'ca' ? 'PREU DE L\'IMMOBLE' : 'PRECIO DEL INMUEBLE'}</div>
                            {prop.previous_price && Number(prop.previous_price) > Number(prop.price) && (
                                <div style={{ background: '#fef3c7', color: '#b45309', padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 900, border: '1px solid #fcd34d', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                    ⬇ {lang === 'ca' ? 'Rebaixada' : 'Rebajada'}
                                </div>
                            )}
                        </div>
                        {prop.previous_price && Number(prop.previous_price) > Number(prop.price) && (
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#94a3b8', textDecoration: 'line-through', marginBottom: 4 }}>
                                {lang === 'ca' ? 'Abans:' : 'Antes:'} {fmtPrice(prop.previous_price)}
                            </div>
                        )}
                        <div style={{ fontSize: 44, fontWeight: 950, color: '#2563eb', marginBottom: 12 }}>{fmtPrice(prop.price)}</div>
                        {prop.previous_price && Number(prop.previous_price) > Number(prop.price) && (() => {
                            const savings = Number(prop.previous_price) - Number(prop.price)
                            const pct = Math.round((savings / Number(prop.previous_price)) * 100)
                            return (
                                <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 800, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    💰 {lang === 'ca' ? 'Estalvies' : 'Ahorras'} {fmtPrice(savings)} ({pct}%)
                                </div>
                            )
                        })()}
                        {(prop.m2_built || prop.m2_useful) && !isAlquiler && prop.price > 0 && (
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {lang === 'ca' ? 'Preu / m²' : 'Precio / m²'}
                                </span>
                                <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginLeft: 'auto' }}>
                                    {Math.round(prop.price / (prop.m2_built || prop.m2_useful)).toLocaleString('es-ES')} €/m²
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Actions Card */}
                    <div style={{ background: '#fff', padding: 32, borderRadius: 24, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 15 }}>
                        <button onClick={() => setShowContact('info')} style={{ width: '100%', background: '#2563eb', color: '#fff', padding: '18px 0', borderRadius: 14, border: 'none', fontWeight: 900, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 8px 24px rgba(37, 99, 235, 0.25)' }}>
                            <Send size={18} /> {lang === 'ca' ? 'Sol·licitar Informació' : 'Solicitar Información'}
                        </button>
                        <button onClick={() => setShowContact('whatsapp')} style={{ width: '100%', background: '#25D366', color: '#fff', padding: '18px 0', borderRadius: 14, border: 'none', fontWeight: 900, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: '0 8px 24px rgba(37, 211, 102, 0.3)' }}>
                            <WhatsAppIcon size={22} /> {lang === 'ca' ? 'WhatsApp Directe' : 'WhatsApp Directo'}
                        </button>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <button onClick={() => onOpenPdf(prop)} style={{ background: '#2563eb', color: '#fff', padding: '15px 0', borderRadius: 14, border: 'none', fontWeight: 900, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)' }}>
                                <FileText size={18} /> {lang === 'ca' ? 'Dossier PDF' : 'Dossier PDF'}
                            </button>
                            <button onClick={() => { onOpenPdf(prop); setTimeout(() => window.print(), 2000); }} style={{ background: '#fff', color: '#1e293b', padding: '15px 0', borderRadius: 14, border: '1.5px solid #e2e8f0', fontWeight: 900, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                <Printer size={18} /> {lang === 'ca' ? 'Imprimir' : 'Imprimir'}
                            </button>
                        </div>
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 6 }}>O TRUCA'NS AL</div>
                            <div style={{ fontSize: 22, fontWeight: 950, color: '#2563eb' }}>{config.contact_phone}</div>
                        </div>
                    </div>

                    {/* Map Card */}
                    <div style={{ background: '#fff', borderRadius: 24, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <div style={{ padding: '15px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Ubicació</span>
                            <a href={`https://maps.google.com/?q=${encodeURIComponent(prop.location_address + ', ' + prop.location_city)}`} target="_blank" style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}>Obre a Maps ↗</a>
                        </div>
                        <div style={{ height: 300, background: '#f8fafc' }}>
                            <iframe
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                style={{ border: 0 }}
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(prop.location_address + ', ' + prop.location_city + ', España')}&t=&z=17&ie=UTF8&iwloc=A&output=embed`}
                                allowFullScreen
                            />
                        </div>
                    </div>

                    {/* Agent Card */}
                    <div style={{ background: '#fff', padding: 25, borderRadius: 24, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 15 }}>
                        <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9' }}>
                            <img src={prop.agent?.photo_url || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Agent" />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>{lang === 'ca' ? 'AGENT RESPONSABLE' : 'AGENTE RESPONSABLE'}</div>
                            <div style={{ fontSize: 18, fontWeight: 900, color: '#1e293b' }}>{prop.agent?.full_name || 'Carles Granell'}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#2563eb', marginTop: 2 }}>{prop.agent?.phone || config.contact_phone}</div>
                        </div>
                    </div>

                    {/* Related Properties */}
                    {related.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 900, color: '#1e293b', marginBottom: 5 }}>{lang === 'ca' ? 'També et pot interessar' : 'También te puede interesar'}</h3>
                            {related.map((p: any) => (
                                <div key={p.id} onClick={() => onSelectProp(p)} style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', display: 'flex', cursor: 'pointer', border: '1px solid #e2e8f0', height: 90, transition: '0.2s' }}>
                                    <img src={p.cover_url} style={{ width: 100, height: '100%', objectFit: 'cover' }} alt="Related" />
                                    <div style={{ padding: '12px 15px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ fontSize: 16, fontWeight: 900, color: '#2563eb' }}>{fmtPrice(p.price)}</div>
                                        <div style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>{p.type} en {p.location_city}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Energy Card */}
                    <div style={{ background: '#fff', padding: 25, borderRadius: 24, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 800, color: '#1e293b', marginBottom: 20 }}>
                            <ShieldCheck size={18} color="#2563eb" /> Certificació Energètica
                        </div>
                        <EnergyCertificateBadge consumption={prop.energy_consumption} consumptionRating={prop.energy_consumption_rating} emissions={prop.energy_emissions} emissionsRating={prop.energy_emissions_rating} lang={lang} />
                    </div>
                </div>
            </div>

            {showContact && <ContactPropertyModal prop={prop} config={config} lang={lang} mode={showContact} onClose={() => setShowContact(null)} />}
            
            <style>{`
                @media (max-width: 1024px) {
                    .details-main-layout {
                        display: flex !important;
                        flex-direction: column !important;
                        gap: 24px !important;
                        margin-top: 20px !important;
                        padding: 0 16px !important;
                    }
                    .main-content-wrapper, .details-sidebar {
                        display: contents !important;
                    }
                    .details-gallery-card { order: 1 !important; }
                    .details-price-card { order: 2 !important; }
                    .details-info-card { order: 3 !important; }
                    .details-sidebar > div { order: 4 !important; }
                    
                    /* Specific overrides for mobile */
                    .details-hero-img { height: 320px !important; }
                    .details-title { font-size: 26px !important; }
                    .details-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
                    .equip-grid { grid-template-columns: 1fr !important; }
                }
            `}</style>
        </motion.div>
    )
}

function MapTile({ coords, addr }: any) {
    if (coords === 'error' || !coords) {
        return (
            <div style={{ width: '100%', height: '100%', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: 10 }}>
                <MapPin size={30} />
                <div style={{ fontSize: 11, fontWeight: 700, textAlign: 'center', padding: '0 20px' }}>{addr}</div>
            </div>
        );
    }
    const zoom = 17;
    // Switching to a more standard OSM-based static map if possible, 
    // but Yandex is usually ok. Let's try combining it with a better PT marker.
    const staticMapUrl = `https://static-maps.yandex.ru/1.x/?lang=es_ES&ll=${coords.lon},${coords.lat}&z=${zoom}&l=map&size=650,450&pt=${coords.lon},${coords.lat},pm2rdm`;
    return <img src={staticMapUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Mapa de ubicación" />;
}

function MapModal({ prop, config, lang, onClose, onSelectProp }: any) {
    const primary = config?.primary_color || '#1e3a8a';
    const accent = config?.accent_color || '#1d4ed8';
    
    const propTitle = lang === 'ca' 
        ? (prop.title_ca || prop.title_es) 
        : (prop.title_es || prop.title_ca || `${prop.type} en ${prop.location_city}`);
        
    const propAddr = [prop.location_address, prop.location_zone, prop.location_city]
        .filter(Boolean)
        .join(', ');

    const mapQuery = prop.latitude && prop.longitude 
        ? `${prop.latitude},${prop.longitude}`
        : `${prop.location_address || ''} ${prop.location_city || ''} España`;

    const googleMapsEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=16&ie=UTF8&iwloc=A&output=embed`;
    const googleMapsExternalUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [onClose])

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                onClick={onClose} 
                style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)' }} 
            />
            <motion.div 
                initial={{ y: 50, opacity: 0, scale: 0.95 }} 
                animate={{ y: 0, opacity: 1, scale: 1 }} 
                exit={{ y: 50, opacity: 0, scale: 0.95 }}
                style={{ 
                    position: 'relative', 
                    background: '#fff', 
                    borderRadius: 24, 
                    width: '100%', 
                    maxWidth: 800, 
                    height: '80vh', 
                    maxHeight: 700,
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
            >
                {/* Header */}
                <div style={{ padding: '20px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, overflow: 'hidden' }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${accent}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0 }}>
                            <MapPin size={22} />
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                            <h3 style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', margin: '0 0 4px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {propTitle}
                            </h3>
                            <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600, margin: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {propAddr}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        style={{ 
                            background: '#f1f5f9', 
                            border: 'none', 
                            borderRadius: '50%', 
                            width: 36, 
                            height: 36, 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#475569',
                            flexShrink: 0,
                            transition: 'background 0.2s'
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Map Body */}
                <div style={{ flex: 1, position: 'relative', background: '#f8fafc' }}>
                    <iframe
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        style={{ border: 0, display: 'block' }}
                        src={googleMapsEmbedUrl}
                        allowFullScreen
                        title="Mapa de la propiedad"
                    />
                </div>

                {/* Footer with actions */}
                <div style={{ padding: '20px 28px', borderTop: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ fontSize: 20, fontWeight: 950, color: primary }}>
                            {prop.price ? prop.price.toLocaleString('es-ES') + ' €' + (prop.operation === 'lloguer' ? '/mes' : '') : '—'}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#64748b', background: '#e2e8f0', padding: '4px 10px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {prop.ref_code}
                        </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 12 }}>
                        <a 
                            href={googleMapsExternalUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                background: '#fff', 
                                color: '#1e293b', 
                                border: '1.5px solid #e2e8f0', 
                                padding: '10px 20px', 
                                borderRadius: 12, 
                                fontWeight: 800, 
                                fontSize: 14, 
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {lang === 'ca' ? 'Obrir a Google Maps ↗' : (lang === 'en' ? 'Open in Google Maps ↗' : (lang === 'fr' ? 'Ouvrir dans Google Maps ↗' : (lang === 'de' ? 'In Google Maps öffnen ↗' : 'Abrir en Google Maps ↗')))}
                        </a>
                        <button 
                            onClick={() => {
                                onSelectProp(prop);
                                onClose();
                            }}
                            style={{ 
                                background: accent, 
                                color: '#fff', 
                                border: 'none', 
                                padding: '10px 24px', 
                                borderRadius: 12, 
                                fontWeight: 800, 
                                fontSize: 14, 
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: `0 4px 12px ${accent}25`
                            }}
                        >
                            {lang === 'ca' ? 'Veure Detalls' : (lang === 'en' ? 'View Details' : (lang === 'fr' ? 'Voir Détails' : (lang === 'de' ? 'Details anzeigen' : 'Ver Detalles')))}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    )
}

function PdfPreview({ prop, lang, config, onClose }: any) {
    const primary = config?.primary_color || '#1e293b'
    const accent = config?.accent_color || '#2563eb'
    const [mapCoords, setMapCoords] = useState<any>(null)
    const propTitle = lang === 'ca' ? (prop.title_ca || prop.title_es) : (prop.title_es || prop.title_ca || `${prop.type} en ${prop.location_city}`)
    const propAddr = [prop.location_address, prop.location_city].filter(Boolean).join(', ')
    const allImgs = (prop.images || []).sort((a: any, b: any) => (b.is_cover ? 1 : 0) - (a.is_cover ? 1 : 0))
    const fmtPrice = (p: number) => p ? p.toLocaleString('es-ES') + ' €' : '—'

    useEffect(() => {
        async function fetchMap() {
            // 1) Si la propiedad tiene coordenadas guardadas, usarlas directamente
            //    (el admin las habrá guardado vía botón "Geocodificar" en la ficha)
            if (prop.latitude != null && prop.longitude != null) {
                setMapCoords({ lat: parseFloat(prop.latitude), lon: parseFloat(prop.longitude) });
                return;
            }
            // 2) Si no hay coords, intentar Nominatim con la dirección
            const city = (prop.location_city || '').trim();
            const zone = (prop.location_zone || '').trim();
            const addr = (prop.location_address || '').replace(/,/g, ' ').trim();
            const queries = [
                [addr, city, 'España'].filter(Boolean).join(', '),
                [zone, city, 'España'].filter(Boolean).join(', '),
                [city, 'España'].filter(Boolean).join(', '),
            ].filter(q => q && q.length > 5);
            try {
                for (const q of queries) {
                    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
                    const data = await res.json();
                    if (data && data.length > 0) {
                        setMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
                        return;
                    }
                }
                setMapCoords('error');
            } catch (e) {
                console.error('Error fetching map coords:', e);
                setMapCoords('error');
            }
        }
        fetchMap();
    }, [prop.latitude, prop.longitude, prop.location_address, prop.location_city, prop.location_zone]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        }
        window.addEventListener('keydown', handleEsc, true) // capture=true handles before parent
        return () => window.removeEventListener('keydown', handleEsc, true)
    }, [onClose]);

    const handleDownloadPdf = () => {
        const element = document.querySelector('.pdf-content-wrapper');
        if (!element) return;
        const opt = {
            margin: 0,
            filename: `dossier-${prop.ref_code || 'inmueble'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { 
                scale: 2, 
                useCORS: true,
                logging: false,
                letterRendering: true
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        // Show loading state if needed? For now just trigger
        html2pdf().from(element).set(opt).save();
    };

    return (
        <div className="pdf-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(15, 23, 42, 0.98)', overflowY: 'auto', backdropFilter: 'blur(10px)' }}>
            <div className="no-print" style={{ position: 'fixed', top: 0, left: 0, right: 0, padding: '15px 40px', background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(15px)', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10001 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <div style={{ background: '#2563eb', color: '#fff', width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={22} /></div>
                    <div>
                        <div style={{ fontWeight: 950, color: '#1e293b', fontSize: 16 }}>{propTitle}</div>
                        <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>{lang === 'ca' ? 'Dossier de presentació' : 'Dossier de presentación'}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <button onClick={() => { window.focus(); setTimeout(() => window.print(), 300); }} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 50, fontWeight: 900, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 15px rgba(59, 130, 246, 0.3)' }}>
                        <Printer size={16} /> {lang === 'ca' ? 'Imprimir' : 'Imprimir'}
                    </button>
                    <button onClick={handleDownloadPdf} style={{ background: '#1e293b', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 50, fontWeight: 900, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 15px rgba(30, 41, 59, 0.3)' }}>
                        <Download size={16} /> {lang === 'ca' ? 'Descarregar PDF' : 'Descargar PDF'}
                    </button>
                    <button onClick={onClose} style={{ background: 'none', color: '#475569', border: 'none', padding: '10px 15px', borderRadius: 12, fontWeight: 900, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <X size={20} /> {lang === 'ca' ? 'Tancar' : 'Cerrar'}
                    </button>
                </div>
            </div>

            <div className="pdf-content-wrapper pdf-content" style={{ maxWidth: 900, margin: '120px auto', display: 'flex', flexDirection: 'column', gap: 30, paddingBottom: 100 }}>
                {/* Page 1: Portada */}
                <div className="pdf-page" style={{ background: '#fff', minHeight: 1200, padding: 80, borderRadius: 15, boxShadow: '0 25px 60px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 60 }}>
                        {config?.logo_url && <img src={config.logo_url} style={{ height: 70, objectFit: 'contain' }} alt="Logo" />}
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 24, fontWeight: 950, color: primary, marginBottom: 5 }}>{config?.agency_name}</div>
                            <div style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{config?.contact_address}</div>
                        </div>
                    </div>
                    <div style={{ height: 500, borderRadius: 30, overflow: 'hidden', marginBottom: 50, border: '1px solid #f1f5f9', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                        <img src={allImgs[0]?.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Portada" />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 40 }}>
                        <h1 style={{ fontSize: 48, fontWeight: 950, color: primary, margin: 0, flex: 1 }}>{propTitle}</h1>
                        <div style={{ fontSize: 42, fontWeight: 950, color: accent, marginLeft: 30 }}>{fmtPrice(prop.price)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 60, padding: '40px 0', borderTop: '2px solid #f1f5f9', borderBottom: '2px solid #f1f5f9', marginBottom: 50 }}>
                        <div><div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, letterSpacing: '0.1em' }}>M² TOTALS</div><div style={{ fontSize: 22, fontWeight: 950, color: primary }}>{prop.m2_built || '—'} m²</div></div>
                        <div><div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, letterSpacing: '0.1em' }}>HABITACIONS</div><div style={{ fontSize: 22, fontWeight: 950, color: primary }}>{prop.rooms || '—'}</div></div>
                        <div><div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, letterSpacing: '0.1em' }}>BANYS</div><div style={{ fontSize: 22, fontWeight: 950, color: primary }}>{prop.baths || '—'}</div></div>
                    </div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 20, borderTop: '1px solid #f1f5f9', paddingTop: 40 }}>
                        <div style={{ width: 80, height: 80, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9' }}>
                            <img src={prop.agent?.photo_url || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Agent" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: 2 }}>{lang === 'ca' ? 'AGENT RESPONSABLE' : 'AGENTE RESPONSABLE'}</div>
                            <div style={{ fontSize: 24, fontWeight: 950, color: primary }}>{prop.agent?.full_name || 'Carles Granell'}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>{prop.agent?.phone || config?.contact_phone || '977 34 21 00'}</div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 700, color: '#475569' }}>
                            <div>{config?.contact_email}</div>
                            <div style={{ color: accent }}>{config?.agency_website_url || 'www.tuinmobiliaria.com'}</div>
                        </div>
                    </div>
                </div>

                {/* Page 2: Galería y Descripcion */}
                <div className="pdf-page" style={{ background: '#fff', minHeight: 1200, padding: 80, borderRadius: 15, boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}>
                    <h2 style={{ fontSize: 24, fontWeight: 950, color: primary, marginBottom: 30, display: 'flex', alignItems: 'center', gap: 12 }}>Galería de Imatges</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 50 }}>
                        {allImgs.slice(1).map((img: any, i: number) => (
                            <div key={i} style={{ height: 260, borderRadius: 20, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                                <img src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Galeria" />
                            </div>
                        ))}
                    </div>
                    <h2 style={{ fontSize: 24, fontWeight: 950, color: primary, marginBottom: 20 }}>Descripció</h2>
                    <p style={{ fontSize: 16, color: '#475569', lineHeight: 2, whiteSpace: 'pre-line', marginBottom: 50 }}>{lang === 'ca' ? (prop.description_ca || prop.description_es) : (prop.description_es || prop.description_ca)}</p>

                    {/* Equipment in Dossier */}
                    {(() => {
                        const feats = typeof prop.features === 'string' ? JSON.parse(prop.features || '{}') : (prop.features || {})
                        const items = [
                            prop.has_elevator && { label: lang === 'ca' ? 'Ascensor' : 'Ascensor', icon: <ArrowUp size={16} /> },
                            prop.orientacion && { label: (lang === 'ca' ? 'Orientació: ' : 'Orientación: ') + prop.orientacion, icon: <Compass size={16} /> },
                            prop.conservation_state && { label: (lang === 'ca' ? 'Estat: ' : 'Estado: ') + prop.conservation_state, icon: <Clock size={16} /> },
                            prop.construction_year && { label: (lang === 'ca' ? 'Any de constr.: ' : 'Año de constr.: ') + prop.construction_year, icon: <CalendarDays size={16} /> },
                            (feats.has_heating || prop.has_heating) && { label: lang === 'ca' ? 'Calefacció' : 'Calefacción', icon: <Flame size={16} /> },
                            (feats.has_air_conditioning || prop.has_air_conditioning) && { label: lang === 'ca' ? 'Aire Condicionat' : 'Aire Acondicionado', icon: <Snowflake size={16} /> },
                            (feats.has_pool || prop.has_pool) && { label: lang === 'ca' ? 'Piscina' : 'Piscina', icon: <Waves size={16} /> },
                            (feats.has_garden || prop.has_garden) && { label: lang === 'ca' ? 'Jardí' : 'Jardín', icon: <Trees size={16} /> },
                            (prop.has_garage || feats.has_parking) && { label: lang === 'ca' ? 'Garatge Inclòs' : 'Garaje Incluido', icon: <Car size={16} /> },
                            (feats.has_terrace || prop.has_terrace) && { label: lang === 'ca' ? 'Terrassa' : 'Terraza', icon: <Layout size={16} /> },
                            (feats.equipped_kitchen || prop.kitchen_equipped) && { label: lang === 'ca' ? 'Cuina equipada' : 'Cocina equipada', icon: <ChefHat size={16} /> },
                            (feats.furnished || prop.is_furnished) && { label: lang === 'ca' ? 'Moblat' : 'Amueblado', icon: <Armchair size={16} /> },
                            (feats.has_storage || prop.has_storage) && { label: lang === 'ca' ? 'Traster' : 'Trastero', icon: <Key size={16} /> },
                            (feats.has_laundry || prop.has_laundry) && { label: lang === 'ca' ? 'Safareig' : 'Lavadero', icon: <Waves size={16} /> },
                            (feats.has_fireplace || prop.has_fireplace) && { label: lang === 'ca' ? 'Llar de foc' : 'Chimenea', icon: <Flame size={16} /> },
                            (feats.has_wardrobes || prop.has_wardrobes) && { label: lang === 'ca' ? 'Armaris de paret' : 'Armarios empotrados', icon: <SlidersHorizontal size={16} /> },
                        ].filter(Boolean)

                        const extras = typeof prop.extras === 'string' ? { es: prop.extras, ca: prop.extras } : (prop.extras || {})
                        const extraStr = lang === 'ca' ? (extras.ca || extras.list || '') : (extras.es || extras.list || '')
                        const extraItems = extraStr.split(',').map((s: string) => s.trim()).filter(Boolean)

                        if (items.length === 0) return null;
                        return (
                            <div>
                                <h2 style={{ fontSize: 24, fontWeight: 950, color: primary, marginBottom: 30 }}>Equipament i Extras</h2>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px 40px' }}>
                                    {items.map((it: any, idx: number) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 15, fontSize: 16, fontWeight: 700, color: '#475569' }}>
                                            <div style={{ color: accent }}>{it.icon}</div>
                                            {it.label}
                                        </div>
                                    ))}
                                    {extraItems.map((ex: string, i: number) => (
                                        <div key={'ex-' + i} style={{ display: 'flex', alignItems: 'center', gap: 15, fontSize: 16, fontWeight: 700, color: '#475569' }}>
                                            <div style={{ color: accent }}><CheckCircle2 size={16} /></div>
                                            {ex}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })()}
                </div>

                {/* Page 3: Ubicacion y Certificado */}
                <div className="pdf-page" style={{ background: '#fff', minHeight: 1200, padding: 80, borderRadius: 15, boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}>
                    <div style={{ marginBottom: 60 }}>
                        <h2 style={{ fontSize: 28, fontWeight: 950, color: primary, marginBottom: 30 }}>Ubicació</h2>
                        <div style={{ height: 500, borderRadius: 30, overflow: 'hidden', border: '1px solid #f1f5f9', marginBottom: 20 }}>
                            <MapTile coords={mapCoords} addr={propAddr} />
                        </div>
                        <div style={{ fontSize: 16, color: '#475569', fontWeight: 700, textAlign: 'center' }}>{propAddr}</div>
                    </div>
                    {(prop.energy_consumption != null || prop.energy_emissions != null) && (
                        <div>
                            <h2 style={{ fontSize: 28, fontWeight: 950, color: primary, marginBottom: 40 }}>Certificació Energètica</h2>
                            <div style={{ maxWidth: 600, margin: '0 auto' }}>
                                <EnergyCertificateBadge
                                    consumption={prop.energy_consumption}
                                    consumptionRating={prop.energy_consumption_rating}
                                    emissions={prop.energy_emissions}
                                    emissionsRating={prop.energy_emissions_rating}
                                    lang={lang}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 0;
                    }
                    html, body {
                        overflow: visible !important;
                        height: auto !important;
                        background: #fff !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    /* Truco clásico: ocultamos TODO con visibility (no display:none,
                       porque eso desmonta ancestros y el overlay no podría posicionarse).
                       Luego volvemos a mostrar el subárbol del dossier. */
                    body * {
                        visibility: hidden !important;
                    }
                    .pdf-overlay,
                    .pdf-overlay * {
                        visibility: visible !important;
                    }
                    /* Pero los botones del header del overlay (Imprimir, Descargar, Cerrar)
                       NO se imprimen. */
                    .pdf-overlay .no-print,
                    .pdf-overlay .no-print * {
                        visibility: hidden !important;
                        display: none !important;
                    }
                    /* El overlay pasa de fixed a absolute para que se imprima
                       el contenido completo (varias páginas), no solo lo visible. */
                    .pdf-overlay {
                        position: absolute !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: 100% !important;
                        height: auto !important;
                        overflow: visible !important;
                        background: #fff !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        backdrop-filter: none !important;
                        z-index: auto !important;
                    }
                    .pdf-content,
                    .pdf-content-wrapper {
                        margin: 0 !important;
                        gap: 0 !important;
                        width: 100% !important;
                        max-width: none !important;
                        padding: 0 !important;
                        display: block !important;
                    }
                    .pdf-page {
                        position: relative !important;
                        display: flex !important;
                        flex-direction: column !important;
                        min-height: 297mm !important;
                        height: auto !important;
                        border-radius: 0 !important;
                        box-shadow: none !important;
                        margin: 0 !important;
                        padding: 20mm !important;
                        page-break-after: always !important;
                        break-after: page !important;
                        border: none !important;
                        overflow: visible !important;
                        background: #fff !important;
                    }
                    .pdf-page:last-child {
                        page-break-after: auto !important;
                        break-after: auto !important;
                    }
                }
            `}</style>
        </div>
    )
}

// -------------------------------------------------------------
// MODAL FORMULARIO: CONTACTAR POR PROPIEDAD
// -------------------------------------------------------------
function ContactPropertyModal({ prop, config, lang, onClose, mode = 'info' }: any) {
    const isWA = mode === 'whatsapp'
    const accent = isWA ? '#22c55e' : (config.accent_color || '#1d4ed8')
    const primary = config.primary_color || '#1e3a8a'
    const propTitle = lang === 'ca' ? (prop.title_ca || prop.title_es || `${prop.type} en ${prop.location_city}`) : (prop.title_es || prop.title_ca || `${prop.type} en ${prop.location_city}`)
    const [num1] = useState(() => Math.floor(Math.random() * 9) + 1)
    const [num2] = useState(() => Math.floor(Math.random() * 9) + 1)
    const [sumAns, setSumAns] = useState('')
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [emailSent, setEmailSent] = useState(false)
    // Errores inline (no usar alert: el modal global "Aviso" queda detrás).
    const [formError, setFormError] = useState<string | null>(null)
    const [form, setForm] = useState({ name: '', email: '', phone: '', interest: lang === 'ca' ? 'Informació' : 'Información', message: '' })

    async function handleSubmit(e: any) {
        e.preventDefault()
        setFormError(null)

        if (parseInt(sumAns) !== num1 + num2) {
            setFormError(lang === 'ca' ? 'La verificació de seguretat és incorrecta. Resol la suma.' : 'La verificación de seguridad es incorrecta. Resuelve la suma.')
            return
        }

        // Validación de email (si se ha rellenado)
        const emailTrim = (form.email || '').trim()
        if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailTrim)) {
            setFormError(lang === 'ca' ? 'Introdueix un email vàlid.' : 'Introduce un email válido.')
            return
        }

        // Validación de teléfono: solo dígitos, espacios, +, -, (, ) y mínimo 7 dígitos reales
        const phoneTrim = (form.phone || '').trim()
        if (phoneTrim) {
            if (!/^[+\d\s\-()]+$/.test(phoneTrim)) {
                setFormError(lang === 'ca' ? 'El telèfon només pot contenir números, espais, +, -, ( i ).' : 'El teléfono solo puede contener números, espacios, +, -, ( y ).')
                return
            }
            const digits = phoneTrim.replace(/\D/g, '')
            if (digits.length < 7) {
                setFormError(lang === 'ca' ? 'El telèfon ha de tenir almenys 7 dígits.' : 'El teléfono debe tener al menos 7 dígitos.')
                return
            }
        }

        // ─── IMPORTANTE en modo WhatsApp ───
        // Abrimos `window.open` DENTRO del handler síncrono del clic, ANTES de
        // cualquier `await`. Si lo hacemos después de awaits, los navegadores
        // (Safari/Chrome móvil sobre todo) lo bloquean por no estar dentro del
        // gesto del usuario. Por eso construimos el URL y abrimos WhatsApp
        // inmediatamente; el guardado del lead va en background.
        let waWindow: Window | null = null
        if (isWA) {
            const rawWA = (config.contact_whatsapp || config.contact_phone || '').replace(/\D/g, '')
            const waPhone = rawWA.startsWith('34') || rawWA.length < 10 ? rawWA : `34${rawWA}`
            const priceText = prop.price ? `${Number(prop.price).toLocaleString('es-ES')} €${prop.operation === 'lloguer' ? '/mes' : ''}` : ''
            const intro = lang === 'ca' ? 'Nou contacte des de la web' : 'Nuevo contacto desde la web'
            const waMsg =
                `*${intro}*\n\n` +
                `*${lang === 'ca' ? 'Propietat' : 'Propiedad'}:* ${propTitle}\n` +
                `*${lang === 'ca' ? 'Referència' : 'Referencia'}:* ${prop.ref_code}\n` +
                (priceText ? `*${lang === 'ca' ? 'Preu' : 'Precio'}:* ${priceText}\n` : '') +
                `\n*${lang === 'ca' ? 'Nom' : 'Nombre'}:* ${form.name}\n` +
                (phoneTrim ? `*${lang === 'ca' ? 'Telèfon' : 'Teléfono'}:* ${phoneTrim}\n` : '') +
                (emailTrim ? `*Email:* ${emailTrim}\n` : '') +
                `*${lang === 'ca' ? 'Tipus interès' : 'Tipo interés'}:* ${form.interest}\n` +
                (form.message ? `\n*${lang === 'ca' ? 'Missatge' : 'Mensaje'}:*\n${form.message}\n` : '')
            waWindow = window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(waMsg)}`, '_blank', 'noopener,noreferrer')
        }

        setSending(true)
        try {
            // Generamos el UUID en cliente: el SELECT post-INSERT lo bloquea la RLS
            // ("leads: select own agency" sólo permite leer a usuarios de la agencia),
            // así que no podríamos recuperar el id devuelto al usar .insert().select().
            const newLeadId = crypto.randomUUID()

            const { error } = await supabase.from('leads').insert({
                id: newLeadId,
                agency_id: config.agency_id,
                property_id: prop.id,
                name: form.name,
                email: emailTrim || null,
                phone: phoneTrim || null,
                message: form.message,
                type: form.interest,
                source: isWA ? 'whatsapp' : 'web',
                status: 'new',
                ...(prop.agent_id ? { assigned_to: prop.agent_id } : {})
            })
            if (error) throw error

            // Notificación por email (idempotente con el trigger SQL)
            try {
                const { data: notifyData, error: notifyErr } = await supabase.functions.invoke('send-lead-notification', {
                    body: { lead_id: newLeadId }
                })
                console.log('[ContactPropertyModal] send-lead-notification respuesta:', { data: notifyData, error: notifyErr })
                const nd = notifyData as any
                if (!notifyErr && nd?.ok && (nd.message_id || nd.sent_email_id)) {
                    setEmailSent(true)
                }
            } catch (notifyErr) {
                console.warn('No se pudo notificar al email de la agencia:', notifyErr)
            }

            // Fallback: si window.open fue bloqueado (waWindow es null), redirigir
            // la página actual a WhatsApp para que el usuario igualmente llegue.
            if (isWA && !waWindow) {
                const rawWA = (config.contact_whatsapp || config.contact_phone || '').replace(/\D/g, '')
                const waPhone = rawWA.startsWith('34') || rawWA.length < 10 ? rawWA : `34${rawWA}`
                // Reconstruimos el mensaje para el fallback
                const priceText = prop.price ? `${Number(prop.price).toLocaleString('es-ES')} €${prop.operation === 'lloguer' ? '/mes' : ''}` : ''
                const intro = lang === 'ca' ? 'Nou contacte des de la web' : 'Nuevo contacto desde la web'
                const waMsg =
                    `*${intro}*\n\n` +
                    `*${lang === 'ca' ? 'Propietat' : 'Propiedad'}:* ${propTitle}\n` +
                    `*${lang === 'ca' ? 'Referència' : 'Referencia'}:* ${prop.ref_code}\n` +
                    (priceText ? `*${lang === 'ca' ? 'Preu' : 'Precio'}:* ${priceText}\n` : '') +
                    `\n*${lang === 'ca' ? 'Nom' : 'Nombre'}:* ${form.name}\n` +
                    (phoneTrim ? `*${lang === 'ca' ? 'Telèfon' : 'Teléfono'}:* ${phoneTrim}\n` : '') +
                    (emailTrim ? `*Email:* ${emailTrim}\n` : '') +
                    `*${lang === 'ca' ? 'Tipus interès' : 'Tipo interés'}:* ${form.interest}\n` +
                    (form.message ? `\n*${lang === 'ca' ? 'Missatge' : 'Mensaje'}:*\n${form.message}\n` : '')
                window.location.href = `https://wa.me/${waPhone}?text=${encodeURIComponent(waMsg)}`
            }

            setSent(true)
        } catch (err: any) {
            console.error(err)
            setFormError((lang === 'ca' ? 'Error en enviar: ' : 'Error al enviar: ') + (err?.message || 'desconocido'))
        } finally {
            setSending(false)
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 12,
            overflowY: 'auto'
        }}
        onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onClose() }}>
            <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                style={{
                    background: '#fff', borderRadius: 20,
                    width: '100%', maxWidth: 480,
                    maxHeight: 'calc(100vh - 24px)',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 32px 64px rgba(0,0,0,0.2)', overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <h2 style={{ fontSize: 17, fontWeight: 900, color: isWA ? '#128c7e' : primary, display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                        {isWA && <WhatsAppIcon size={22} color="#25D366" />}
                        {isWA
                            ? (lang === 'ca' ? 'Contactar per WhatsApp' : 'Contactar por WhatsApp')
                            : (lang === 'ca' ? 'M\'interessa' : 'Me Interesa')}
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 4, display: 'flex', alignItems: 'center' }} aria-label={lang === 'ca' ? 'Tancar' : 'Cerrar'}><X size={22} /></button>
                </div>

                <div style={{ padding: '20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    {sent ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <CheckCircle2 size={56} color="#16a34a" style={{ margin: '0 auto 16px' }} />
                            <h3 style={{ fontSize: 20, fontWeight: 900, color: '#16a34a', marginBottom: 8 }}>{lang === 'ca' ? 'Missatge enviat!' : '¡Mensaje enviado!'}</h3>
                            <p style={{ color: '#475569', fontSize: 14 }}>{lang === 'ca' ? 'Ens posarem en contacte amb tu el més aviat possible.' : 'Nos pondremos en contacto contigo lo antes posible.'}</p>
                            {emailSent && (
                                <div style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8, background: '#dcfce7', color: '#166534', padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
                                    <CheckCircle2 size={16} /> {lang === 'ca' ? 'Email enviat' : 'Email enviado'}
                                </div>
                            )}
                            <div style={{ marginTop: 20 }}>
                                <button onClick={onClose} style={{ background: primary, color: '#fff', border: 'none', padding: '10px 28px', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>{lang === 'ca' ? 'Tancar' : 'Cerrar'}</button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                            {/* Pill de propiedad */}
                            <div style={{ background: '#eef2ff', color: primary, padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700 }}>
                                {lang === 'ca' ? 'Interès en: ' : 'Interés en: '} <strong>{propTitle.slice(0, 60)}{propTitle.length > 60 ? '...' : ''}</strong>
                            </div>

                            {/* Nombre */}
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>{lang === 'ca' ? 'Nom *' : 'Nombre *'}</label>
                                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder={lang === 'ca' ? 'Nom' : 'Nombre'}
                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                            </div>

                            {/* Email + Teléfono */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>Email</label>
                                    <input
                                        type="email"
                                        inputMode="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                        placeholder="email@ejemplo.com"
                                        title={lang === 'ca' ? 'Introdueix una adreça d\'email vàlida' : 'Introduce un email válido'}
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>{lang === 'ca' ? 'Telèfon (opcional)' : 'Teléfono (opcional)'}</label>
                                    <input
                                        type="tel"
                                        inputMode="tel"
                                        autoComplete="tel"
                                        pattern="^[+\d\s\-()]+$"
                                        title={lang === 'ca' ? 'Només dígits, espais, +, -, ( i )' : 'Solo dígitos, espacios, +, -, ( y )'}
                                        value={form.phone}
                                        onChange={e => {
                                            // Filtrar caracteres no válidos en tiempo real
                                            const filtered = e.target.value.replace(/[^+\d\s\-()]/g, '')
                                            setForm({ ...form, phone: filtered })
                                        }}
                                        placeholder="+34 600..."
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                </div>
                            </div>

                            {/* Tipo interés */}
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>{lang === 'ca' ? 'Tipus d\'interès' : 'Tipo de interés'}</label>
                                <select value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })}
                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' }}>
                                    <option value={lang === 'ca' ? 'Informació' : 'Información'}>{lang === 'ca' ? 'Informació' : 'Información'}</option>
                                    <option value={lang === 'ca' ? 'Visita' : 'Visita'}>{lang === 'ca' ? 'Visita' : 'Visita'}</option>
                                    <option value={lang === 'ca' ? 'Oferta' : 'Oferta'}>{lang === 'ca' ? 'Oferta' : 'Oferta'}</option>
                                    <option value={lang === 'ca' ? 'Finançament' : 'Financiación'}>{lang === 'ca' ? 'Finançament' : 'Financiación'}</option>
                                </select>
                            </div>

                            {/* Mensaje */}
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 5 }}>{lang === 'ca' ? 'Missatge' : 'Mensaje'}</label>
                                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                                    placeholder={lang === 'ca' ? 'Missatge' : 'Mensaje'}
                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 9, fontSize: 14, outline: 'none', resize: 'none', height: 90, boxSizing: 'border-box' }} />
                            </div>

                            {/* Anti-spam */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', padding: '12px 16px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', marginBottom: 4 }}>{lang === 'ca' ? 'Verificació de seguretat *' : 'Verificación de seguridad *'}</div>
                                    <div style={{ fontSize: 16, fontWeight: 900, color: primary }}>{num1} + {num2} = ?</div>
                                </div>
                                <input required type="number" value={sumAns} onChange={e => setSumAns(e.target.value)}
                                    style={{ width: 64, padding: '10px 4px', borderRadius: 9, border: '1.5px solid #d1d5db', textAlign: 'center', fontSize: 16, fontWeight: 900, outline: 'none' }} />
                                <span style={{ fontSize: 11, color: '#475569' }}>{lang === 'ca' ? 'Resol la suma' : 'Resuelve la suma'}</span>
                            </div>

                            {/* Banner de error inline (no usar alert: queda detrás del modal) */}
                            {formError && (
                                <div style={{
                                    background: '#fef2f2', border: '1px solid #fecaca',
                                    color: '#b91c1c', padding: '10px 14px', borderRadius: 10,
                                    fontSize: 13, fontWeight: 700,
                                    display: 'flex', alignItems: 'flex-start', gap: 8
                                }}>
                                    <span style={{ flexShrink: 0 }}>⚠️</span>
                                    <span style={{ flex: 1 }}>{formError}</span>
                                </div>
                            )}

                            <button type="submit" disabled={sending}
                                style={{ width: '100%', background: isWA ? '#25D366' : accent, color: '#fff', border: 'none', padding: '13px 0', borderRadius: 10, fontWeight: 900, fontSize: 15, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                                {sending
                                    ? <Loader2 size={18} className="animate-spin" />
                                    : (isWA ? <WhatsAppIcon size={18} /> : <Send size={16} />)}
                                {isWA
                                    ? (lang === 'ca' ? 'Enviar per WhatsApp' : 'Enviar por WhatsApp')
                                    : (lang === 'ca' ? 'Enviar Missatge' : 'Enviar Mensaje')}
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    )
}



/* ═══════════════════════════════════════════════════════════
   BLOG VIEW — Public post list
═══════════════════════════════════════════════════════════ */
function BlogView({ config, lang, accent, primary, posts, selectedPost, setSelectedPost }: any) {
    const [loading, setLoading] = useState(false)
    const [activeTag, setActiveTag] = useState<string | null>(null)
    const navigate = useNavigate()

    useEffect(() => {
        // Solo sincronizamos URL si seguimos dentro del blog.
        const path = window.location.pathname
        const stillOnBlog = path.includes('/blog') || path.includes('/noticies') || path.includes('/news')
        if (!stillOnBlog) return

        if (selectedPost) {
            // URL SEO-friendly: /es/blog/villa-moderna-piscina-salou-ab223c
            const desired = addTenant(blogPostUrl(lang, selectedPost))
            if (window.location.pathname + window.location.search !== desired) {
                navigate(desired, { replace: true })
            }
        } else if (posts.length > 0) {
            const params = new URLSearchParams(window.location.search)
            // Si seguimos en /blog/{slug} pero ya no hay post seleccionado, volver al índice
            const segments = window.location.pathname.split('/').filter(Boolean)
            const onPostUrl = segments.length === 3 // /:lang/blog/:slug
            if (params.has('post') || onPostUrl) {
                const desired = addTenant(lang === 'ca' ? `/${lang}/noticies` : (lang === 'en' ? `/${lang}/news` : `/${lang}/blog`))
                navigate(desired, { replace: true })
            }
        }
    }, [selectedPost])

    // All unique tags
    const allTags = Array.from(new Set(posts.flatMap(p => (p.tags || []))))
    const filtered = activeTag ? posts.filter(p => (p.tags || []).includes(activeTag)) : posts

    if (selectedPost) {
        return <BlogPostView post={selectedPost} lang={lang} accent={accent} primary={primary} onBack={() => setSelectedPost(null)} />
    }

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '56px 24px' }}>
            {/* Blog Header */}
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <div style={{ display: 'inline-block', padding: '6px 18px', background: `${accent}18`, borderRadius: 20, fontSize: 13, fontWeight: 700, color: accent, marginBottom: 12 }}>
                    {lang === 'ca' ? '📝 Notícies' : '📝 Blog'}
                </div>
                <h1 style={{ fontSize: 36, fontWeight: 900, color: primary, margin: '0 0 12px' }}>
                    {lang === 'ca' ? 'Les nostres notícies' : 'Nuestras noticias'}
                </h1>
                <p style={{ color: '#475569', fontSize: 16, margin: 0 }}>
                    {lang === 'ca' ? 'Consells, novetats i tot sobre el mercat immobiliari' : 'Consejos, novedades y todo sobre el mercado inmobiliario'}
                </p>
            </div>

            {/* Tag filter */}
            {allTags.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 36 }}>
                    <button
                        onClick={() => setActiveTag(null)}
                        style={{ padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: !activeTag ? accent : '#f1f5f9', color: !activeTag ? '#fff' : '#475569' }}
                    >
                        {lang === 'ca' ? 'Tots' : 'Todos'}
                    </button>
                    {allTags.map(tag => (
                        <button
                            key={tag}
                            onClick={() => setActiveTag(tag)}
                            style={{ padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: activeTag === tag ? accent : '#f1f5f9', color: activeTag === tag ? '#fff' : '#475569' }}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}

            {/* Posts Grid */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={36} color={accent} style={{ animation: 'spin 1s linear infinite' }} /></div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 80, color: '#475569' }}>
                    <p style={{ fontSize: 18 }}>{lang === 'ca' ? 'Encara no hi ha articles publicats.' : 'Todavía no hay artículos publicados.'}</p>
                </div>
            ) : (
                <div style={
                    config?.blog_layout === 'feed'
                        ? { display: 'flex', flexDirection: 'column', gap: 40, maxWidth: 800, margin: '0 auto' }
                        : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 28 }
                }>
                    {filtered.map((post: any) => {
                        const title = (lang === 'ca' ? post.title_ca : post.title_es) || post.title_es || post.title_ca || ''
                        const excerpt = (lang === 'ca' ? post.excerpt_ca : post.excerpt_es) || ''
                        const date = post.published_at ? new Date(post.published_at).toLocaleDateString(lang === 'ca' ? 'ca-ES' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
                        return (
                            <div
                                key={post.id}
                                onClick={() => setSelectedPost(post)}
                                style={{
                                    background: '#fff',
                                    borderRadius: config?.blog_layout === 'feed' ? 20 : 16,
                                    overflow: 'hidden',
                                    boxShadow: config?.blog_layout === 'feed' ? '0 4px 20px rgba(0,0,0,0.05)' : '0 2px 12px rgba(0,0,0,.07)',
                                    border: config?.blog_layout === 'feed' ? '1px solid #e2e8f0' : 'none',
                                    cursor: 'pointer',
                                    transition: 'transform .2s, box-shadow .2s'
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,.12)' }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = config?.blog_layout === 'feed' ? '0 4px 20px rgba(0,0,0,0.05)' : '0 2px 12px rgba(0,0,0,.07)' }}
                            >
                                {/* Cover */}
                                <div style={{ height: config?.blog_layout === 'feed' ? 300 : 200, overflow: 'hidden', position: 'relative' }}>
                                    {post.cover_url ? (
                                        <img src={post.cover_url} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${primary}22, ${accent}22)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <span style={{ fontSize: 48 }}>📰</span>
                                        </div>
                                    )}
                                    {/* Tags */}
                                    {post.tags?.length > 0 && (
                                        <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 6 }}>
                                            {post.tags.slice(0, 2).map((t: string) => (
                                                <span key={t} style={{ padding: '4px 12px', background: 'rgba(255,255,255,.92)', borderRadius: 20, fontSize: 12, fontWeight: 700, color: accent }}>{t}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Body */}
                                <div style={{ padding: config?.blog_layout === 'feed' ? '32px' : '20px 24px 24px' }}>
                                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{date}</div>
                                    <h2 style={{ fontSize: 18, fontWeight: 700, color: primary, margin: '0 0 10px', lineHeight: 1.4 }}>{title}</h2>
                                    {excerpt && <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 16px' }}>{excerpt}</p>}
                                    <span style={{ fontSize: 13, fontWeight: 700, color: accent }}>
                                        {lang === 'ca' ? 'Llegir article →' : 'Leer artículo →'}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    )
}

/* ═══════════════════════════════════════════════════════════
   BLOG POST VIEW — Full article
═══════════════════════════════════════════════════════════ */
function BlogPostView({ post, lang, accent, primary, onBack }: { post: any; lang: 'es' | 'ca'; accent: string; primary: string; onBack: () => void }) {
    const title = (lang === 'ca' ? post.title_ca : post.title_es) || post.title_es || post.title_ca || ''
    const content = (lang === 'ca' ? post.content_ca : post.content_es) || post.content_es || post.content_ca || ''
    const date = post.published_at ? new Date(post.published_at).toLocaleDateString(lang === 'ca' ? 'ca-ES' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : ''

    useEffect(() => { window.scrollTo(0, 0) }, [])

    return (
        <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px 24px' }}>
            {/* Back */}
            <button
                onClick={onBack}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: accent, fontWeight: 700, fontSize: 14, marginBottom: 32, padding: 0 }}
            >
                <ArrowLeft size={16} /> {lang === 'ca' ? 'Tornar al blog' : 'Volver al blog'}
            </button>

            {/* Tags */}
            {post.tags?.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {post.tags.map((t: string) => (
                        <span key={t} style={{ padding: '4px 12px', background: `${accent}18`, borderRadius: 20, fontSize: 12, fontWeight: 700, color: accent }}>{t}</span>
                    ))}
                </div>
            )}

            {/* Title */}
            <h1 style={{ fontSize: 34, fontWeight: 900, color: primary, margin: '0 0 12px', lineHeight: 1.25 }}>{title}</h1>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 28 }}>{date}</div>

            {/* Cover */}
            {post.cover_url && (
                <img src={post.cover_url} alt={title} style={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 16, marginBottom: 36 }} />
            )}

            {/* Content */}
            <div
                dangerouslySetInnerHTML={{ __html: content }}
                style={{ fontSize: 16, lineHeight: 1.8, color: '#334155' }}
            />
        </div>
    )
}

/* ═══════════════════════════════════════════════════════════
   FOOTER — Site wide footer
═══════════════════════════════════════════════════════════ */
function Footer({ config, lang, view, setView, customPageId, setCustomPageId, navigate }: { config: AgencyConfig; lang: 'es' | 'ca'; view: string; setView: (v: any) => void; customPageId: string | null; setCustomPageId: (id: string | null) => void; navigate: any }) {
    const primary = ensureContrast(config.primary_color || '#1e3a8a')
    const accent = ensureContrast(config.accent_color || '#1d4ed8')
    const curYear = new Date().getFullYear()

    return (
        <footer style={{ background: '#ffffff', color: '#1e293b', padding: '80px 40px 40px', borderTop: '1px solid #f1f5f9' }} className="no-print">
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 60, marginBottom: 80 }}>
                    <div style={{ maxWidth: 350 }}>
                        {config.logo_url ? (
                            <img 
                                src={optimizeImg(config.logo_url, 300, 80)} 
                                alt={config.agency_name} 
                                width="160" 
                                height="45" 
                                style={{ height: 45, width: 'auto', maxWidth: '100%', marginBottom: 25, objectFit: 'contain', cursor: 'pointer' }} 
                                onClick={() => setView('home')}
                            />
                        ) : (
                            <div 
                                style={{ fontSize: 24, fontWeight: 900, marginBottom: 20, color: primary, cursor: 'pointer' }}
                                onClick={() => setView('home')}
                            >
                                {config.agency_name}
                            </div>
                        )}
                        <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.7, marginBottom: 25 }}>
                            {lang === 'ca'
                                ? 'La vostra immobiliària de confiança. T\'ajudem a trobar l\'habitatge dels teus somnis amb el millor assessorament professional.'
                                : 'Tu inmobiliaria de confianza. Te ayudamos a encontrar la vivienda de tus sueños con el mejor asesoramiento profesional.'
                            }
                        </p>
                    </div>

                    <div>
                        <h4 style={{ fontSize: 13, fontWeight: 900, marginBottom: 25, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0f172a' }}>{lang === 'ca' ? 'Horari' : 'Horario'}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15, color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
                            {lang === 'ca' ? (
                                <div style={{ whiteSpace: 'pre-line' }}>{config.opening_hours_ca || 'Dilluns a Divendres :\n9:30 - 14:00, 16:30 - 19:00'}</div>
                            ) : (
                                <div style={{ whiteSpace: 'pre-line' }}>{config.opening_hours_es || 'Lunes a Viernes :\n9:30 - 14:00, 16:30 - 19:00'}</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <h4 style={{ fontSize: 13, fontWeight: 900, marginBottom: 25, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0f172a' }}>{lang === 'ca' ? 'Navegació' : 'Navegación'}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                            <a href={addTenant(`/${lang}`)} onClick={(e) => { e.preventDefault(); setView('home'); setCustomPageId(null); navigate(addTenant(`/${lang}`)); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>{lang === 'ca' ? 'Inici' : 'Inicio'}</a>
                            <a href={addTenant(lang === 'ca' ? `/${lang}/propietats` : `/${lang}/propiedades`)} onClick={(e) => { e.preventDefault(); setView('properties'); setCustomPageId(null); navigate(addTenant(lang === 'ca' ? `/${lang}/propietats` : `/${lang}/propiedades`)); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>{lang === 'ca' ? 'Propietats' : 'Propiedades'}</a>
                            
                            {(config?.custom_pages || []).filter((p: any) => p.is_active !== false && !p.parent_id).map((p: any) => {
                                const children = (config?.custom_pages || []).filter((c: any) => c.parent_id === p.id && c.is_active !== false);
                                return (
                                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <a href={addTenant(`/p/${p.slug}`)} onClick={(e) => { e.preventDefault(); setView('custom'); setCustomPageId(p.id); navigate(addTenant(`/p/${p.slug}`)); window.scrollTo(0, 0); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>
                                            {lang === 'ca' ? p.title_ca : p.title_es}
                                        </a>
                                        {children.map(child => (
                                            <a key={child.id} href={addTenant(`/p/${child.slug}`)} onClick={(e) => { e.preventDefault(); setView('custom'); setCustomPageId(child.id); navigate(addTenant(`/p/${child.slug}`)); window.scrollTo(0, 0); }} style={{ color: '#475569', fontSize: 13, cursor: 'pointer', fontWeight: 500, paddingLeft: 12, textDecoration: 'none' }}>
                                                - {lang === 'ca' ? child.title_ca : child.title_es}
                                            </a>
                                        ))}
                                    </div>
                                );
                            })}

                            <a href={addTenant(lang === 'ca' ? `/${lang}/valoracio` : `/${lang}/valoracion`)} onClick={(e) => { e.preventDefault(); setView('valuation'); setCustomPageId(null); navigate(addTenant(lang === 'ca' ? `/${lang}/valoracio` : `/${lang}/valoracion`)); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>{lang === 'ca' ? 'Valoració' : 'Valoración'}</a>
                            <a href={addTenant(lang === 'ca' ? `/${lang}/contacte` : `/${lang}/contacto`)} onClick={(e) => { e.preventDefault(); setView('contact'); setCustomPageId(null); navigate(addTenant(lang === 'ca' ? `/${lang}/contacte` : `/${lang}/contacto`)); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>{lang === 'ca' ? 'Contacte' : 'Contacto'}</a>
                            <a href={addTenant(`/${lang}/calculadora`)} onClick={(e) => { e.preventDefault(); setView('calculator'); setCustomPageId(null); navigate(addTenant(`/${lang}/calculadora`)); }} style={{ color: '#475569', fontSize: 14, cursor: 'pointer', fontWeight: 600, textDecoration: 'none' }}>{lang === 'ca' ? 'Calculadora' : 'Calculadora'}</a>
                        </div>
                    </div>

                    <div>
                        <h4 style={{ fontSize: 13, fontWeight: 900, marginBottom: 25, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0f172a' }}>{lang === 'ca' ? 'Legal' : 'Legal'}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15, color: '#475569', fontSize: 14 }}>
                            <a href={addTenant(`/${lang}/${lang === 'ca' ? 'avis-legal' : 'aviso-legal'}`)} onClick={(e) => { e.preventDefault(); setView(lang === 'ca' ? 'avis-legal' : 'aviso-legal'); }} style={{ cursor: 'pointer', transition: 'color .2s', color: '#475569', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.color = accent} onMouseLeave={e => e.currentTarget.style.color = '#475569'}>{lang === 'ca' ? 'Avís Legal' : 'Aviso Legal'}</a>
                            <a href={addTenant(`/${lang}/${lang === 'ca' ? 'privadesa' : 'privacidad'}`)} onClick={(e) => { e.preventDefault(); setView(lang === 'ca' ? 'privadesa' : 'privacidad'); }} style={{ cursor: 'pointer', transition: 'color .2s', color: '#475569', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.color = accent} onMouseLeave={e => e.currentTarget.style.color = '#475569'}>{lang === 'ca' ? 'Política de Privadesa' : 'Política de Privacidad'}</a>
                            <a href={addTenant(`/${lang}/cookies`)} onClick={(e) => { e.preventDefault(); setView('cookies'); }} style={{ cursor: 'pointer', transition: 'color .2s', color: '#475569', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.color = accent} onMouseLeave={e => e.currentTarget.style.color = '#475569'}>{lang === 'ca' ? 'Política de Cookies' : 'Política de Cookies'}</a>
                            <a href={addTenant(`/${lang}/${lang === 'ca' ? 'termes' : 'terminos'}`)} onClick={(e) => { e.preventDefault(); setView(lang === 'ca' ? 'termes' : 'terminos'); }} style={{ cursor: 'pointer', transition: 'color .2s', color: '#475569', textDecoration: 'none' }} onMouseEnter={e => e.currentTarget.style.color = accent} onMouseLeave={e => e.currentTarget.style.color = '#475569'}>{lang === 'ca' ? 'Termes de Servei' : 'Términos de Servicio'}</a>
                            {config.show_kit_digital && (
                                <div style={{ marginTop: 25 }}>
                                    <img
                                        src={optimizeImg(config.kit_digital_logo_url || "https://upload.wikimedia.org/wikipedia/commons/e/e0/Logo_Kit_Digital_Red.es.png", 400, 90)}
                                        alt="Kit Digital"
                                        style={{ height: 'auto', maxHeight: 80, width: 'auto', maxWidth: '100%', objectFit: 'contain', filter: 'grayscale(0)', opacity: 1, display: 'block' }}
                                        onError={(e: any) => { e.target.src = "https://red.es/sites/default/files/logo-kit-digital.png" }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 40, justifyContent: 'center', marginBottom: 60, flexWrap: 'wrap', opacity: 0.8, alignItems: 'center' }}>
                    {(config?.portals_list || []).filter((p: any) => p.active).map((p: any) => {
                        const lowerName = p.name?.toLowerCase() || '';
                        let portalLogo = '';
                        
                        // 1. Prioritize user-uploaded logo in portal config
                        if (p.logo) {
                            if (p.logo.startsWith('http') || p.logo.startsWith('data:')) {
                                portalLogo = p.logo;
                            } else {
                                // Assume it's a Supabase path if not a full URL
                                const bucket = 'agency-assets';
                                portalLogo = `https://${import.meta.env.VITE_SUPABASE_URL?.split('//')[1]}/storage/v1/object/public/${bucket}/${p.logo}`;
                            }
                        } 
                        
                        // 2. Fallback to high-quality stable SVGs if no user logo
                        if (!portalLogo) {
                            if (lowerName.includes('idealista')) portalLogo = 'https://upload.wikimedia.org/wikipedia/commons/3/33/Idealista_logo.svg';
                            else if (lowerName.includes('pisos.com')) portalLogo = 'https://upload.wikimedia.org/wikipedia/commons/a/a2/Logo_Pisos.com.svg'; 
                            else if (lowerName.includes('fotocasa')) portalLogo = 'https://upload.wikimedia.org/wikipedia/commons/3/3a/Logo_fotocasa.svg'; // Updated URL
                            else if (lowerName.includes('habitaclia')) portalLogo = 'https://upload.wikimedia.org/wikipedia/commons/8/86/Logo_Habitaclia.svg'; 
                            else if (lowerName.includes('yaencontre')) portalLogo = 'https://upload.wikimedia.org/wikipedia/commons/e/ea/Logo_yaencontre.svg';
                        }

                        if (!portalLogo) return null;

                        return (
                            <a key={p.id} href={p.url || '#'} target="_blank" rel="noopener noreferrer" style={{ transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}>
                                <img
                                    src={portalLogo.includes('supabase.co') ? optimizeImg(portalLogo, 400, 90) : portalLogo}
                                    alt={p.name}
                                    style={{ height: 'auto', maxHeight: 40, width: 'auto', maxWidth: 150, objectFit: 'contain', filter: 'grayscale(0.2)', opacity: 0.9, display: 'block' }}
                                    onError={(e: any) => {
                                        // Final fallback
                                        if (lowerName.includes('idealista')) e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/3/33/Idealista_logo.svg';
                                        else if (lowerName.includes('pisos')) e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/a/a2/Logo_Pisos.com.svg';
                                    }}
                                />
                            </a>
                        );
                    })}
                    {(!config?.portals_list || config.portals_list.length === 0) && (
                        <>
                            <img src="https://upload.wikimedia.org/wikipedia/commons/3/33/Idealista_logo.svg" alt="Idealista" width="100" height="32" style={{ height: 32, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                            <img src="https://www.pisos.com/comun/img/pisos-logo.svg" alt="Pisos.com" width="100" height="32" style={{ height: 32, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                            <img src="https://upload.wikimedia.org/wikipedia/commons/2/23/Logo_Fotocasa.svg" alt="Fotocasa" width="100" height="32" style={{ height: 32, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                            <img src="https://www.habitaclia.com/img/habitaclia-logo.svg" alt="Habitaclia" width="100" height="32" style={{ height: 32, width: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
                        </>
                    )}
                </div>

                <div style={{ paddingTop: 40, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
                    <div style={{ color: '#475569', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 15 }}>
                        <span>© {curYear} {config.agency_name}. {lang === 'ca' ? 'Tots els drets reservats.' : 'Todos los derechos reservados.'}</span>
                        <span style={{ opacity: 0.5 }}>|</span>
                        <a href="https://www.pontsoft.cat/" target="_blank" rel="noopener noreferrer" style={{ color: '#475569', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            InmoCRM by <strong style={{ color: '#475569' }}>Pontsoft</strong> <ExternalLink size={12} />
                        </a>
                    </div>
                    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                        <span onClick={() => navigate(addTenant('/admin'))} style={{ color: '#475569', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Lock size={14} /> Admin
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
